import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import DocumentsFiscaux from './DocumentsFiscaux';
import Optimisation from './Optimisation';
import BankStatements from './BankStatements';
import DashboardConfigurator from './DashboardConfigurator';
import AdminRAG from './AdminRAG';
import { ToastProvider, useToast } from './Toast';
import CopiloteDeclaration from './CopiloteDeclaration';
import SectionAbonnement from './SectionAbonnement';
import ModulePaie from './ModulePaie';
import URSSAFDSN from './URSSAFSection';
import CadrageTiers from './CadrageTiers';
import CashFlow from './CashFlow';
import Juridique from './Juridique';
import ComptabilitePremium from './ComptabilitePremium';
import FiscalPremium from './FiscalPremium';
import SocialPremium from './SocialPremium';
import EcheancesCalendar from './EcheancesCalendar';
import FacturationEinvoicing from './FacturationEinvoicing';
import CopiloteCreationEntreprise from './CopiloteCreationEntreprise';
import { trackFeatureUse, trackPageView } from '../services/analytics';

// Hook personnalisé pour la détection mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

// Fonction pour générer le badge de conformité
const getConformiteBadge = (conformite) => {
  if (!conformite) {
    return {
      label: '— Non vérifié',
      background: '#F3F4F6',
      color: '#9CA3AF',
      icon: '—'
    };
  }

  // Vérification PRIORITAIRE : identité client
  if (conformite.verification_client && !conformite.verification_client.client_trouve) {
    return {
      label: '🚨 Mauvais destinataire',
      background: '#FEE2E2',
      color: '#A32D2D',
      icon: '🚨',
      details: {
        type: conformite.type_label,
        score: conformite.score,
        champsManquants: conformite.champs_manquants || [],
        alerte: conformite.verification_client.alerte
      }
    };
  }

  // Classification par conformité (pas par score)
  const estConforme = conformite.conforme === true;
  const auNomClient = conformite.au_nom_client === true;

  // Priorité absolue : document pas au nom du client
  if (!auNomClient) {
    return {
      label: '🚨 Non conforme',
      background: '#FEE2E2',
      color: '#A32D2D',
      icon: '🚨',
      details: {
        type: conformite.type_label,
        score: conformite.score || 0,
        champsManquants: conformite.champs_manquants || [],
        champsPresents: conformite.champs_presents,
        totalChamps: conformite.total_champs,
        messageAlerte: conformite.message_alerte
      }
    };
  }

  // Ensuite, vérifier la conformité globale
  if (estConforme) {
    return {
      label: '✅ Conforme',
      background: '#E1F5EE',
      color: '#0F6E56',
      icon: '✅',
      details: {
        type: conformite.type_label,
        score: conformite.score || 0,
        champsManquants: conformite.champs_manquants || [],
        champsPresents: conformite.champs_presents,
        totalChamps: conformite.total_champs
      }
    };
  } else {
    return {
      label: '🚨 Non conforme',
      background: '#FEE2E2',
      color: '#A32D2D',
      icon: '🚨',
      details: {
        type: conformite.type_label,
        score: conformite.score || 0,
        champsManquants: conformite.champs_manquants || [],
        champsPresents: conformite.champs_presents,
        totalChamps: conformite.total_champs
      }
    };
  }
};

const ComptableDashboardInner = ({ clientId, clientNom, onLogout }) => {
  const { addToast } = useToast();
  const isMobile = useIsMobile();

  // Fonction simple pour formatter le markdown sans librairie externe
  const formatMessage = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br/>');
  };

  // Fonction utilitaire pour télécharger un export
  const downloadExport = async (endpoint, filename) => {
    try {
      const response = await fetch(`${endpoint}?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      } else {
        const errorText = await response.text();
        console.error('Erreur export:', response.status, errorText);
        alert('Erreur lors du téléchargement');
      }
    } catch (error) {
      console.error('Erreur export:', error);
      alert('Erreur lors du téléchargement');
    }
  };

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [docMonth, setDocMonth] = useState(null);
  const [docStatus, setDocStatus] = useState('tous');
  const [docSearch, setDocSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [activeSection, setActiveSectionState] = useState('dashboard');
  // Wrapper : track l'usage des features (anonyme, fire-and-forget)
  const setActiveSection = (section) => {
    trackFeatureUse(section);
    setActiveSectionState(section);
  };
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Configuration du tableau de bord (widgets actifs/inactifs, tailles)
  const [dashboardConfig, setDashboardConfig] = useState({});

  // Helper : renvoie true si un widget doit être affiché (avec fallback sur les défauts)
  const WIDGET_DEFAULTS = {
    metrics: true, tva: true, docs: true, chat: true,
    charges: true, echeances: false, tresorerie: false, fournisseurs: false, audit: false
  };
  const wgtActive = (id) => {
    if (dashboardConfig[id] !== undefined) return dashboardConfig[id].active;
    return WIDGET_DEFAULTS[id] !== undefined ? WIDGET_DEFAULTS[id] : true;
  };
  const wgtSize = (id) => {
    if (dashboardConfig[id]?.size) return dashboardConfig[id].size;
    return 'half';
  };

  const handleMobileNavClick = (sectionId) => {
    setIsMobileMenuOpen(false);
    if (checkModuleAccess(sectionId)) {
      setActiveSection(sectionId);
    }
  };
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: 'ai', text: `Bonjour ${clientNom || 'Cher Client'} ! 👋 Je suis **ComptaAI**, votre partenaire de gestion. 

Je suis là pour vous aider à piloter votre entreprise. Je peux par exemple :
- **Analyser** votre dernier bilan 📊
- **Calculer** votre TVA en temps réel 💸
- **Rechercher** une facture spécifique 🔍
- Vous donner des **conseils d'optimisation** 🚀

Comment puis-je vous accompagner aujourd'hui ?`, time: new Date().getHours() + ':' + String(new Date().getMinutes()).padStart(2,'0') }
  ]);
  const [ecrituresData, setEcrituresData] = useState([]);
  const [ecrituresLoading, setEcrituresLoading] = useState(false);
  const [tvaData, setTvaData] = useState(null);
  const [tvaLoading, setTvaLoading] = useState(false);
  const [documentsData, setDocumentsData] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [chargesData, setChargesData] = useState(null);
  const [chargesLoading, setChargesLoading] = useState(false);
  const [tvaDetailData, setTvaDetailData] = useState(null);
  const [tvaDetailLoading, setTvaDetailLoading] = useState(false);
  const [bilanData, setBilanData] = useState(null);
  const [bilanLoading, setBilanLoading] = useState(false);

  const [clotureData, setClotureData] = useState(null);
  const [clotureLoading, setClotureLoading] = useState(false);

  const [syncConfig, setSyncConfig] = useState({
    enabled: false,
    cloudUrl: '',
    apiKey: '',
    lastSyncAt: null
  });
  const [syncSaving, setSyncSaving] = useState(false);
  const [syncRunning, setSyncRunning] = useState(false);

  const [updateInfo, setUpdateInfo] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateApplying, setUpdateApplying] = useState(false);

  // States pour modification du mot de passe (bcrypt côté API)
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);

  // States pour les quotas d'abonnements et popups de surclassement
  const [subData, setSubData] = useState(null);
  const [upgradePopup, setUpgradePopup] = useState({ show: false, title: '', message: '' });
  const [showMobileSyncModal, setShowMobileSyncModal] = useState(false);

  const checkUpdates = async () => {
    try {
      const res = await api.get('/updates/check');
      if (res && res.success) {
        setUpdateInfo(res);
      }
    } catch (e) {
      console.error('Erreur check updates:', e);
    }
  };

  const handleApplyUpdate = async () => {
    if (!updateInfo) return;
    setUpdateApplying(true);
    try {
      const res = await api.post(`/updates/apply?download_url=${encodeURIComponent(updateInfo.download_url)}`);
      if (res && res.success) {
        addToast(res.message, 'success');
        setShowUpdateModal(false);
      } else {
        addToast('❌ Échec du téléchargement de la mise à jour', 'error');
      }
    } catch (error) {
      console.error('Erreur update:', error);
      addToast('❌ Erreur réseau lors de la mise à jour', 'error');
    } finally {
      setUpdateApplying(false);
    }
  };

  const loadAbonnement = async () => {
    try {
      const resp = await api.get('/abonnements/me');
      if (resp && resp.plan) {
        setSubData(resp);
      }
    } catch (err) {
      console.error('Erreur chargement abonnement:', err);
    }
  };

  const checkModuleAccess = (sectionId) => {
    if (sectionId === 'facturation') return true;
    if (!subData || !subData.features) return true; // Si pas encore chargé, laisser passer
    const f = subData.features;
    const planId = subData?.plan?.id;

    if (sectionId === 'juridique' && planId !== 'pro' && planId !== 'expert') {
      setUpgradePopup({
        show: true,
        title: '⚖ Outil Juridique Premium',
        message: 'L\'analyse automatique de documents juridiques (statuts, PV d\'AG) et l\'extraction IA de clauses contractuelles sont réservées aux abonnements Pro et supérieurs.'
      });
      return false;
    }

    if (sectionId === 'comptabilite_premium' && planId !== 'pro' && planId !== 'expert') {
      setUpgradePopup({
        show: true,
        title: '🧮 Comptabilité Premium',
        message: 'Le diagnostic de conformité FEC, le contrôle qualité des écritures et le module complet de relance des factures échues sont réservés aux abonnements Pro et supérieurs.'
      });
      return false;
    }

    if (sectionId === 'fiscal_premium' && planId !== 'pro' && planId !== 'expert') {
      setUpgradePopup({
        show: true,
        title: '📝 Doctrine Fiscale (BOFiP) par IA',
        message: 'L\'interrogation en langage naturel de la doctrine officielle des impôts (BOFiP) avec recherche en temps réel est disponible à partir du forfait Pro.'
      });
      return false;
    }

    if (sectionId === 'social_premium' && planId !== 'pro' && planId !== 'expert') {
      setUpgradePopup({
        show: true,
        title: '👥 Doctrine Sociale (BOSS) par IA',
        message: 'L\'interrogation de la doctrine officielle de la sécurité sociale (BOSS) pour vos questions de cotisations et paie nécessite le forfait Pro.'
      });
      return false;
    }
    
    if (sectionId === 'optimisation' && !f.optimisation) {
      setUpgradePopup({
        show: true,
        title: '📊 Optimisation Fiscale',
        message: 'L\'optimisation fiscale assistée par IA est réservée aux abonnements payants. Passez au plan Pro pour débloquer les rapports de déduction de charges et conseils fiscaux !'
      });
      return false;
    }
    if (sectionId === 'inventaire' && !f.inventaire) {
      setUpgradePopup({
        show: true,
        title: '📦 Immobilisations & Amortissements',
        message: 'Le module d\'inventaire et d\'amortissement des immobilisations n\'est pas disponible dans le plan Gratuit. Passez à la formule supérieure pour gérer vos actifs.'
      });
      return false;
    }
    if (sectionId === 'social' && !f.social) {
      setUpgradePopup({
        show: true,
        title: '👥 Salariés & Paie',
        message: 'La gestion des fiches de paie et des déclarations DSN nécessite le plan Essentiel ou supérieur. Améliorez votre formule pour ajouter des salariés.'
      });
      return false;
    }
    if (sectionId === 'urssaf' && !f.social && !f.copilote_urssaf) {
      setUpgradePopup({
        show: true,
        title: '🏛️ URSSAF / DSN',
        message: 'La gestion des déclarations URSSAF et DSN nécessite le plan Pro ou supérieur. Améliorez votre formule pour débloquer le module URSSAF / DSN.'
      });
      return false;
    }
    if (sectionId === 'tva' && !f.copilote_tva) {
      setUpgradePopup({
        show: true,
        title: '% Déclaration de TVA',
        message: 'L\'assistant automatique de déclaration de TVA pré-remplie nécessite un plan payant. Améliorez votre abonnement pour automatiser vos déclarations fiscales.'
      });
      return false;
    }
    if (sectionId === 'tresorerie') {
      const planId = subData?.plan?.id;
      if (planId !== 'pro' && planId !== 'expert') {
        setUpgradePopup({
          show: true,
          title: '↕ Trésorerie & Prévisions (Cash Flow)',
          message: 'Le module complet de suivi de trésorerie avec prévisions automatiques et alertes de solde critique est réservé aux abonnements Pro et supérieurs (à partir de 49€). Passez à la formule Pro pour piloter vos liquidités en toute sérénité !'
        });
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    checkUpdates();
    loadAbonnement();
    trackPageView('dashboard');
  }, []);

  const loadSyncConfig = async () => {
    try {
      const response = await api.get('/sync/config');
      if (response && response.success) {
        setSyncConfig(response.config);
      }
    } catch (error) {
      console.error('Erreur chargement config sync:', error);
    }
  };

  const handleSaveSyncConfig = async (e) => {
    e.preventDefault();
    setSyncSaving(true);
    try {
      const response = await api.post('/sync/config', syncConfig);
      if (response && response.success) {
        addToast('✅ Configuration de synchronisation mise à jour !', 'success');
      } else {
        addToast('❌ Erreur lors de la sauvegarde de la config', 'error');
      }
    } catch (error) {
      console.error('Erreur sauvegarde config sync:', error);
      addToast('❌ Erreur de connexion', 'error');
    } finally {
      setSyncSaving(false);
    }
  };

  const handleTriggerSync = async () => {
    setSyncRunning(true);
    try {
      const response = await api.post('/sync/trigger');
      if (response && response.success) {
        addToast(response.message, 'success');
        if (response.nb_sync > 0) {
          loadDocuments();
        }
        loadSyncConfig();
      } else {
        addToast('❌ Erreur de synchronisation', 'error');
      }
    } catch (error) {
      console.error('Erreur déclenchement sync:', error);
      addToast('❌ Impossible de contacter le serveur local', 'error');
    } finally {
      setSyncRunning(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      addToast('❌ Saisissez votre mot de passe actuel.', 'error');
      return;
    }
    if (newPassword.length < 8) {
      addToast('❌ Le mot de passe doit contenir au moins 8 caractères.', 'error');
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      addToast('❌ Le mot de passe doit inclure une majuscule, une minuscule et un chiffre.', 'error');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      addToast('❌ Les mots de passe ne correspondent pas.', 'error');
      return;
    }

    setPwdLoading(true);
    try {
      const res = await api.post('/auth/change-password', {
        old_password: currentPassword,
        new_password: newPassword
      });
      if (!res || !res.success) {
        throw new Error(res?.error || 'Impossible de modifier le mot de passe');
      }
      addToast('✅ Votre mot de passe a été mis à jour avec succès !', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      console.error('Erreur changement mot de passe:', error);
      addToast(`❌ Erreur : ${error.message || 'Impossible de modifier le mot de passe'}`, 'error');
    } finally {
      setPwdLoading(false);
    }
  };

  const isExerciseClosed = clotureData && clotureData.length === 12 && clotureData.every(m => m.cloture && m.cloture.statut === 'cloture');

  const loadClotureData = async () => {
    setClotureLoading(true);
    try {
      const res = await api.get(`/dashboard/${clientId}/suivi-mensuel?year=${selectedYear}`);
      if (res.success && res.suivi) {
        setClotureData(res.suivi);
      } else {
        addToast("Erreur lors du chargement du suivi mensuel", "error");
      }
    } catch (err) {
      console.error(err);
      addToast("Erreur de connexion avec le serveur", "error");
    } finally {
      setClotureLoading(false);
    }
  };

  const handleCloturerMois = async (periode, moisNom) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir clôturer le mois de ${moisNom} ${selectedYear} ? Cette action gèlera tous les justificatifs et transactions de ce mois.`)) {
      return;
    }
    try {
      const res = await api.post(`/dashboard/${clientId}/cloturer-mois`, { periode });
      if (res.success) {
        addToast(`Mois de ${moisNom} clôturé avec succès ! 🔒`, "success");
        loadClotureData();
      } else {
        addToast(res.detail || "Erreur lors de la clôture", "error");
      }
    } catch (err) {
      console.error(err);
      addToast("Erreur de connexion", "error");
    }
  };

  const handleCloturerExercice = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir clôturer définitivement l'exercice ${selectedYear} ? Le grand livre sera verrouillé et archivé.`)) {
      return;
    }
    try {
      const res = await api.post(`/dashboard/${clientId}/cloturer-exercice`, { annee: selectedYear });
      if (res.success) {
        addToast(`Exercice ${selectedYear} clôturé et archivé définitivement ! 🏆🔒`, "success");
        loadClotureData();
      } else {
        addToast(res.detail || "Erreur lors de la clôture", "error");
      }
    } catch (err) {
      console.error(err);
      addToast("Erreur de connexion", "error");
    }
  };
  const [bankData, setBankData] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);

  // États pour le suivi d'upload
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');

  // États pour le Pull-to-Refresh
  const [pullDistance, setPullDistance] = useState(0);
  const [pulling, setPulling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [startY, setStartY] = useState(0);
  const touchAreaRef = useRef(null);

  // États pour la modale de validation
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showTvaCopilote, setShowTvaCopilote] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocumentConformite, setSelectedDocumentConformite] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [loadingDocumentDetails, setLoadingDocumentDetails] = useState(false);
  const [conformiteTooltip, setConformiteTooltip] = useState(null); // { docId, x, y, conformite }

  // État local pour les modifications du formulaire
  const [validationForm, setValidationForm] = useState({
    fournisseur: '',
    montant_ht: 0,
    tva: 0,
    taux_tva: 20,
    compte_pcg: '607000',  // Par défaut : achats de marchandises
    lignes_tva: []  // Ventilation multi-taux
  });

  // Support & Tickets states
  const [supportTickets, setSupportTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReplyText, setTicketReplyText] = useState('');
  const [newTicketForm, setNewTicketForm] = useState({ sujet: '', description: '', categorie: 'technique', priorite: 'moyenne' });
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [ticketActionLoading, setTicketActionLoading] = useState(false);
  const [ticketSuccessMsg, setTicketSuccessMsg] = useState(null);

  // Handler pour mettre à jour un champ du formulaire et propager aux autres champs
  const handleFormFieldChange = (field, value) => {
    // Mettre à jour le formulaire local
    setValidationForm(prev => ({
      ...prev,
      [field]: value
    }));

    // Mettre à jour selectedDocument pour propager aux autres champs
    setSelectedDocument(prev => {
      if (!prev) return prev;

      const updated = { ...prev };

      // Si le fournisseur change, mettre à jour tous les champs fournisseur
      if (field === 'fournisseur') {
        updated.fournisseur = value;
        if (updated.fournisseur_complet) {
          updated.fournisseur_complet.nom = value;
        }
      }

      // Si montant_ht change, recalculer TTC
      if (field === 'montant_ht') {
        updated.montant_ht = parseFloat(value);
        updated.montant_ttc = updated.montant_ht + (updated.tva || 0);
      }

      // Si tva change, recalculer TTC
      if (field === 'tva') {
        updated.tva = parseFloat(value);
        updated.montant_ttc = (updated.montant_ht || 0) + updated.tva;
      }

      // Si taux_tva change
      if (field === 'taux_tva') {
        updated.taux_tva = parseFloat(value);
      }

      return updated;
    });
  };

  // Fonction pour valider un document en attente
  const validerDocument = async (documentId) => {
    if (isExerciseClosed) {
      alert("❌ Impossible de valider un document sur un exercice clôturé.");
      return;
    }

    if (!validationForm.fournisseur || validationForm.fournisseur.trim() === '') {
      alert('❌ Veuillez saisir le nom du fournisseur');
      return;
    }

    try {
      const montant_ht = parseFloat(validationForm.montant_ht);
      const tva = parseFloat(validationForm.tva);
      const montant_ttc = montant_ht + tva;

      // Enrichir les lignes_tva avec les comptes PCG
      const COMPTES_TVA = { 20: '445662', 10: '445661', 5.5: '445663', 2.1: '445664' };
      const lignes_tva_enrichies = (validationForm.lignes_tva || []).map(l => ({
        ...l,
        compte_tva: l.compte_tva || COMPTES_TVA[l.taux] || '445662'
      }));

      const response = await fetch(`/documents/${clientId}/documents/${documentId}/valider`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          montant_ht: montant_ht,
          tva: tva,
          montant_ttc: montant_ttc,
          taux_tva: parseFloat(validationForm.taux_tva),
          fournisseur: validationForm.fournisseur,
          compte_pcg: validationForm.compte_pcg || '607000',
          date_facture: selectedDocument.date_facture || new Date().toISOString().split('T')[0],
          lignes_tva: lignes_tva_enrichies  // VENTILATION MULTI-TAUX
        })
      });

      if (response.ok) {
        const result = await response.json();
        const nbTaux = result.nb_taux_differents || 0;
        const msgTaux = nbTaux > 1 ? `\n\nVentilation TVA : ${nbTaux} taux différents comptabilisés` : '';
        alert(`✅ Document validé avec succès !\n\nÉcritures comptables générées : ${result.ecritures_créées}${msgTaux}`);
        // Recharger la liste des documents et le dashboard
        loadDocuments();
        loadDashboardData();
        // Fermer la modale
        setShowValidationModal(false);
        setSelectedDocument(null);

        // Réinitialiser le formulaire
        setValidationForm({
          fournisseur: '',
          montant_ht: 0,
          tva: 0,
          taux_tva: 20,
          compte_pcg: '607000',
          lignes_tva: []
        });
      } else {
        const error = await response.json();
        alert(`❌ Erreur lors de la validation : ${error.detail}`);
      }
    } catch (error) {
      console.error('Erreur validation document:', error);
      alert('❌ Erreur lors de la validation');
    }
  };

  // Fonction pour charger les détails complets d'un document
  const loadDocumentDetails = async (documentId) => {
    setLoadingDocumentDetails(true);
    try {
      console.log('📄 DEBUG - Chargement détails document:', documentId);

      const response = await fetch(`/documents/${clientId}/documents/${documentId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log('📄 DEBUG - Réponse API brute:', data);

      // Transformer les données pour correspondre au format attendu
      const transformedDoc = {
        id: data.id,
        nom: data.nom,
        fournisseur: data.fournisseur?.nom || data.fournisseur || '',
        fournisseur_complet: data.fournisseur,  // Conserver toutes les infos fournisseur
        date_facture: data.dates?.facture,
        date_reception: data.dates?.reception,
        date_echeance: data.dates?.echeance,
        montant_ht: data.montants?.ht,
        tva: data.montants?.tva,
        taux_tva: data.tva?.taux,
        montant_ttc: data.montants?.ttc,
        devise: data.montants?.devise,
        tva_complet: data.tva,  // Conserver toutes les infos TVA
        extraction: data.extraction,  // Conserver les métadonnées OCR
        statut: data.statut,
        ecritures: data.ecritures || []  // Ajouter les écritures comptables
      };

      console.log('📄 DEBUG - Document transformé:', transformedDoc);
      console.log('📄 DEBUG - Conformite:', data.conformite);

      setSelectedDocument(transformedDoc);
      setSelectedDocumentConformite(data.conformite || null);

      // Initialiser le formulaire de validation avec les valeurs extraites
      // Récupérer les lignes_tva depuis la réponse (uploadées depuis API recents OU upload direct)
      const lignesTvaInitiales = transformedDoc.lignes_tva || data.lignes_tva || [];

      setValidationForm({
        fournisseur: transformedDoc.fournisseur || '',
        montant_ht: transformedDoc.montant_ht || 0,
        tva: transformedDoc.tva || 0,
        taux_tva: transformedDoc.taux_tva || 20,
        compte_pcg: transformedDoc.compte_pcg || '607000',
        lignes_tva: lignesTvaInitiales
      });

      setShowValidationModal(true);
    } catch (error) {
      console.error('Erreur chargement détails document:', error);
      alert('❌ Erreur lors du chargement des détails du document');
    } finally {
      setLoadingDocumentDetails(false);
    }
  };

  // Fonction pour supprimer un document
  const supprimerDocument = async (documentId) => {
    if (isExerciseClosed) {
      alert("❌ Impossible de supprimer un document sur un exercice clôturé.");
      return;
    }

    if (!confirm('⚠️ Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible.')) {
      return;
    }

    try {
      const response = await fetch(`/documents/${clientId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        alert('✅ Document supprimé avec succès !');
        // Recharger la liste des documents et le dashboard
        loadDocuments();
        loadDashboardData();
        // Fermer la modale
        setShowValidationModal(false);
        setSelectedDocument(null);

        // Réinitialiser le formulaire
        setValidationForm({
          fournisseur: '',
          montant_ht: 0,
          tva: 0,
          taux_tva: 20,
          compte_pcg: '607000',
          lignes_tva: []
        });
      } else {
        const error = await response.json();
        alert(`❌ Erreur lors de la suppression : ${error.detail}`);
      }
    } catch (error) {
      console.error('Erreur suppression document:', error);
      alert('❌ Erreur lors de la suppression');
    }
  };

  // Refs pour auto-scroll chat
  const chatMessagesRef = useRef(null);
  const dashboardChatMessagesRef = useRef(null);

  // Refs pour inputs file (upload mobile)
  const fileInputCameraRef = useRef(null);
  const fileInputGalleryRef = useRef(null);
  const fileInputPdfRef = useRef(null);
  const fileInputDesktopRef = useRef(null);
  const fileInputDocumentsDesktopRef = useRef(null);

  // Auto-scroll vers le bas quand les messages changent
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Les écouteurs d'événements sont maintenant attachés directement via onChange dans le JSX

  useEffect(() => {
    if (dashboardChatMessagesRef.current) {
      dashboardChatMessagesRef.current.scrollTop = dashboardChatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Charge la configuration personnalisée du tableau de bord
  const loadDashboardConfig = async () => {
    try {
      const response = await api.get(`/dashboard/${clientId}/config`);
      if (response.success && response.data?.widgets) {
        const configMap = {};
        response.data.widgets.forEach(w => {
          configMap[w.id] = { active: w.active, size: w.size };
        });
        setDashboardConfig(configMap);
      }
    } catch (err) {
      console.warn('Impossible de charger la config du dashboard:', err);
    }
  };

  useEffect(() => {
    console.log('DEBUG ComptableDashboard: clientId =', clientId, 'loading =', loading);
    if (clientId) {
      loadDashboardData();
      loadDashboardConfig();
    }
  }, [clientId, selectedYear]);

  useEffect(() => {
    if (clientId) {
      loadClotureData();
    }
  }, [clientId, selectedYear]);

  const loadDashboardData = async () => {
    if (!clientId) return;

    try {
      const response = await api.get(`/dashboard/${clientId}/metrics?year=${selectedYear}`);
      console.log('DEBUG loadDashboardData:', JSON.stringify(response));

      if (response.metrics || response.client) {
        // Toujours charger les données, même si vides
        setData(response);

        // Vérifier si le client a des données réelles (pas de zéros)
        const hasRevenue = response.metrics &&
                          response.metrics.some(m => m.valeur_numerique > 0);
        const hasDocuments = response.documents_recents &&
                            response.documents_recents.length > 0;

        // isEmpty = true seulement si tout est à 0
        setIsEmpty(!hasRevenue && !hasDocuments);

        // Vérifier si un cadrage d'activité est nécessaire (première connexion)
        try {
          const cadrage = await api.get('/copilote/cadrage');
          if (cadrage && (cadrage.reponse_libre === 'Description de mon activité.' || !cadrage.reponse_libre || cadrage.secteur === 'autre')) {
            setActiveSection('chat');
            setChatMessages([
              {
                role: 'ai',
                text: `Bonjour ! Bienvenue sur **ComptaAI** 👋.

Pour que je puisse vous conseiller au mieux et configurer vos écritures comptables sans erreurs, j'aimerais réaliser un court diagnostic de démarrage avec vous.

**Quelle est l'activité principale de votre entreprise ?** (Ex: Transport VTC, E-commerce, Consultant freelance, Artisan électricien...)`,
                time: new Date().getHours() + ':' + String(new Date().getMinutes()).padStart(2,'0')
              }
            ]);
          }
        } catch (cadrageErr) {
          console.warn('Erreur vérification cadrage:', cadrageErr);
        }
      } else {
        console.error('DEBUG loadDashboardData: response.success = false');
        console.error('DEBUG response.error:', response.error);
      }
    } catch (err) {
      console.error('DEBUG loadDashboardData catch:', err);
      setIsEmpty(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeSection === 'ecritures' && ecrituresData.length === 0) {
      loadEcritures();
    }
    if (activeSection === 'tva' && !tvaData) {
      loadTva();
    }
    if (activeSection === 'documents') {
      loadDocuments();
    }
    if (activeSection === 'dashboard') {
      loadDashboardConfig();
    }
    if (activeSection === 'dashboard' && !chargesData) {
      loadChargesData();
    }
    if (activeSection === 'dashboard' && !tvaDetailData) {
      loadTvaDetailData();
    }
    if (activeSection === 'bank') {
      loadBankTransactions();
    }
    if (activeSection === 'bilan' && !bilanData) {
      loadBilan();
    }
    if (activeSection === 'support') {
      loadSupportTickets();
    }
    if (activeSection === 'cloture') {
      loadClotureData();
    }
    if (activeSection === 'parametres') {
      loadSyncConfig();
    }
  }, [activeSection, selectedYear]);

  useEffect(() => {
    if (activeSection === 'documents') {
      loadDocuments();
    }
  }, [activeSection, selectedYear, docMonth, docStatus, docSearch]);

  const loadSupportTickets = async () => {
    setTicketsLoading(true);
    try {
      const res = await api.get('/tickets');
      if (res.success && res.tickets) {
        setSupportTickets(res.tickets);
        if (selectedTicket) {
          const updated = res.tickets.find(t => t.id === selectedTicket.id);
          if (updated) setSelectedTicket(updated);
        }
      }
    } catch (err) {
      console.error('Erreur chargement tickets:', err);
    } finally {
      setTicketsLoading(false);
    }
  };

  const handleCreateTicketSubmit = async (e) => {
    e.preventDefault();
    if (!newTicketForm.sujet.trim() || !newTicketForm.description.trim()) return;

    setTicketActionLoading(true);
    setTicketSuccessMsg(null);
    try {
      const res = await api.post('/tickets', {
        sujet: newTicketForm.sujet,
        description: newTicketForm.description,
        categorie: newTicketForm.categorie,
        priorite: newTicketForm.priorite
      });
      if (res.success) {
        setTicketSuccessMsg('✅ Votre ticket a été créé avec succès ! Nos équipes y répondront très bientôt.');
        setNewTicketForm({ sujet: '', description: '', categorie: 'technique', priorite: 'moyenne' });
        setShowCreateTicket(false);
        loadSupportTickets();
      }
    } catch (err) {
      console.error('Erreur création ticket:', err);
      alert('Erreur lors de la création du ticket. Veuillez réessayer.');
    } finally {
      setTicketActionLoading(false);
    }
  };

  const handleSendTicketReply = async (e) => {
    e.preventDefault();
    if (!ticketReplyText.trim() || !selectedTicket) return;

    setTicketActionLoading(true);
    try {
      const res = await api.post(`/tickets/${selectedTicket.id}/messages`, {
        text: ticketReplyText
      });
      if (res.success && res.messages) {
        setTicketReplyText('');
        loadSupportTickets();
      }
    } catch (err) {
      console.error('Erreur envoi message ticket:', err);
    } finally {
      setTicketActionLoading(false);
    }
  };

  const loadDocuments = async () => {
    console.log('📄 DEBUG - loadDocuments() appelé');
    setDocumentsLoading(true);
    try {
      let url = `/documents/${clientId}/recents?year=${selectedYear}`;
      if (docMonth !== null) {
        url += `&month=${docMonth}`;
      }
      if (docStatus === 'a_valider') {
        url += `&statut=en_attente`;
      } else if (docStatus === 'traites') {
        url += `&statut=traite`;
      } else if (docStatus === 'non_rapproches') {
        url += `&rapproche=false`;
      }
      if (docSearch) {
        url += `&search=${encodeURIComponent(docSearch)}`;
      }
      const response = await api.get(url);
      console.log('📄 DEBUG - Réponse /recents:', response);
      if (response && response.documents) {
        console.log('📄 DEBUG - Documents chargés:', response.documents.length, 'documents');
        setDocumentsData(response);
      } else {
        console.error('📄 DEBUG - Response invalide:', response);
      }
    } catch (err) {
      console.error('Erreur chargement documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const loadTva = async () => {
    setTvaLoading(true);
    try {
      const response = await api.get(`/tva/${clientId}/current`);
      if (response && response.periode) {
        setTvaData(response);
      }
    } catch (err) {
      console.error('Erreur chargement TVA:', err);
    } finally {
      setTvaLoading(false);
    }
  };

  const loadEcritures = async () => {
    setEcrituresLoading(true);
    try {
      const response = await api.get(`/dashboard/${clientId}/ecritures`);
      if (response.success && response.ecritures) {
        setEcrituresData(response.ecritures);
      }
    } catch (err) {
      console.error('Erreur chargement écritures:', err);
    } finally {
      setEcrituresLoading(false);
    }
  };

  const loadChargesData = async () => {
    setChargesLoading(true);
    try {
      const response = await api.get(`/dashboard/${clientId}/charges-repartition?year=${selectedYear}`);
      if (response && response.repartition) {
        setChargesData(response);
      }
    } catch (err) {
      console.error('Erreur chargement répartition charges:', err);
    } finally {
      setChargesLoading(false);
    }
  };

  const loadTvaDetailData = async () => {
    setTvaDetailLoading(true);
    try {
      const response = await api.get(`/dashboard/${clientId}/tva-detail?year=${selectedYear}`);
      if (response && response.tva_collectee) {
        setTvaDetailData(response);
      }
    } catch (err) {
      console.error('Erreur chargement détail TVA:', err);
    } finally {
      setTvaDetailLoading(false);
    }
  };

  // Fonctions pour les relevés bancaires
  const loadBankTransactions = async () => {
    console.log('🏦 DEBUG - loadBankTransactions() appelé');
    setBankLoading(true);
    try {
      const response = await api.get(`/bank/${clientId}/transactions?limit=100`);
      console.log('🏦 DEBUG - Réponse transactions:', response);
      if (response && response.transactions) {
        console.log('🏦 DEBUG - Transactions chargées:', response.transactions.length, 'transactions');
        setBankData(response);
      } else {
        console.error('🏦 DEBUG - Response invalide:', response);
      }
    } catch (err) {
      console.error('Erreur chargement transactions bancaires:', err);
    } finally {
      setBankLoading(false);
    }
  };

  const loadBilan = async () => {
    setBilanLoading(true);
    try {
      const response = await api.get(`/dashboard/${clientId}/bilan`);
      if (response) {
        setBilanData(response);
      }
    } catch (err) {
      console.error('Erreur chargement bilan:', err);
    } finally {
      setBilanLoading(false);
    }
  };

  const handleBankUpload = async (file) => {
    if (!file) return;

    console.log('🏦 Upload relevé bancaire:', file.name);
    setUploadInProgress(true);
    setUploadFileName(file.name);
    setUploadStatus('Import en cours...');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.upload(`/bank/${clientId}/upload`, formData);

      if (response && response.success) {
        const stats = response.statistiques;
        setUploadStatus(`✅ ${stats.nb_transactions} transactions importées`);
        console.log('🏦 Upload réussi:', stats);

        // Recharger les transactions
        setTimeout(() => loadBankTransactions(), 500);
      } else {
        setUploadStatus('❌ Erreur lors de l\'import');
      }
    } catch (err) {
      console.error('Erreur upload relevé bancaire:', err);
      setUploadStatus('❌ Erreur lors de l\'import');
    } finally {
      setUploadInProgress(false);
      setUploadProgress(0);
      setUploadFileName('');
      setTimeout(() => setUploadStatus(''), 3000);
    }
  };

  const handleRapprochement = async () => {
    console.log('🔍 Rapprochement automatique lancé');
    setRapprochementLoading(true);

    try {
      const response = await api.post(`/bank/${clientId}/rapprochement`);

      if (response && response.success) {
        // Cas avec statistiques
        if (response.statistiques) {
          const stats = response.statistiques;
          alert(`✅ Rapprochement terminé !\n\n${response.message}\n\nTaux de rapprochement : ${stats.taux_rapprochement}%`);
        } else {
          // Cas sans statistiques (aucune transaction)
          alert(`✅ ${response.message}`);
        }
        console.log('🔍 Rapprochement réussi:', response);

        // Recharger les transactions
        loadBankTransactions();
      } else {
        alert('❌ Erreur lors du rapprochement');
      }
    } catch (err) {
      console.error('Erreur rapprochement:', err);
      alert('❌ Erreur lors du rapprochement');
    } finally {
      setRapprochementLoading(false);
    }
  };

  const sendChatMessage = async (question) => {
    if (!question.trim() || chatLoading) return;

    // Vérification du quota Chat pour le plan gratuit
    if (subData && subData.plan && subData.plan.id === 'gratuit') {
      const chatUsage = subData.usage_mois?.chat || 0;
      const chatLimit = subData.features?.chat?.limite || 10;
      if (chatUsage >= chatLimit) {
        setUpgradePopup({
          show: true,
          title: '💬 Quota Chat IA Épuisé',
          message: 'Vous avez atteint votre limite mensuelle de 10 messages de Chat IA sur le plan Gratuit. Ce quota sera automatiquement remis à zéro au début du mois prochain. Pour continuer à échanger sans limites dès aujourd\'hui, passez au plan supérieur !'
        });
        return;
      }
    }

    const userMsg = { role: 'user', text: question, time: getCurrentTime() };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);
    try {
      // Utiliser Hermes Agent
      const response = await api.post(`/hermes/${clientId}/chat`, { message: question });

      // Réponse normalisée de Hermes : {response: "texte", agent: "hermes"}
      const answer = response.response || response.error || 'Désolé, je n\'ai pas pu traiter votre question. Réessayez.';

      setChatMessages(prev => [...prev, { role: 'ai', text: answer, time: getCurrentTime() }]);
      
      // Rafraîchir l'abonnement pour mettre à jour les compteurs
      loadAbonnement();
    } catch (err) {
      console.error('Erreur chat:', err);
      setChatMessages(prev => [...prev, { role: 'ai', text: 'Erreur de connexion à l\'assistant. Réessayez.', time: getCurrentTime() }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleSendChat = () => sendChatMessage(chatInput);
  const handleQuickQuestion = (question) => { setActiveSection('chat'); sendChatMessage(question); };

  // Handlers pour Pull-to-Refresh
  const handleTouchStart = (e) => {
    // Uniquement si on est en haut de la page
    if (window.scrollY === 0 && activeSection === 'chat') {
      setStartY(e.touches[0].clientY);
      setPulling(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!pulling) return;

    const currentY = e.touches[0].clientY;
    const deltaY = currentY - startY;

    // Uniquement si on tire vers le bas (deltaY positif)
    if (deltaY > 0) {
      // Limiter la distance de tirage
      const maxPull = 120;
      const resistance = 0.4; // Résistance pour le tirage
      setPullDistance(Math.min(deltaY * resistance, maxPull));
    }
  };

  const handleTouchEnd = async () => {
    if (!pulling) return;

    // Si on a tiré assez loin (> 60px), déclencher le refresh
    if (pullDistance > 60) {
      setRefreshing(true);
      await loadDashboardData();
      setTimeout(() => {
        setRefreshing(false);
        setPullDistance(0);
      }, 500);
    } else {
      setPullDistance(0);
    }

    setPulling(false);
    setStartY(0);
  };

  const getCurrentTime = () => {
    const now = new Date();
    return `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  const handleFileUpload = async (e) => {
    console.log('=== HANDLEFILEUPLOAD START ===');

    if (isExerciseClosed) {
      alert("🔒 Cet exercice est clôturé et archivé. Le dépôt de documents est désactivé.");
      return;
    }

    // Empêcher le rechargement de page par défaut sur mobile
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      console.log('Event prevented');
    }

    console.log('DEBUG: handleFileUpload called', e);
    const files = e.dataTransfer?.files || e.target?.files;
    console.log('DEBUG: Files selected', files);

    if (files && files.length > 0) {
      const file = files[0];
      console.log('DEBUG: File to upload', file.name, file.size, file.type);

      // Vérification du quota OCR pour le plan gratuit
      if (subData && subData.plan && subData.plan.id === 'gratuit') {
        const ocrUsage = subData.usage_mois?.ocr || 0;
        const ocrLimit = subData.features?.ocr?.limite || 10;
        if (ocrUsage >= ocrLimit) {
          setUpgradePopup({
            show: true,
            title: '📄 Quota OCR Épuisé',
            message: 'Vous avez atteint votre limite mensuelle de 10 traitements OCR de factures sur le plan Gratuit. Ce quota sera automatiquement remis à zéro au début du mois prochain. Pour traiter vos factures en illimité dès aujourd\'hui, passez au plan Pro !'
          });
          return;
        }
      }

      // VALIDATION FORMAT - Avant tout envoi
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      const acceptedFormats = ['pdf', 'jpg', 'jpeg', 'png'];

      if (!acceptedFormats.includes(fileExtension)) {
        const errorMsg = { role: 'ai', text: '❌ Format non supporté. Utilisez PDF, JPG ou PNG uniquement', time: getCurrentTime() };
        setChatMessages(prev => [...prev, errorMsg]);
        return;
      }

      // Message format accepté
      let formatMessage = '';
      if (fileExtension === 'pdf') {
        formatMessage = '✅ Format PDF — valeur probante optimale';
      } else if (fileExtension === 'jpg' || fileExtension === 'jpeg' || fileExtension === 'png') {
        formatMessage = '⚠️ Format image - PDF recommandé pour facture B2B (valeur probante légale)';
      }

      if (formatMessage) {
        const formatMsg = { role: 'ai', text: formatMessage, time: getCurrentTime() };
        setChatMessages(prev => [...prev, formatMsg]);
      }

      // Réinitialiser l'input file pour permettre de ré-uploader le même fichier
      // IMPORTANT : après avoir récupéré le fichier, pas avant !
      if (e.target) {
        e.target.value = '';
      }

      // Initialiser l'état d'upload
      setUploadInProgress(true);
      setUploadProgress(0);
      setUploadFileName(file.name);
      setUploadStatus('Préparation de l\'envoi...');

      const formData = new FormData();
      formData.append('file', file);

      try {
        console.log('DEBUG: Starting upload to', `/documents/${clientId}/upload`);

        // Utiliser XMLHttpRequest pour le suivi de progression
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(percentComplete);

            if (percentComplete < 30) {
              setUploadStatus(`Envoi en cours... ${percentComplete}%`);
            } else if (percentComplete < 70) {
              setUploadStatus(`Analyse OCR en cours... ${percentComplete}%`);
            } else if (percentComplete < 95) {
              setUploadStatus(`Traitement comptable... ${percentComplete}%`);
            } else {
              setUploadStatus(`Finalisation... ${percentComplete}%`);
            }
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            try {
              const result = JSON.parse(xhr.responseText);
              console.log('DEBUG: Upload successful', result);

              if (result.document) {
                setUploadStatus('Upload terminé avec succès !');
                setUploadProgress(100);

                // Rafraîchir l'abonnement pour mettre à jour les compteurs
                loadAbonnement();

                // Message de succès avec alerte conformité si nécessaire
                setTimeout(() => {
                  let messageText = `✅ Document "${result.document.nom}" reçu et analysé !\n\nStatut : ${result.statut === 'en_attente' ? '⏳ En attente de validation' : '✅ Traité automatiquement'}`;

                  const conformite = result.conformite;

                  if (conformite) {
                    // 1. QUALITÉ OCR (si alerte)
                    if (conformite.qualite_ocr && conformite.qualite_ocr.alerte) {
                      messageText += `\n\n${conformite.qualite_ocr.message}`;
                    }

                    // 2. IDENTITÉ CLIENT
                    if (conformite.au_nom_client !== undefined) {
                      if (conformite.au_nom_client) {
                        messageText += `\n\n✅ Facture établie au nom de ${conformite.nom_client_attendu || 'votre société'}`;
                      } else {
                        messageText += `\n\n🚨 Facture NON établie au nom de ${conformite.nom_client_attendu || 'votre société'}`;
                      }
                    }

                    // 3. TYPE DOCUMENT
                    if (conformite.type_label) {
                      const typeIcons = {
                        'Facture B2B': '📄',
                        'Ticket de caisse': '🧾',
                        'Note de frais': '🍽️'
                      };
                      const icon = typeIcons[conformite.type_label] || '📄';
                      messageText += `\n\n${icon} ${conformite.type_label}`;
                    }

                    // 4. SCORE CONFORMITÉ
                    if (conformite.score !== undefined) {
                      const score = conformite.score;
                      const champsPresents = conformite.champs_presents || 0;
                      const champsRequis = conformite.champs_requis || 0;

                      messageText += `\n\nScore conformité : ${score}/100 (${champsPresents}/${champsRequis} champs présents)`;

                      // 5. CHAMPS DÉTAIL
                      if (conformite.champs_detail) {
                        const champsDetails = conformite.champs_detail;
                        const champsLabels = {
                          'numero_facture': 'Numéro de facture',
                          'date': 'Date',
                          'siret': 'SIRET',
                          'siren': 'SIREN',
                          'montant_ttc': 'Montant TTC',
                          'montant_ht': 'Montant HT',
                          'taux_tva': 'Taux de TVA',
                          'adresse_vendeur': 'Adresse vendeur'
                        };

                        messageText += `\n\nChamps détectés :`;
                        for (const [champ, present] of Object.entries(champsDetails)) {
                          const label = champsLabels[champ] || champ;
                          const status = present ? '✅' : '❌';
                          messageText += `\n${status} ${label}`;
                        }

                        // Champs manquants
                        const champsManquants = conformite.champs_manquants || [];
                        if (champsManquants.length > 0) {
                          messageText += `\n\n⚠️ Champs manquants :\n• ${champsManquants.join('\n• ')}`;
                        }
                      }

                      // 6. RECOMMANDATION FINALE (basée sur conforme, pas score)
                      if (!conformite.au_nom_client) {
                        messageText += `\n\n🚨 ${conformite.message_alerte || 'Document non établi au nom de votre société'}`;
                      } else if (!conformite.conforme) {
                        messageText += `\n\n🚨 Document non conforme — ne pas comptabiliser en l'état`;
                      } else {
                        messageText += `\n\n✅ Document conforme — vous pouvez valider`;
                      }
                    }
                  }

                  const successMsg = { role: 'ai', text: messageText, time: getCurrentTime() };
                  setChatMessages(prev => [...prev, successMsg]);

                  // Recharger les documents et le dashboard avec un délai pour laisser le backend finaliser
                  setTimeout(() => {
                    loadDocuments();
                    loadDashboardData();
                  }, 1000);

                  // Réinitialiser l'état après un délai
                  setTimeout(() => {
                    setUploadInProgress(false);
                    setUploadProgress(0);
                    setUploadFileName('');
                    setUploadStatus('');
                  }, 2000);
                }, 500);
              }
            } catch (e) {
              console.error('DEBUG: JSON parse error', e);
              setUploadStatus('Erreur lors du traitement de la réponse');
              setUploadInProgress(false);
              alert('❌ Erreur lors du traitement de la réponse du serveur');
            }
          } else {
            console.error('DEBUG: Upload failed', xhr.status, xhr.responseText);
            setUploadStatus('Erreur lors de l\'upload');
            setUploadInProgress(false);
            
            let errorMessage = `Erreur lors de l'upload : ${xhr.status}`;
            try {
              const errObj = JSON.parse(xhr.responseText);
              if (errObj && errObj.detail) {
                errorMessage = errObj.detail;
              }
            } catch (e) {}
            
            alert(`❌ ${errorMessage}`);
          }
        });

        xhr.addEventListener('error', () => {
          console.error('DEBUG: XHR error');
          setUploadStatus('Erreur de connexion');
          setUploadInProgress(false);
          alert('❌ Erreur de connexion au serveur');
        });

        xhr.open('POST', `/documents/${clientId}/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('auth_token')}`);
        xhr.send(formData);

      } catch (error) {
        console.error('DEBUG: Upload error', error);
        setUploadStatus('Erreur lors de l\'upload');
        setUploadInProgress(false);
        alert(`❌ Erreur lors de l'upload : ${error.message}`);
      }
    }
  };

  if (loading) {
    return (
      <div style={{height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F5F4ED'}}>
        <div style={{textAlign: 'center'}}>
          <div style={{width: '48px', height: '48px', border: '4px solid #1D9E75', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite'}}></div>
          <p style={{color: '#73726C', fontSize: '14px'}}>Chargement...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div style={{height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F5F4ED', fontFamily: 'Inter, system-ui, sans-serif'}}>
        <div style={{background: 'white', padding: '40px', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', textAlign: 'center', maxWidth: '400px'}}>
          <div style={{fontSize: '48px', marginBottom: '16px'}}>⚠️</div>
          <h2 style={{fontSize: '20px', fontWeight: '600', color: '#2D2D2D', marginBottom: '12px'}}>Erreur de navigation</h2>
          <p style={{fontSize: '14px', color: '#73726C', marginBottom: '24px'}}>
            Aucun client connecté. Veuillez vous reconnecter.
          </p>
          <button
            onClick={onLogout}
            style={{padding: '12px 24px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'}}
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="p-8 text-center" style={{background: '#F5F4ED'}}>Erreur de chargement</div>;
  }

  return (
    <div className={`app ${activeSection === 'chat' ? 'chat-mode' : ''}`} style={{display: 'flex', height: '100%', fontFamily: 'Inter, system-ui, sans-serif', overflow: 'hidden'}}>
      {/* SIDEBAR */}
      <div className="sidebar" style={{width: '250px', minWidth: '250px', background: 'white', borderRight: '1px solid #E8E7E3', display: 'flex', flexDirection: 'column', height: '100%', flexShrink: 0, overflow: 'hidden'}}>
        {/* Logo */}
        <div className="sidebar-logo" style={{padding: '20px 16px 16px', borderBottom: '1px solid #E8E7E3', flexShrink: 0}}>
          <div className="logo-mark" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <div className="logo-icon" style={{width: '28px', height: '28px', borderRadius: '6px', background: '#1D9E75', color: 'white', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              C
            </div>
            <div>
              <div className="logo-text" style={{fontSize: '14px', fontWeight: '500', color: '#2D2D2D'}}>ComptaAI</div>
              <div className="logo-sub" style={{fontSize: '11px', color: '#73726C', marginTop: '1px'}}>Assistant comptable</div>
            </div>
          </div>
        </div>

        {/* Client Badge */}
        {data?.client && (
          <div className="client-badge" style={{margin: '12px 12px 4px', background: '#FAF9F5', borderRadius: '8px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0}}>
            <div className="client-avatar" style={{width: '28px', height: '28px', borderRadius: '50%', background: '#E1F5EE', color: '#0F6E56', fontSize: '11px', fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              {(data.client.nom || 'CN')[0].toUpperCase()}
            </div>
            <div>
              <div className="client-name" style={{fontSize: '12px', fontWeight: '500', color: '#2D2D2D'}}>{data.client.nom}</div>
              <div className="client-siren" style={{fontSize: '10px', color: '#73726C'}}>SIREN {data.client.siren}</div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="nav" style={{padding: '8px 0', flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch'}}>
          <div className="nav-section" style={{padding: '12px 16px 4px', fontSize: '10px', fontWeight: '500', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.06em'}}>
            PRINCIPAL
          </div>
          {[{id:'dashboard',icon:'▦',label:'Tableau de bord'},{id:'echeances',icon:'📅',label:'Échéances & Rappels',badge:'New',badgeColor:'#1D9E75'},{id:'cadrage',icon:'🎯',label:'Cadrage & Tiers',badge:'New',badgeColor:'#1D9E75'},{id:'documents',icon:'↑',label:'Déposer documents',badge:data?.documents_recents?.length||null,badgeColor:'#EF9F27'},{id:'cloture',icon:'⏳',label:'Suivi & Clôture',badge:'New',badgeColor:'#7C3AED'},{id:'ecritures',icon:'≡',label:'Écritures'},{id:'chat',icon:'◎',label:'Assistant IA'}].map(item => (
            <div key={item.id} onClick={() => setActiveSection(item.id)}
              style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 12px',margin:'0 8px 1px',borderRadius:'8px',cursor:'pointer',fontSize:'13px',fontWeight: activeSection===item.id?'500':'400',background:activeSection===item.id?'#E1F5EE':'transparent',color:activeSection===item.id?'#0F6E56':'#2D2D2D',transition:'background 0.15s'}}
              onMouseEnter={e=>{if(activeSection!==item.id)e.currentTarget.style.background='#F5F4ED';}}
              onMouseLeave={e=>{if(activeSection!==item.id)e.currentTarget.style.background='transparent';}}
            >
              <span>{item.icon}</span><span style={{flex:1}}>{item.label}</span>
              {item.badge && <span style={{background:item.badgeColor,color:'white',fontSize:'10px',fontWeight:'500',padding:'1px 6px',borderRadius:'10px'}}>{item.badge}</span>}
            </div>
          ))}

          <div style={{padding:'12px 16px 4px',fontSize:'10px',fontWeight:'500',color:'#73726C',textTransform:'uppercase',letterSpacing:'0.06em'}}>FISCAL</div>
          {[{id:'creation_entreprise',icon:'🚀',label:'Création d\'Entreprise',badge:'A-Z',badgeColor:'#0284C7'},{id:'facturation',icon:'🧾',label:'Facturation Factur-X',badge:'2026',badgeColor:'#1D9E75'},{id:'documents_fiscaux',icon:'📄',label:'Documents fiscaux',badge:'New',badgeColor:'#1D9E75'},{id:'optimisation',icon:'📊',label:'Optimisation',badge:'AI',badgeColor:'#7C3AED'},{id:'tresorerie',icon:'↕',label:'Trésorerie',badge:'AI',badgeColor:'#3B82F6'},{id:'tva',icon:'%',label:'Déclaration TVA'},{id:'bilan',icon:'□',label:'Bilan comptable'},{id:'inventaire',icon:'📦',label:'Immobilisations',badge:'New',badgeColor:'#0891B2'},{id:'exports',icon:'↓',label:'Exports'}].map(item => (
            <div key={item.id} onClick={() => { console.log('DEBUG: Click menu', item.id); if (checkModuleAccess(item.id)) setActiveSection(item.id); }}
              style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 12px',margin:'0 8px 1px',borderRadius:'8px',cursor:'pointer',fontSize:'13px',background:activeSection===item.id?'#E1F5EE':'transparent',color:activeSection===item.id?'#0F6E56':'#2D2D2D',transition:'background 0.15s'}}
              onMouseEnter={e=>{if(activeSection!==item.id)e.currentTarget.style.background='#F5F4ED';}}
              onMouseLeave={e=>{if(activeSection!==item.id)e.currentTarget.style.background='transparent';}}
            >
              <span>{item.icon}</span><span style={{flex:1}}>{item.label}</span>
              {item.badge && <span style={{background:item.badgeColor,color:'white',fontSize:'10px',fontWeight:'500',padding:'1px 6px',borderRadius:'10px'}}>{item.badge}</span>}
            </div>
          ))}

          <div style={{padding:'12px 16px 4px',fontSize:'10px',fontWeight:'500',color:'#73726C',textTransform:'uppercase',letterSpacing:'0.06em'}}>SOCIAL</div>
          {[{id:'social',icon:'👥',label:'Salariés & Paie',badge:'New',badgeColor:'#7C3AED'},{id:'urssaf',icon:'🏛️',label:'URSSAF / DSN'}].map(item => (
            <div key={item.id} onClick={() => { if (checkModuleAccess(item.id)) setActiveSection(item.id); }}
              style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 12px',margin:'0 8px 1px',borderRadius:'8px',cursor:'pointer',fontSize:'13px',background:activeSection===item.id?'#E1F5EE':'transparent',color:activeSection===item.id?'#0F6E56':'#2D2D2D',transition:'background 0.15s'}}
              onMouseEnter={e=>{if(activeSection!==item.id)e.currentTarget.style.background='#F5F4ED';}}
              onMouseLeave={e=>{if(activeSection!==item.id)e.currentTarget.style.background='transparent';}}
            >
              <span>{item.icon}</span><span style={{flex:1}}>{item.label}</span>
              {item.badge && <span style={{background:item.badgeColor,color:'white',fontSize:'10px',fontWeight:'500',padding:'1px 6px',borderRadius:'10px'}}>{item.badge}</span>}
            </div>
          ))}

          <div style={{padding:'12px 16px 4px',fontSize:'10px',fontWeight:'500',color:'#73726C',textTransform:'uppercase',letterSpacing:'0.06em'}}>SERVICES IA PREMIUM</div>
          {[
            {id:'juridique',icon:'⚖',label:'Juridique',badge:'Pro',badgeColor:'#D97706'},
            {id:'comptabilite_premium',icon:'🧮',label:'Compta Premium',badge:'Pro',badgeColor:'#D97706'},
            {id:'fiscal_premium',icon:'📝',label:'Fiscal BOFiP',badge:'Pro',badgeColor:'#D97706'},
            {id:'social_premium',icon:'👥',label:'Social BOSS',badge:'Pro',badgeColor:'#D97706'}
          ].map(item => (
            <div key={item.id} onClick={() => { if (checkModuleAccess(item.id)) setActiveSection(item.id); }}
              style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 12px',margin:'0 8px 1px',borderRadius:'8px',cursor:'pointer',fontSize:'13px',background:activeSection===item.id?'#E1F5EE':'transparent',color:activeSection===item.id?'#0F6E56':'#2D2D2D',transition:'background 0.15s'}}
              onMouseEnter={e=>{if(activeSection!==item.id)e.currentTarget.style.background='#F5F4ED';}}
              onMouseLeave={e=>{if(activeSection!==item.id)e.currentTarget.style.background='transparent';}}
            >
              <span>{item.icon}</span><span style={{flex:1}}>{item.label}</span>
              {item.badge && <span style={{background:item.badgeColor,color:'white',fontSize:'10px',fontWeight:'500',padding:'1px 6px',borderRadius:'10px'}}>{item.badge}</span>}
            </div>
          ))}

          <div style={{padding:'12px 16px 4px',fontSize:'10px',fontWeight:'500',color:'#73726C',textTransform:'uppercase',letterSpacing:'0.06em'}}>COMPTE</div>
          <div onClick={() => setActiveSection('bank')}
            style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 12px',margin:'0 8px 1px',borderRadius:'8px',cursor:'pointer',fontSize:'13px',background:activeSection==='bank'?'#E1F5EE':'transparent',color:activeSection==='bank'?'#0F6E56':'#2D2D2D'}}
            onMouseEnter={e=>{if(activeSection!=='bank')e.currentTarget.style.background='#F5F4ED';}}
            onMouseLeave={e=>{if(activeSection!=='bank')e.currentTarget.style.background='transparent';}}
          >
            <span>🏦</span><span>Relevés bancaires</span>
          </div>
          <div onClick={() => setActiveSection('parametres')}
            style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 12px',margin:'0 8px 1px',borderRadius:'8px',cursor:'pointer',fontSize:'13px',background:activeSection==='parametres'?'#E1F5EE':'transparent',color:activeSection==='parametres'?'#0F6E56':'#2D2D2D'}}
          >
            <span>◈</span><span>Paramètres</span>
          </div>
          <div onClick={() => setActiveSection('abonnement')}
            style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 12px',margin:'0 8px 1px',borderRadius:'8px',cursor:'pointer',fontSize:'13px',background:activeSection==='abonnement'?'#E1F5EE':'transparent',color:activeSection==='abonnement'?'#0F6E56':'#2D2D2D'}}
            onMouseEnter={e=>{if(activeSection!=='abonnement')e.currentTarget.style.background='#F5F4ED';}}
            onMouseLeave={e=>{if(activeSection!=='abonnement')e.currentTarget.style.background='transparent';}}
          >
            <span>💳</span><span>Mon Abonnement</span>
          </div>
          <div onClick={() => setActiveSection('support')}
            style={{display:'flex',alignItems:'center',gap:'8px',padding:'7px 12px',margin:'0 8px 1px',borderRadius:'8px',cursor:'pointer',fontSize:'13px',background:activeSection==='support'?'#E1F5EE':'transparent',color:activeSection==='support'?'#0F6E56':'#2D2D2D'}}
            onMouseEnter={e=>{if(activeSection!=='support')e.currentTarget.style.background='#F5F4ED';}}
            onMouseLeave={e=>{if(activeSection!=='support')e.currentTarget.style.background='transparent';}}
          >
            <span>🎫</span><span>Support & Tickets</span>
          </div>
          {subData && subData.plan && (
            <div style={{
              margin: '12px 8px 8px',
              padding: '10px 12px',
              borderRadius: '8px',
              background: subData.plan.id === 'gratuit' ? '#FCE8E6' : '#E1F5EE',
              border: subData.plan.id === 'gratuit' ? '1px solid #EF4444' : '1px solid #1D9E75',
              color: subData.plan.id === 'gratuit' ? '#EF4444' : '#0F6E56',
              fontSize: '11px',
              fontWeight: '600',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'transform 0.15s ease'
            }} 
            onClick={() => setActiveSection('abonnement')}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'none'}
            >
              {subData.plan.id === 'gratuit' ? '🔴 Plan Gratuit (Free)' : `🟢 Plan ${subData.plan.nom}`}
              {subData.plan.id === 'gratuit' && <div style={{fontSize: '9px', fontWeight: 'normal', marginTop: '2px', textDecoration: 'underline'}}>🚀 Passer au plan Pro</div>}
            </div>
          )}

          <div
            className="nav-item"
            onClick={onLogout}
            style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', margin: '0 8px 1px', borderRadius: '8px', color: '#D85A30', fontSize: '13px', cursor: 'pointer'}}
            onMouseEnter={(e) => e.currentTarget.style.background = '#FAEEDA'}
            onMouseLeave={(e) => e.currentTarget.style.background = ''}
          >
            <span className="nav-icon">↩</span>
            <span>Déconnexion</span>
          </div>
        </div>

        {/* Footer */}
        <div className="sidebar-footer" style={{padding: '12px', borderTop: '1px solid #E8E7E3', flexShrink: 0}}>
          <div className="regime-tag" style={{fontSize: '11px', color: '#73726C', background: '#FAF9F5', padding: '4px 8px', borderRadius: '8px', textAlign: 'center'}}>
            {data?.client?.regime || 'Réel normal'} · TVA {data?.client?.tva || 'Mensuelle'}
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className={`main ${activeSection === 'chat' ? 'chat-mode' : ''}`} style={{flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden'}}>
        {/* Topbar */}
        <div className="topbar" style={{background:'white',borderBottom:'1px solid #E8E7E3',padding:'0 24px',height:'52px',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
          <div className="topbar-title" style={{fontSize:'15px',fontWeight:'500',color:'#2D2D2D'}}>
            {activeSection==='dashboard' && `Tableau de bord — ${data.periode.mois}`}
            {activeSection==='cadrage' && '🎯 Cadrage Métier & Référentiel Tiers'}
            {activeSection==='chat' && '◎ Assistant IA'}
            {activeSection==='documents' && '↑ Déposer des documents'}
            {activeSection==='ecritures' && '≡ Écritures comptables'}
            {activeSection==='documents_fiscaux' && '📄 Documents Fiscaux'}
            {activeSection==='optimisation' && '📊 Optimisation Fiscale'}
            {activeSection==='tva' && '% Déclaration TVA'}
            {activeSection==='bilan' && '□ Bilan comptable'}
            {activeSection==='exports' && '↓ Exports'}
            {activeSection==='bank' && '🏦 Relevés bancaires'}
            {activeSection==='parametres' && '◈ Paramètres'}
            {activeSection==='abonnement' && '💳 Mon Abonnement'}
            {activeSection==='social' && '👥 Salariés & Paie'}
            {activeSection==='urssaf' && '🏛️ URSSAF & DSN'}
            {activeSection==='support' && '🎫 Support & Tickets d\'Assistance'}
            {activeSection==='cloture' && '⏳ Suivi Mensuel & Clôture de l\'Exercice'}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
            {updateInfo && updateInfo.update_available && (
              <div 
                onClick={() => setShowUpdateModal(true)}
                style={{
                  background: '#10B981',
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: '700',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  marginRight: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <span>🚀</span> Mise à jour disponible
              </div>
            )}
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginRight: '16px'}}>
              <span style={{fontSize: '12px', color: '#73726C', fontWeight: '500'}}>Exercice :</span>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: '1px solid #E8E7E3',
                  background: 'white',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#2D2D2D',
                  outline: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s'
                }}
              >
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
                <option value={2027}>2027</option>
              </select>
            </div>
            {/* Indicateur de refresh - visible uniquement pendant le chargement */}
            {refreshing && (
              <div style={{
                width:'36px',
                height:'36px',
                borderRadius:'8px',
                background:'#F5F4ED',
                border:'1px solid #E8E7E3',
                display:'flex',
                alignItems:'center',
                justifyContent:'center',
                fontSize:'16px',
                animation:'spin 1s linear infinite'
              }}>🔄</div>
            )}
            {/* Bouton Déconnexion - visible uniquement sur mobile (sidebar cachée) */}
            <button
              onClick={onLogout}
              title="Se déconnecter"
              className="btn-logout-mobile"
              style={{width:'36px',height:'36px',borderRadius:'8px',background:'transparent',border:'1px solid #FCEBEB',cursor:'pointer',fontSize:'16px',display: isMobile ? 'flex' : 'none',alignItems:'center',justifyContent:'center',color:'#D85A30',transition:'background 0.15s'}}
              onMouseEnter={e => e.currentTarget.style.background='#FAEEDA'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}
            >↩</button>
            {activeSection !== 'facturation' && (
              <button 
                onClick={() => setActiveSection('facturation')} 
                style={{
                  fontSize: '12px',
                  padding: '6px 14px',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #1D9E75, #10B981)',
                  color: 'white',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 5px rgba(29, 158, 117, 0.3)'
                }}
              >
                🧾 Facturation Factur-X
              </button>
            )}
            {activeSection !== 'documents' && (
              <button className="btn-export" onClick={() => setActiveSection('exports')} style={{fontSize:'12px',padding:'6px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',background:'transparent',color:'#2D2D2D',cursor:'pointer'}}>↓ Exporter CA3</button>
            )}
            {activeSection !== 'documents' && (
              <button className="btn-deposer" onClick={() => setActiveSection('documents')} style={{fontSize:'12px',padding:'6px 12px',border:'none',borderRadius:'8px',background:'#1D9E75',color:'white',cursor:'pointer', display: window.innerWidth < 768 ? 'none' : 'block'}}>+ Déposer un document</button>
            )}
          </div>
        </div>

        {isExerciseClosed && (
          <div style={{
            background: '#FEE2E2',
            borderBottom: '1px solid #FCA5A5',
            color: '#991B1B',
            padding: '10px 24px',
            fontSize: '13px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            zIndex: 10
          }}>
            <span>🔒 Cet exercice est clôturé et archivé. Le dépôt de relevés/factures et les modifications sont désactivés.</span>
          </div>
        )}

        {/* BOTTOM NAVIGATION MOBILE - PRINCIPALE */}
        <div className="bottom-nav">
          <button onClick={() => handleMobileNavClick('dashboard')} className={activeSection === 'dashboard' ? 'active' : ''}>
            <span>🏠</span>
            <span>Accueil</span>
          </button>
          <button onClick={() => handleMobileNavClick('documents')} className={activeSection === 'documents' ? 'active' : ''}>
            <span>⬆</span>
            <span>Déposer</span>
          </button>
          <button onClick={() => handleMobileNavClick('chat')} className={activeSection === 'chat' ? 'active' : ''}>
            <span>💬</span>
            <span>Assistant</span>
          </button>
          <button onClick={() => handleMobileNavClick('bank')} className={activeSection === 'bank' ? 'active' : ''}>
            <span>🏦</span>
            <span>Relevés</span>
          </button>
          {isMobile && (
            <button onClick={() => setIsMobileMenuOpen(true)}>
              <span>➕</span>
              <span>Menu</span>
            </button>
          )}
        </div>

        {/* MENU SECONDAIRE MOBILE (Slide-up Panel) */}
        {isMobile && (
          <>
            <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`} onClick={() => setIsMobileMenuOpen(false)}></div>
            <div className={`mobile-slide-panel ${isMobileMenuOpen ? 'open' : ''}`}>
              <div className="mobile-slide-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E8E7E3'}}>
                <h3 style={{margin: 0, fontSize: '16px', fontWeight: '600'}}>Menu</h3>
                <button onClick={() => setIsMobileMenuOpen(false)} style={{background: 'none', border: 'none', fontSize: '24px', color: '#73726C', cursor: 'pointer', padding: '0 8px', lineHeight: '1'}}>×</button>
              </div>
              <div className="mobile-slide-content" style={{padding: '12px', overflowY: 'auto', maxHeight: '60vh', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))'}}>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('facturation')}>
                  <span className="icon">🧾</span> <span className="label">Facturation Factur-X</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('echeances')}>
                  <span className="icon">📅</span> <span className="label">Échéances & Rappels</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('cadrage')}>
                  <span className="icon">🎯</span> <span className="label">Cadrage & Tiers</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('documents_fiscaux')}>
                  <span className="icon">📄</span> <span className="label">Documents fiscaux</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('optimisation')}>
                  <span className="icon">📊</span> <span className="label">Optimisation AI</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('tresorerie')}>
                  <span className="icon">↕</span> <span className="label">Trésorerie & Cash Flow</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('tva')}>
                  <span className="icon">🧾</span> <span className="label">Déclaration TVA</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('bilan')}>
                  <span className="icon">📈</span> <span className="label">Bilan comptable</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('inventaire')}>
                  <span className="icon">📦</span> <span className="label">Immobilisations</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('ecritures')}>
                  <span className="icon">📒</span> <span className="label">Écritures</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('exports')}>
                  <span className="icon">⬇️</span> <span className="label">Exports</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('social')}>
                  <span className="icon">👥</span> <span className="label">Salariés & Paie</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('urssaf')}>
                  <span className="icon">🏛️</span> <span className="label">URSSAF / DSN</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('cloture')}>
                  <span className="icon">⏳</span> <span className="label">Suivi & Clôture</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('parametres')}>
                  <span className="icon">⚙️</span> <span className="label">Paramètres</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('abonnement')}>
                  <span className="icon">💳</span> <span className="label">Mon Abonnement</span>
                </button>
                <button className="slide-panel-item" onClick={() => handleMobileNavClick('support')}>
                  <span className="icon">🎫</span> <span className="label">Support & Tickets</span>
                </button>
                <div style={{height: '1px', background: '#E8E7E3', margin: '8px 0'}}></div>
                <button className="slide-panel-item text-danger" onClick={onLogout} style={{color: '#D85A30'}}>
                  <span className="icon">🚪</span> <span className="label">Déconnexion</span>
                </button>
              </div>
            </div>
          </>
        )}

        {/* Content - rendu conditionnel par section */}
        {activeSection === 'chat' && (
          <div
            ref={touchAreaRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
              flex:1,
              display:'flex',
              flexDirection:'column',
              overflow:'hidden',
              background:'#F5F4ED',
              paddingBottom: '0',
              position:'relative'
            }}
          >
            {/* Pull-to-Refresh Indicator */}
            {pullDistance > 0 && (
              <div style={{
                position: 'absolute',
                top: `${Math.min(pullDistance, 60)}px`,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                transition: 'top 0.1s ease-out'
              }}>
                <div style={{
                  background: 'white',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '12px',
                  color: '#1D9E75'
                }}>
                  {refreshing ? (
                    <>
                      <span style={{animation: 'spin 1s linear infinite'}}>🔄</span>
                      <span>Chargement...</span>
                    </>
                  ) : pullDistance > 60 ? (
                    <>
                      <span>⬇️</span>
                      <span>Relâcher pour rafraîchir</span>
                    </>
                  ) : (
                    <>
                      <span>⬇️</span>
                      <span>Tirer pour rafraîchir</span>
                    </>
                  )}
                </div>
              </div>
            )}
            <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

            <div className="chat-messages" ref={chatMessagesRef} style={{flex:1,overflowY:'auto',padding:'24px',display:'flex',flexDirection:'column',gap:'12px'}}>
              {chatMessages.map((msg,idx) => (
                <div key={idx} style={{display:'flex',justifyContent:msg.role==='user'?'flex-end':'flex-start'}}>
                  {msg.role==='ai' && <div style={{width:'32px',height:'32px',borderRadius:'50%',background:'#1D9E75',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'14px',flexShrink:0,marginRight:'10px'}}>C</div>}
                  <div style={{maxWidth:'65%'}}>
                    <div style={{padding:'12px 16px',borderRadius:'16px',fontSize:'13px',lineHeight:'1.6',background:msg.role==='ai'?'white':'#1D9E75',color:msg.role==='ai'?'#2D2D2D':'white',boxShadow:'0 1px 3px rgba(0,0,0,0.08)',borderBottomLeftRadius:msg.role==='ai'?'4px':'16px',borderBottomRightRadius:msg.role==='user'?'4px':'16px'}} dangerouslySetInnerHTML={{__html: formatMessage(msg.text)}}></div>
                    <div style={{fontSize:'10px',color:'#73726C',marginTop:'4px',padding:'0 4px',textAlign:msg.role==='user'?'right':'left'}}>{msg.time}</div>
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
                  <div style={{width:'32px',height:'32px',borderRadius:'50%',background:'#1D9E75',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'14px'}}>C</div>
                  <div style={{background:'white',padding:'12px 16px',borderRadius:'16px',borderBottomLeftRadius:'4px',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
                    <span style={{display:'inline-flex',gap:'4px'}}>
                      <span style={{width:'6px',height:'6px',borderRadius:'50%',background:'#1D9E75',animation:'bounce 1s infinite'}}></span>
                      <span style={{width:'6px',height:'6px',borderRadius:'50%',background:'#1D9E75',animation:'bounce 1s 0.2s infinite'}}></span>
                      <span style={{width:'6px',height:'6px',borderRadius:'50%',background:'#1D9E75',animation:'bounce 1s 0.4s infinite'}}></span>
                    </span>
                  </div>
                </div>
              )}
              <style>{`@keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}`}</style>
            </div>
            <div
              className="chat-input-container"
              style={{
                background:'white',
                borderTop:'1px solid #E8E7E3',
                padding: window.innerWidth < 768 ? '8px 12px' : '16px 24px',
                paddingBottom: `calc(${window.innerWidth < 768 ? '8px' : '16px'} + env(safe-area-inset-bottom, 0px))`,
                display:'flex',
                gap:'12px',
                alignItems:'center',
                boxShadow: '0 -2px 10px rgba(0,0,0,0.05)'
              }}
            >
              <input
                style={{flex:1,padding: window.innerWidth < 768 ? '8px 12px' : '12px 16px',border:'1px solid #E8E7E3',borderRadius:'24px',fontSize:'13px',outline:'none',background:'#FAF9F5'}}
                type="text" placeholder="Posez votre question comptable…"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key==='Enter' && handleSendChat()}
              />
              <button onClick={handleSendChat} disabled={chatLoading}
                style={{width: window.innerWidth < 768 ? '36px' : '44px',height: window.innerWidth < 768 ? '36px' : '44px',borderRadius:'50%',background:chatLoading?'#73726C':'#1D9E75',border:'none',cursor:chatLoading?'not-allowed':'pointer',color:'white',fontSize:'18px',display:'flex',alignItems:'center',justifyContent:'center'}}>
                ↑
              </button>
            </div>
          </div>
        )}


        {activeSection !== 'chat' && (
          <div className="content-area" style={{flex:1,minHeight:0,overflowY:'auto',padding:'20px 24px'}}>
          {activeSection === 'dashboard' && (
            <>
              {/* Alert Bar - seulement si documents en attente */}
              {data?.documents_recents?.filter(d => d.statut === 'en_attente').length > 0 && (
                <div className="alert-bar" style={{background: '#FAEEDA', border: '1px solid #FAC775', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: '#633806'}}>
                  <div className="alert-dot" style={{width: '6px', height: '6px', borderRadius: '50%', background: '#EF9F27'}}></div>
                  <span><strong>{data.documents_recents.filter(d => d.statut === 'en_attente').length} documents</strong> en attente de validation. <strong>Cliquez sur un document ci-dessous pour le valider.</strong></span>
                </div>
              )}

              {/* Header de bienvenue Desktop & Mobile */}
              {data?.client && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '16px',
                  borderBottom: '1px solid #E8E7E3',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div>
                    <div style={{fontSize: '20px', fontWeight: '600', color: '#2D2D2D'}}>Bonjour, {data.client.nom} 👋</div>
                    <div style={{fontSize: '13px', color: '#73726C', marginTop: '4px'}}>
                      SIREN {data.client.siren} • {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Bouton Accès Rapide Factur-X */}
                    <button 
                      onClick={() => setActiveSection('facturation')}
                      style={{
                        background: 'linear-gradient(135deg, #1D9E75 0%, #10B981 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 2px 6px rgba(29, 158, 117, 0.25)',
                        transition: 'all 0.2s'
                      }}
                    >
                      🧾 Facturation Factur-X (2026)
                    </button>

                    {/* Bouton Synchro Mobile */}
                    <button 
                      onClick={() => setShowMobileSyncModal(true)}
                      style={{
                        background: '#FAF9F5',
                        color: '#0F6E56',
                        border: '1px solid #1D9E75',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#E1F5EE'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#FAF9F5'; }}
                    >
                      📲 Relier mon Smartphone
                    </button>
                  </div>
                </div>
              )}

              {/* Cartes Services & Modules */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>✨ Services & Outils de Gestion</div>
                  <button 
                    onClick={() => setActiveSection('abonnement')}
                    style={{
                      background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '4px 10px',
                      borderRadius: '16px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(217, 119, 6, 0.2)'
                    }}
                  >
                    👑 Voir les Offres
                  </button>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(5, 1fr)', gap: '14px' }}>
                  {[
                    { id: 'facturation', icon: '🧾', title: 'Factur-X 2026', desc: 'Émettez des factures normées Factur-X, télétransmettez au PPF et téléchargez le e-Reporting DGFiP.' },
                    { id: 'juridique', icon: '⚖', title: 'Juridique', desc: "Analysez vos documents juridiques en quelques clics. PV d'assemblées, statuts : décryptés par IA." },
                    { id: 'comptabilite_premium', icon: '🧮', title: 'Comptabilité', desc: 'Gagnez du temps : relances clients, contrôle qualité des écritures, analyse de FEC.' },
                    { id: 'fiscal_premium', icon: '📝', title: 'Fiscal', desc: 'Interrogez la doctrine fiscale (BOFiP) en langage naturel avec réponse immédiate.' },
                    { id: 'social_premium', icon: '👥', title: 'Sociale', desc: 'Accédez aux réponses du BOSS pour vos cotisations et déclarations paie.' }
                  ].map(item => (
                    <div 
                      key={item.id}
                      onClick={() => { if (checkModuleAccess(item.id)) setActiveSection(item.id); }}
                      style={{
                        background: 'white',
                        padding: '16px',
                        borderRadius: '12px',
                        border: '1px solid #E5E7EB',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = '#0F6E56';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = '#E5E7EB';
                        e.currentTarget.style.transform = 'none';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.02)';
                      }}
                    >
                      <div style={{
                        width: '38px',
                        height: '38px',
                        background: item.id === 'facturation' ? '#E1F5EE' : '#EEF2FF',
                        color: item.id === 'facturation' ? '#0F6E56' : '#4338CA',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        marginBottom: '12px'
                      }}>
                        {item.icon}
                      </div>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937', margin: '0 0 6px 0' }}>{item.title}</h4>
                      <p style={{ fontSize: '11.5px', color: '#6B7280', margin: 0, lineHeight: '1.4' }}>{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

          {/* Metrics */}
          {isEmpty ? (
            <div style={{background: 'white', border: '2px dashed #E8E7E3', borderRadius: '16px', padding: '48px', marginBottom: '20px', textAlign: 'center'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>📄</div>
              <h3 style={{fontSize: '20px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px'}}>Aucune donnée comptable</h3>
              <p style={{fontSize: '14px', color: '#73726C', marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px'}}>
                Déposez vos premiers documents (factures, tickets) pour commencer à suivre votre comptabilité.
              </p>
              <button
                onClick={() => setActiveSection('documents')}
                style={{padding: '12px 24px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'background 0.2s'}}
                onMouseEnter={(e) => e.target.style.background = '#0F6E56'}
                onMouseLeave={(e) => e.target.style.background = '#1D9E75'}
              >
                + Déposer un document
              </button>
            </div>
          ) : (
            <div className="metrics-grid" style={{display: 'grid', gap: '12px', marginBottom: '20px'}}>
              {data.metrics.map((metric, idx) => {
                const isJustif = metric.titre === 'Taux de Justification';
                const isCA = metric.titre === "Chiffre d'Affaires";
                const isDepenses = metric.titre === "Dépenses";
                
                let bg = '#FAF9F5';
                let border = 'none';
                let labelColor = '#73726C';
                let valueColor = '#2D2D2D';
                let labelPrefix = '';

                if (isJustif) {
                  bg = 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)';
                  border = '1px solid #BBF7D0';
                  labelColor = '#166534';
                  valueColor = '#15803D';
                  labelPrefix = '🎯 ';
                } else if (isCA) {
                  bg = 'linear-gradient(135deg, #F0FDFA 0%, #CCFBF1 100%)';
                  border = '1px solid #99F6E4';
                  labelColor = '#0F766E';
                  valueColor = '#0D9488';
                  labelPrefix = '💰 ';
                } else if (isDepenses) {
                  bg = 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)';
                  border = '1px solid #FECACA';
                  labelColor = '#991B1B';
                  valueColor = '#DC2626';
                  labelPrefix = '💸 ';
                }

                return (
                  <div key={idx} className="metric-card" style={{
                    background: bg,
                    border: border,
                    borderRadius: '8px', 
                    padding: '14px 16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                  }}>
                    <div className="metric-label" style={{fontSize: '11px', color: labelColor, marginBottom: '6px', fontWeight: border !== 'none' ? '600' : 'normal'}}>
                      {labelPrefix}{metric.titre}
                    </div>
                    <div className="metric-value" style={{fontSize: '22px', fontWeight: '600', color: valueColor, lineHeight: '1'}}>
                      {metric.valeur}
                    </div>
                    {metric.trend && (
                      <div className="metric-sub" style={{fontSize: '11px', color: '#73726C', marginTop: '4px'}}>
                        <span className={metric.trend.direction === 'up' ? 'metric-up' : 'metric-down'} style={{color: metric.trend.direction === 'up' ? '#1D9E75' : '#D85A30'}}>
                          {metric.trend.value}
                        </span>
                        <span> {metric.trend.label}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Grid3 */}
          <div className="grid3" style={{display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (wgtActive('tva') || wgtActive('chat') ? '2fr 1fr' : '1fr'), gap: '16px', marginBottom: '16px'}}>
            {/* Left Column */}
            <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
              {/* Bar Chart - Répartition des charges */}
              {wgtActive('charges') && (
              <div className="card" style={{background: 'white', border: '1px solid #E8E7E3', borderRadius: '12px', padding: '16px'}}>
                <div className="card-header" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px'}}>
                  <div className="card-title" style={{fontSize: '13px', fontWeight: '500', color: '#2D2D2D'}}>Répartition des charges</div>
                  <div className="card-action" onClick={() => setActiveSection('comptabilite')} style={{fontSize: '11px', color: '#1D9E75', cursor: 'pointer', userSelect: 'none'}}>Détail →</div>
                </div>
                {chargesLoading ? (
                  <div style={{textAlign: 'center', padding: '20px', color: '#73726C'}}>Chargement...</div>
                ) : chargesData && chargesData.repartition && chargesData.repartition.length > 0 ? (
                  <div className="bar-chart" style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                    {chargesData.repartition.map((item, idx) => (
                      <div key={idx} className="bar-row" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <div className="bar-label" style={{fontSize: '11px', color: '#73726C', width: '60px', textAlign: 'right', flexShrink: 0}}>{item.categorie}</div>
                        <div className="bar-track" style={{flex: 1, height: '6px', background: '#FAF9F5', borderRadius: '3px', overflow: 'hidden'}}>
                          <div className="bar-fill" style={{
                            height: '100%',
                            borderRadius: '3px',
                            background: idx === 0 ? '#1D9E75' : idx < 2 ? '#5DCAA5' : '#EF9F27',
                            width: `${Math.max(item.pourcentage, 5)}%`
                          }}></div>
                        </div>
                        <div className="bar-val" style={{fontSize: '11px', color: '#73726C', width: '52px', flexShrink: 0}}>
                          {item.montant.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} €
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{textAlign: 'center', padding: '20px', color: '#73726C', fontSize: '12px'}}>
                    Déposez vos premiers documents pour voir la répartition de vos charges.
                  </div>
                )}
              </div>
              )}

              {/* Documents récents */}
              {wgtActive('docs') && (
              <div className="card" style={{background: 'white', border: '1px solid #E8E7E3', borderRadius: '12px', padding: '16px'}}>
                <div className="card-header" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px'}}>
                  <div className="card-title" style={{fontSize: '13px', fontWeight: '500', color: '#2D2D2D'}}>Documents récents</div>
                  <div className="card-action" onClick={() => setActiveSection('documents')} style={{fontSize: '11px', color: '#1D9E75', cursor: 'pointer', userSelect: 'none'}}>Tout voir →</div>
                </div>

                {data.documents_recents.map((doc) => (
                  <div
                    key={doc.id}
                    className="doc-item"
                    onClick={() => {
                      console.log('📄 DEBUG - Click document depuis dashboard, statut:', doc.statut);
                      setActiveSection('documents');
                      setTimeout(() => {
                        loadDocumentDetails(doc.id);
                      }, 100);
                    }}
                    style={{display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #E8E7E3', cursor: 'pointer'}}
                  >
                    <div className={`doc-icon pdf`} style={{width: '32px', height: '32px', borderRadius: '8px', background: '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px'}}>
                      📄
                    </div>
                    <div className="doc-info" style={{flex: 1, minWidth: 0}}>
                      <div className="doc-name" style={{fontSize: '12px', fontWeight: '500', color: '#2D2D2D', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{doc.nom}</div>
                      <div className="doc-meta" style={{fontSize: '10px', color: '#73726C'}}>{doc.fournisseur} · {doc.date}</div>
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px'}}>
                      <div className={`doc-status ${doc.statut === 'traite' ? 'ok' : doc.statut === 'en_attente' ? 'wait' : 'err'}`} style={{fontSize: '10px', padding: '2px 7px', borderRadius: '10px', fontWeight: '500', background: doc.statut === 'traite' ? '#E1F5EE' : doc.statut === 'en_attente' ? '#FAEEDA' : '#FCEBEB', color: doc.statut === 'traite' ? '#0F6E56' : doc.statut === 'en_attente' ? '#633806' : '#A32D2D'}}>
                        {doc.statut === 'traite' ? '✓ Traité' : doc.statut === 'en_attente' ? 'À valider' : 'Révision'}
                      </div>
                      {doc.statut === 'traite' && (
                        <div style={{
                          fontSize: '8px', padding: '1px 5px', borderRadius: '6px', fontWeight: '600',
                          background: doc.rapproche ? '#E0F2FE' : '#FEF3C7',
                          color: doc.rapproche ? '#0369A1' : '#B45309',
                          border: doc.rapproche ? '1px solid #BAE6FD' : '1px solid #FDE68A',
                        }} title={doc.rapproche ? `Rapproché le ${doc.date_rapprochement} - ${doc.libelle_rapprochement}` : 'Aucune transaction bancaire correspondante'}>
                          {doc.rapproche ? '🔗 Rapproché' : '⏳ Non rapproché'}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              )}

              {/* Opérations à justifier / Relevé bancaire */}
              {data.non_rapprochees && data.non_rapprochees.length > 0 && (
              <div className="card" style={{background: 'white', border: '1px solid #E8E7E3', borderRadius: '12px', padding: '16px', marginTop: '16px'}}>
                <div className="card-header" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <div className="card-title" style={{fontSize: '13px', fontWeight: '500', color: '#2D2D2D'}}>Justificatifs manquants</div>
                    <span style={{fontSize: '10px', background: '#FEE2E2', color: '#991B1B', padding: '2px 6px', borderRadius: '10px', fontWeight: '600'}}>
                      {data.non_rapprochees.length} opération{data.non_rapprochees.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="card-action" onClick={() => setActiveSection('bank')} style={{fontSize: '11px', color: '#1D9E75', cursor: 'pointer'}}>Voir le relevé →</div>
                </div>

                <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  {data.non_rapprochees.map((tx) => {
                    const isCredit = tx.type === 'CREDIT';
                    const dateObj = new Date(tx.date);
                    const formattedDate = isNaN(dateObj.getTime()) ? tx.date : dateObj.toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit'});
                    
                    return (
                      <div key={tx.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#FAF9F5', borderRadius: '8px'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1}}>
                          <div style={{fontSize: '10px', color: '#73726C', fontWeight: '500'}}>{formattedDate}</div>
                          <div style={{fontSize: '11px', color: '#2D2D2D', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} title={tx.libelle}>
                            {tx.libelle}
                          </div>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginLeft: '12px'}}>
                          <div style={{fontSize: '11px', fontWeight: '600', color: isCredit ? '#0F6E56' : '#2D2D2D'}}>
                            {isCredit ? '+' : '−'} {Math.abs(tx.montant).toFixed(2).replace('.', ',')} €
                          </div>
                          <button
                            onClick={() => setActiveSection('documents')}
                            style={{
                              fontSize: '10px',
                              background: '#1D9E75',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              minHeight: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            📎 Ajouter
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}
            </div>

            {/* Right Column */}
            {(wgtActive('tva') || wgtActive('chat')) && (
            <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
              {/* TVA Block */}
              {wgtActive('tva') && (
              <div className="card" style={{background: 'white', border: '1px solid #E8E7E3', borderRadius: '12px', padding: '16px'}}>
                <div className="card-header" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px'}}>
                  <div className="card-title" style={{fontSize: '13px', fontWeight: '500', color: '#2D2D2D'}}>TVA — {data.periode.mois}</div>
                </div>

                {/* TVA Collectée */}
                {tvaDetailLoading ? (
                  <div style={{textAlign: 'center', padding: '20px', color: '#73726C'}}>Chargement...</div>
                ) : tvaDetailData ? (
                  <>
                    <div style={{marginBottom: '10px'}}>
                      {tvaDetailData.tva_collectee.detail.map((item, idx) => (
                        <div key={idx} className="tva-row" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #E8E7E3', fontSize: '12px'}}>
                          <span className="tva-label" style={{color: '#73726C'}}>Collectée {item.taux}%</span>
                          <span className="tva-val" style={{fontWeight: '500', color: '#2D2D2D'}}>{item.montant.toFixed(2).replace('.', ',')} €</span>
                        </div>
                      ))}
                      <div className="tva-row" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', fontSize: '12px'}}>
                        <span className="tva-label" style={{color: '#73726C'}}>Déductible achats</span>
                        <span className="tva-val" style={{fontWeight: '500', color: '#0F6E56'}}>− {tvaDetailData.tva_deductible.montant.toFixed(2).replace('.', ',')} €</span>
                      </div>
                    </div>

                    {/* Total */}
                    <div className="tva-total-row" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: tvaDetailData.solde.nature === 'a_payer' ? '#E1F5EE' : '#FAF9F5', borderRadius: '8px', marginTop: '8px'}}>
                      <span className="tva-total-label" style={{fontSize: '12px', fontWeight: '500', color: tvaDetailData.solde.nature === 'a_payer' ? '#085041' : '#2D2D2D'}}>
                        {tvaDetailData.solde.nature === 'a_payer' ? 'Net à payer' : 'Crédit de TVA'}
                      </span>
                      <span className="tva-total-val" style={{fontSize: '16px', fontWeight: '500', color: tvaDetailData.solde.nature === 'a_payer' ? '#085041' : '#2D2D2D'}}>
                        {tvaDetailData.solde.montant.toFixed(2).replace('.', ',')} €
                      </span>
                    </div>
                  </>
                ) : (
                  <div style={{textAlign: 'center', padding: '20px', color: '#73726C', fontSize: '12px'}}>
                    Déposez vos premiers documents pour voir votre détail TVA.
                  </div>
                )}
              </div>
              )}

              {/* Chat Panel */}
              {wgtActive('chat') && (
              <div className="card" style={{background: 'white', border: '1px solid #E8E7E3', borderRadius: '12px', padding: '0', overflow: 'hidden'}}>
                <div className="chat-panel" style={{border: 'none', borderRadius: '0', height: '260px', display: 'flex', flexDirection: 'column'}}>
                  <div className="chat-header" style={{padding: '12px 16px', borderBottom: '1px solid #E8E7E3', display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <div className="chat-dot" style={{width: '6px', height: '6px', borderRadius: '50%', background: '#1D9E75'}}></div>
                    <div className="chat-title" style={{fontSize: '13px', fontWeight: '500', color: '#2D2D2D'}}>Assistant comptable</div>
                  </div>
                  <div className="chat-messages" ref={dashboardChatMessagesRef} style={{flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '10px'}}>
                    {chatMessages.map((msg, idx) => (
                      <div key={idx} className={`msg ${msg.role}`} style={{maxWidth: '82%', alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start'}}>
                        <div className="msg-bubble" style={{padding: '8px 11px', borderRadius: '12px', fontSize: '12px', lineHeight: '1.5', background: msg.role === 'ai' ? '#FAF9F5' : '#1D9E75', color: msg.role === 'ai' ? '#2D2D2D' : 'white', borderBottomLeftRadius: msg.role === 'ai' ? '3px' : '12px', borderBottomRightRadius: msg.role === 'user' ? '3px' : '12px'}}>
                          {msg.text}
                        </div>
                        <div className="msg-time" style={{fontSize: '10px', color: '#73726C', marginTop: '3px', padding: '0 3px', textAlign: msg.role === 'user' ? 'right' : 'left'}}>{msg.time}</div>
                      </div>
                    ))}
                  </div>
                  <div className="chat-input-area" style={{padding: '10px 12px', borderTop: '1px solid #E8E7E3', display: 'flex', gap: '8px', alignItems: 'center'}}>
                    <input
                      className="chat-input"
                      style={{flex: 1, fontSize: '12px', padding: '7px 10px', border: '1px solid #E8E7E3', borderRadius: '8px', background: '#FAF9F5', color: '#2D2D2D', outline: 'none'}}
                      type="text"
                      placeholder="Posez une question comptable…"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSendChat(); }}
                    />
                    <button
                      className="chat-send"
                      style={{width: '30px', height: '30px', borderRadius: '8px', background: '#1D9E75', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '13px', flexShrink: 0}}
                      onClick={handleSendChat}
                    >
                      ↑
                    </button>
                  </div>
                </div>
              </div>
              )}
            </div>
            )}
          </div>

          {/* Upload Zone */}
          <div className="card" style={{background: 'white', border: '1px solid #E8E7E3', borderRadius: '12px', padding: '16px'}}>
            <div className="card-header" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px'}}>
              <div className="card-title" style={{fontSize: '13px', fontWeight: '500', color: '#2D2D2D'}}>Déposer un document</div>
              <div className="card-action" onClick={() => setActiveSection('documents')} style={{fontSize: '11px', color: '#1D9E75', cursor: 'pointer', userSelect: 'none'}}>Historique →</div>
            </div>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
              <div>
                {window.innerWidth < 768 ? (
                  // Mobile : 3 boutons distincts
                  <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px'}}>
                    <button
                      type="button"
                      onClick={() => {
                        console.log('DEBUG: Click camera button');
                        if (fileInputCameraRef.current) {
                          fileInputCameraRef.current.click();
                        } else {
                          console.error('DEBUG: fileInputCameraRef not found');
                        }
                      }}
                      style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '12px', color: '#2D2D2D'}}
                    >
                      <span style={{fontSize: '16px'}}>📷</span>
                      <span>Prendre une photo</span>
                    </button>
                    <input
                      id="fileInputCamera"
                      ref={fileInputCameraRef}
                      type="file"
                      style={{display: 'none'}}
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handleFileUpload(e)}
                    />

                    <button
                      type="button"
                      onClick={() => {
                        console.log('DEBUG: Click gallery button');
                        if (fileInputGalleryRef.current) {
                          fileInputGalleryRef.current.click();
                        } else {
                          console.error('DEBUG: fileInputGalleryRef not found');
                        }
                      }}
                      style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '12px', color: '#2D2D2D'}}
                    >
                      <span style={{fontSize: '16px'}}>📁</span>
                      <span>Depuis la galerie</span>
                    </button>
                    <input id="fileInputGallery" ref={fileInputGalleryRef} type="file" style={{display: 'none'}} accept="image/*" onChange={(e) => handleFileUpload(e)} />

                    <button
                      type="button"
                      onClick={() => {
                        console.log('DEBUG: Click PDF button');
                        if (fileInputPdfRef.current) {
                          fileInputPdfRef.current.click();
                        } else {
                          console.error('DEBUG: fileInputPdfRef not found');
                        }
                      }}
                      style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '12px', color: '#2D2D2D'}}
                    >
                      <span style={{fontSize: '16px'}}>📄</span>
                      <span>Fichier PDF</span>
                    </button>
                    <input id="fileInputPdf" ref={fileInputPdfRef} type="file" style={{display: 'none'}} accept=".pdf" onChange={(e) => handleFileUpload(e)} />
                  </div>
                ) : (
                  // Desktop : zone de dépôt classique
                  <>
                    <div
                      className="upload-zone"
                      style={{border: '1px dashed #E8E7E3', borderRadius: '12px', padding: '20px', textAlign: 'center', cursor: 'pointer', marginBottom: '12px', transition: 'background 0.15s'}}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = '#FAF9F5'; }}
                      onDragLeave={(e) => { e.currentTarget.style.background = ''; }}
                      onDrop={(e) => { e.preventDefault(); e.currentTarget.style.background = ''; handleFileUpload(e); }}
                      onClick={() => {
                        console.log('DEBUG: Click desktop upload zone');
                        if (fileInputDesktopRef.current) {
                          fileInputDesktopRef.current.click();
                        } else {
                          console.error('DEBUG: fileInputDesktopRef not found');
                        }
                      }}
                    >
                      <div className="upload-icon" style={{fontSize: '22px', marginBottom: '6px'}}>↑</div>
                      <div className="upload-text" style={{fontSize: '12px', color: '#2D2D2D'}}>Glissez vos documents ici</div>
                      <div className="upload-sub" style={{fontSize: '10px', color: '#73726C', marginTop: '3px'}}>PDF, JPG, PNG · Max 10 Mo par fichier</div>
                    </div>
                    <input
                      id="fileInputDesktop"
                      ref={fileInputDesktopRef}
                      type="file"
                      style={{display: 'none'}}
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileUpload(e)}
                    />
                  </>
                )}
                <div style={{fontSize: '11px', color: '#73726C'}}>
                  Factures fournisseurs · Tickets de caisse · Relevés bancaires · Factures clients
                </div>
              </div>
              <div>
                <div style={{fontSize: '12px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px'}}>Questions fréquentes</div>
                <div className="quick-btns" style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                  {data.questions_frequentes.map((question, idx) => (
                    <button
                      key={idx}
                      className="quick-btn"
                      style={{fontSize: '11px', padding: '7px 10px', border: '1px solid #E8E7E3', borderRadius: '8px', background: 'transparent', color: '#2D2D2D', cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s, color 0.1s'}}
                      onClick={() => handleQuickQuestion(question)}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#E1F5EE'; e.currentTarget.style.color = '#0F6E56'; e.currentTarget.style.borderColor = '#5DCAA5'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.color = ''; e.currentTarget.style.borderColor = ''; }}
                    >
                      {question} ↗
                    </button>
                  ))}
                </div>
              </div>
            </div>
              </div>
            </>
          )}

          {/* NOUVELLES SECTIONS */}
          {activeSection === 'creation_entreprise' && (
            <CopiloteCreationEntreprise clientId={clientId} />
          )}

          {activeSection === 'documents_fiscaux' && (
            <DocumentsFiscaux clientId={clientId} />
          )}

          {activeSection === 'optimisation' && (
            <Optimisation clientId={clientId} />
          )}

          {activeSection === 'facturation' && (
            <FacturationEinvoicing clientId={clientId} />
          )}

          {activeSection === 'echeances' && (
            <EcheancesCalendar clientId={clientId} />
          )}

          {activeSection === 'tresorerie' && (
            <CashFlow clientId={clientId} />
          )}

          {activeSection === 'juridique' && (
            <Juridique clientId={clientId} />
          )}

          {activeSection === 'comptabilite_premium' && (
            <ComptabilitePremium clientId={clientId} />
          )}

          {activeSection === 'fiscal_premium' && (
            <FiscalPremium clientId={clientId} />
          )}

          {activeSection === 'social_premium' && (
            <SocialPremium clientId={clientId} />
          )}

          {activeSection === 'bank' && (
            <BankStatements clientId={clientId} isClosed={isExerciseClosed} />
          )}

          {activeSection === 'bilan' && (
            <div style={{background:'white', padding:'24px', borderRadius:'12px', border:'1px solid #E8E7E3'}}>
              <h2 style={{fontSize:'20px', fontWeight:'600', marginBottom:'16px'}}>Bilan comptable</h2>

              {bilanLoading ? (
                <div style={{textAlign:'center', padding:'40px'}}>
                  <div style={{fontSize:'14px', color:'#73726C'}}>Chargement du bilan...</div>
                </div>
              ) : bilanData ? (
                <div>
                  {/* ACTIF */}
                  <div style={{marginBottom:'24px'}}>
                    <h3 style={{fontSize:'16px', fontWeight:'600', marginBottom:'12px', color:'#2D2D2D'}}>ACTIF</h3>
                    {bilanData.actif.details.length > 0 ? (
                      <div style={{background:'#FAF9F5', borderRadius:'8px', padding:'16px'}}>
                        {bilanData.actif.details.map((item, idx) => (
                          <div key={idx} style={{display:'flex', justifyContent:'space-between', marginBottom:'8px', fontSize:'14px'}}>
                            <span style={{color:'#73726C'}}>{item.libelle} ({item.compte})</span>
                            <span style={{fontWeight:'500', color:'#2D2D2D'}}>{item.montant.toFixed(2)} €</span>
                          </div>
                        ))}
                        <div style={{display:'flex', justifyContent:'space-between', marginTop:'12px', paddingTop:'12px', borderTop:'1px solid #E8E7E3', fontWeight:'600'}}>
                          <span>Total Actif</span>
                          <span style={{color:'#0F6E56'}}>{bilanData.actif.total.toFixed(2)} €</span>
                        </div>
                      </div>
                    ) : (
                      <p style={{fontSize:'13px', color:'#73726C'}}>Aucun actif comptabilisé</p>
                    )}
                  </div>

                  {/* PASSIF */}
                  <div style={{marginBottom:'24px'}}>
                    <h3 style={{fontSize:'16px', fontWeight:'600', marginBottom:'12px', color:'#2D2D2D'}}>PASSIF</h3>
                    {bilanData.passif.details.length > 0 ? (
                      <div style={{background:'#FAF9F5', borderRadius:'8px', padding:'16px'}}>
                        {bilanData.passif.details.map((item, idx) => (
                          <div key={idx} style={{display:'flex', justifyContent:'space-between', marginBottom:'8px', fontSize:'14px'}}>
                            <span style={{color:'#73726C'}}>{item.libelle} ({item.compte})</span>
                            <span style={{fontWeight:'500', color:'#2D2D2D'}}>{item.montant.toFixed(2)} €</span>
                          </div>
                        ))}
                        <div style={{display:'flex', justifyContent:'space-between', marginTop:'12px', paddingTop:'12px', borderTop:'1px solid #E8E7E3', fontWeight:'600'}}>
                          <span>Total Passif</span>
                          <span style={{color:'#A32D2D'}}>{bilanData.passif.total.toFixed(2)} €</span>
                        </div>
                      </div>
                    ) : (
                      <p style={{fontSize:'13px', color:'#73726C'}}>Aucun passif comptabilisé</p>
                    )}
                  </div>

                  {/* RÉSULTAT DE L'EXERCICE */}
                  <div>
                    <h3 style={{fontSize:'16px', fontWeight:'600', marginBottom:'12px', color:'#2D2D2D'}}>RÉSULTAT DE L'EXERCICE</h3>
                    <div style={{background:'#FAF9F5', borderRadius:'8px', padding:'16px'}}>
                      {bilanData.resultat.charges.details.length > 0 && (
                        <>
                          <div style={{fontSize:'13px', color:'#73726C', marginBottom:'8px'}}>Charges totales</div>
                          {bilanData.resultat.charges.details.map((item, idx) => (
                            <div key={idx} style={{display:'flex', justifyContent:'space-between', marginBottom:'6px', fontSize:'13px', marginLeft:'12px'}}>
                              <span style={{color:'#73726C'}}>{item.libelle} ({item.compte})</span>
                              <span style={{fontWeight:'500', color:'#A32D2D'}}>{item.montant.toFixed(2)} €</span>
                            </div>
                          ))}
                        </>
                      )}
                      <div style={{display:'flex', justifyContent:'space-between', marginTop:'12px', paddingTop:'12px', borderTop:'1px solid #E8E7E3', fontWeight:'600', fontSize:'15px'}}>
                        <span>Résultat net ({bilanData.resultat.est_benefice ? 'bénéfice' : 'perte'})</span>
                        <span style={{color: bilanData.resultat.est_benefice ? '#0F6E56' : '#A32D2D'}}>
                          {bilanData.resultat.net >= 0 ? '+' : ''}{bilanData.resultat.net.toFixed(2)} €
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ÉQUILIBRE */}
                  <div style={{marginTop:'16px', textAlign:'center', fontSize:'12px', color:'#73726C'}}>
                    {bilanData.equilibre ? '✅ Bilan équilibré' : '⚠️ Déséquilibre détecté'}
                  </div>
                </div>
              ) : (
                <div style={{textAlign:'center', padding:'40px'}}>
                  <div style={{fontSize:'48px', marginBottom:'16px'}}>📊</div>
                  <p style={{color:'#73726C', marginBottom:'20px'}}>Aucune donnée comptable disponible</p>
                  <p style={{fontSize:'13px', color:'#73726C'}}>Importez vos factures pour générer le bilan.</p>
                </div>
              )}
            </div>
          )}

          {/* ================================================================ */}
          {/* SECTION INVENTAIRE & IMMOBILISATIONS                             */}
          {/* ================================================================ */}
          {activeSection === 'inventaire' && (
            <InventaireSection clientId={clientId} token={localStorage.getItem('auth_token')} />
          )}

          {/* ================================================================ */}
          {/* SECTION SOCIAL & PAIE                                            */}
          {/* ================================================================ */}
          {activeSection === 'social' && (
            <SocialSection clientId={clientId} token={localStorage.getItem('auth_token')} />
          )}

          {activeSection === 'exports' && (
            <div style={{background:'white', padding:'24px', borderRadius:'12px', border:'1px solid #E8E7E3'}}>
              <h2 style={{fontSize:'20px', fontWeight:'600', marginBottom:'16px'}}>Exports</h2>
              <p style={{color:'#73726C', marginBottom:'24px'}}>Téléchargez vos documents comptables et déclarations fiscales.</p>

              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:'16px', marginBottom:'24px'}}>
                {/* Carte 1 : Export PDF */}
                <div style={{border:'1px solid #E8E7E3', borderRadius:'12px', padding:'20px', background:'#FAF9F5'}}>
                  <div style={{fontSize:'32px', marginBottom:'12px'}}>📄</div>
                  <h3 style={{fontSize:'16px', fontWeight:'600', marginBottom:'8px'}}>Liste des documents</h3>
                  <p style={{fontSize:'13px', color:'#73726C', marginBottom:'16px'}}>Export PDF de tous vos documents avec montants et statuts.</p>
                  <button
                    onClick={() => downloadExport(`/exports/${clientId}/documents/pdf`, `Documents_${new Date().toISOString().split('T')[0]}.pdf`)}
                    style={{width:'100%', padding:'10px 16px', border:'none', borderRadius:'8px', background:'#1D9E75', color:'white', fontSize:'14px', fontWeight:'500', cursor:'pointer'}}
                  >
                    Générer PDF
                  </button>
                </div>

                {/* Carte 2 : Export Excel */}
                <div style={{border:'1px solid #E8E7E3', borderRadius:'12px', padding:'20px', background:'#FAF9F5'}}>
                  <div style={{fontSize:'32px', marginBottom:'12px'}}>📊</div>
                  <h3 style={{fontSize:'16px', fontWeight:'600', marginBottom:'8px'}}>Données comptables</h3>
                  <p style={{fontSize:'13px', color:'#73726C', marginBottom:'16px'}}>Export Excel avec tous vos montants et détails fournisseurs.</p>
                  <button
                    onClick={() => downloadExport(`/exports/${clientId}/donnees/excel`, `Donnees_comptables_${new Date().toISOString().split('T')[0]}.xlsx`)}
                    style={{width:'100%', padding:'10px 16px', border:'none', borderRadius:'8px', background:'#1D9E75', color:'white', fontSize:'14px', fontWeight:'500', cursor:'pointer'}}
                  >
                    Télécharger Excel
                  </button>
                </div>

                {/* Carte 3 : Déclaration CA3 */}
                <div style={{border:'1px solid #E8E7E3', borderRadius:'12px', padding:'20px', background:'#FAF9F5'}}>
                  <div style={{fontSize:'32px', marginBottom:'12px'}}>📝</div>
                  <h3 style={{fontSize:'16px', fontWeight:'600', marginBottom:'8px'}}>Déclaration CA3</h3>
                  <p style={{fontSize:'13px', color:'#73726C', marginBottom:'16px'}}>Formulaire officiel de déclaration TVA avec lignes CA3.</p>
                  <button
                    onClick={() => downloadExport(`/exports/${clientId}/tva/ca3`, `CA3_TVA_${new Date().toISOString().split('T')[0]}.pdf`)}
                    style={{width:'100%', padding:'10px 16px', border:'none', borderRadius:'8px', background:'#1D9E75', color:'white', fontSize:'14px', fontWeight:'500', cursor:'pointer'}}
                  >
                    Imprimer CA3
                  </button>
                </div>
              </div>

              <div style={{background:'#E1F5EE', padding:'16px', borderRadius:'8px', border:'1px solid #5DCAA5'}}>
                <p style={{fontSize:'13px', color:'#0F6E56', margin:0}}>
                  <strong>💡 Conseil :</strong> Exportez vos données avant chaque échéance fiscale (le 15 du mois pour la TVA).
                </p>
              </div>
            </div>
          )}

          {activeSection === 'parametres' && (
            <div style={{display: 'flex', flexDirection: 'column', gap: '24px'}}>
              <div style={{background:'white', padding:'24px', borderRadius:'12px', border:'1px solid #E8E7E3'}}>
                <h2 style={{fontSize:'20px', fontWeight:'600', marginBottom:'16px'}}>Paramètres du compte</h2>
                <p style={{color:'#73726C'}}>Configuration de votre compte et de votre profil utilisateur.</p>
              </div>

              {/* CARTE : MODIFICATION DE MOT DE PASSE */}
              <div style={{background:'white', padding:'24px', borderRadius:'12px', border:'1px solid #E8E7E3'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px'}}>
                  <span style={{fontSize: '24px'}}>🔒</span>
                  <h3 style={{fontSize:'18px', fontWeight:'600', margin: 0}}>Modifier le mot de passe</h3>
                </div>
                
                <p style={{fontSize: '14px', color:'#73726C', lineHeight: '1.5', marginBottom: '20px'}}>
                  Mettez à jour le mot de passe de votre compte de manière sécurisée.
                </p>

                <form onSubmit={handlePasswordChange} style={{display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '400px'}}>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                    <label style={{fontSize: '12px', fontWeight: '600', color: '#73726C'}}>Mot de passe actuel :</label>
                    <div style={{position: 'relative'}}>
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        placeholder="Votre mot de passe actuel"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        style={{width: '100%', padding: '10px', paddingRight: '40px', borderRadius: '6px', border: '1px solid #E8E7E3', outline: 'none', fontSize: '13px'}}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                      >
                        {showCurrentPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                    <label style={{fontSize: '12px', fontWeight: '600', color: '#73726C'}}>Nouveau mot de passe :</label>
                    <div style={{position: 'relative'}}>
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder="Minimum 8 caractères"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        style={{width: '100%', padding: '10px', paddingRight: '40px', borderRadius: '6px', border: '1px solid #E8E7E3', outline: 'none', fontSize: '13px'}}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                      >
                        {showNewPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>

                  <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                    <label style={{fontSize: '12px', fontWeight: '600', color: '#73726C'}}>Confirmer le mot de passe :</label>
                    <div style={{position: 'relative'}}>
                      <input
                        type={showConfirmNewPassword ? 'text' : 'password'}
                        placeholder="Saisissez à nouveau"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        required
                        style={{width: '100%', padding: '10px', paddingRight: '40px', borderRadius: '6px', border: '1px solid #E8E7E3', outline: 'none', fontSize: '13px'}}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                        style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                      >
                        {showConfirmNewPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={pwdLoading}
                    style={{
                      padding: '10px 20px',
                      background: '#1D9E75',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      marginTop: '8px',
                      alignSelf: 'flex-start'
                    }}
                  >
                    {pwdLoading ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
                  </button>
                </form>
              </div>

              <div style={{background:'white', padding:'24px', borderRadius:'12px', border:'1px solid #E8E7E3'}}>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px'}}>
                  <span style={{fontSize: '24px'}}>📲</span>
                  <h3 style={{fontSize:'18px', fontWeight:'600', margin: 0}}>Synchronisation Mobile Hybride</h3>
                </div>
                
                <p style={{fontSize: '14px', color:'#73726C', lineHeight: '1.5', marginBottom: '20px'}}>
                  Activez la synchronisation pour envoyer instantanément des photos de factures depuis votre mobile vers votre espace comptable local.
                </p>

                <form onSubmit={handleSaveSyncConfig} style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <input
                      type="checkbox"
                      id="sync_enabled"
                      checked={syncConfig.enabled}
                      onChange={(e) => setSyncConfig(prev => ({...prev, enabled: e.target.checked}))}
                      style={{width: '18px', height: '18px', cursor: 'pointer'}}
                    />
                    <label htmlFor="sync_enabled" style={{fontSize: '14px', fontWeight: '600', color: '#2D2D2D', cursor: 'pointer'}}>
                      Activer le connecteur de synchronisation mobile
                    </label>
                  </div>

                  {syncConfig.enabled && (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '14px', padding: '16px', background: '#FAF9F5', borderRadius: '8px', border: '1px solid #E8E7E3', marginTop: '4px'}}>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                        <label style={{fontSize: '12px', fontWeight: '600', color: '#73726C'}}>Adresse de la passerelle Cloud (URL) :</label>
                        <input
                          type="text"
                          placeholder="Ex: https://sync.comptaai.fr"
                          value={syncConfig.cloudUrl}
                          onChange={(e) => setSyncConfig(prev => ({...prev, cloudUrl: e.target.value}))}
                          style={{padding: '10px', borderRadius: '6px', border: '1px solid #E8E7E3', outline: 'none', fontSize: '13px'}}
                        />
                      </div>

                      <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                        <label style={{fontSize: '12px', fontWeight: '600', color: '#73726C'}}>Clé secrète de synchronisation :</label>
                        <input
                          type="password"
                          placeholder="Clé d'authentification API secrète"
                          value={syncConfig.apiKey}
                          onChange={(e) => setSyncConfig(prev => ({...prev, apiKey: e.target.value}))}
                          style={{padding: '10px', borderRadius: '6px', border: '1px solid #E8E7E3', outline: 'none', fontSize: '13px'}}
                        />
                      </div>
                    </div>
                  )}

                  <div style={{display: 'flex', gap: '12px', marginTop: '8px'}}>
                    <button
                      type="submit"
                      disabled={syncSaving}
                      style={{
                        padding: '10px 20px',
                        background: '#1D9E75',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        fontSize: '13px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                    >
                      {syncSaving ? 'Sauvegarde...' : 'Sauvegarder les paramètres'}
                    </button>
                    
                    <button
                      type="button"
                      onClick={handleTriggerSync}
                      disabled={syncRunning}
                      style={{
                        padding: '10px 20px',
                        background: 'transparent',
                        color: '#0F6E56',
                        border: '1px solid #1D9E75',
                        borderRadius: '8px',
                        fontWeight: '600',
                        fontSize: '13px',
                        cursor: 'pointer'
                      }}
                    >
                      {syncRunning ? 'Synchro en cours...' : '🔄 Synchroniser maintenant'}
                    </button>
                  </div>
                </form>

                {syncConfig.lastSyncAt && (
                  <div style={{fontSize: '12px', color: '#73726C', marginTop: '16px'}}>
                    Dernière synchronisation réussie : <strong>{new Date(syncConfig.lastSyncAt).toLocaleString('fr-FR')}</strong>
                  </div>
                )}

                <div style={{marginTop: '24px', padding: '16px', background: '#E1F5EE', borderRadius: '8px', border: '1px solid #5DCAA5', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap'}}>
                  <div style={{flex: '1 1 250px'}}>
                    <h4 style={{margin: '0 0 6px', fontSize: '13px', fontWeight: '700', color: '#0F6E56'}}>💡 Comment l'utiliser sur votre mobile ?</h4>
                    <p style={{margin: 0, fontSize: '12px', color: '#0F6E56', lineHeight: '1.5'}}>
                      1. Scannez simplement le code QR ci-contre avec votre appareil photo mobile.<br />
                      2. Ou accédez depuis le navigateur de votre mobile à :<br />
                      <code style={{background: 'rgba(255,255,255,0.7)', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', marginTop: '4px', fontWeight: 'bold', wordBreak: 'break-all'}}>
                        {`${window.location.origin}/mobile`}
                      </code><br />
                      3. Renseignez l'identifiant client <strong>{clientId}</strong> sur votre téléphone pour lier votre appareil.<br />
                      4. Photographiez vos justificatifs : ils apparaîtront instantanément dans votre espace !
                    </p>
                  </div>
                  <div style={{background: 'white', padding: '8px', borderRadius: '8px', border: '1px solid #C2EAD9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto'}}>
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`${window.location.origin}/mobile`)}`} 
                      alt="QR Code Mobile Sync" 
                      style={{width: '120px', height: '120px', display: 'block'}} 
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ================================================================ */}
          {/* SECTION SUIVI MENSUEL & CLÔTURE DE L'EXERCICE                  */}
          {/* ================================================================ */}
          {activeSection === 'cloture' && (
            <div style={{display: 'flex', flexDirection: 'column', gap: '24px'}}>

              {/* En-tête de la section */}
              <div style={{background: 'linear-gradient(135deg, #1e3a5f 0%, #0F6E56 100%)', borderRadius: '16px', padding: '28px 32px', color: 'white', position: 'relative', overflow: 'hidden'}}>
                <div style={{position: 'absolute', top: '-30px', right: '-30px', width: '150px', height: '150px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)'}}></div>
                <div style={{position: 'absolute', bottom: '-20px', right: '80px', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)'}}></div>
                <h2 style={{margin: '0 0 8px', fontSize: '22px', fontWeight: '700', letterSpacing: '-0.3px'}}>⏳ Suivi Mensuel &amp; Clôture Comptable</h2>
                <p style={{margin: 0, fontSize: '14px', opacity: 0.85, maxWidth: '520px', lineHeight: '1.5'}}>Suivez l'avancement de l'exercice {selectedYear} mois par mois. Clôturez chaque mois après validation de tous les justificatifs, puis procédez à la clôture définitive de l'exercice pour générer le bilan final.</p>
                <div style={{display: 'flex', gap: '24px', marginTop: '20px'}}>
                  {clotureData && (() => {
                    const moisClotures = clotureData.filter(m => m.cloture.statut === 'cloture').length;
                    const totalDocs = clotureData.reduce((s, m) => s + (m.documents.total || 0), 0);
                    const totalTraites = clotureData.reduce((s, m) => s + (m.documents.traites || 0), 0);
                    const tvaNet = clotureData.reduce((s, m) => s + (m.tva.solde || 0), 0);
                    return (
                      <>
                        <div style={{background: 'rgba(255,255,255,0.15)', borderRadius: '10px', padding: '12px 16px', minWidth: '90px', textAlign: 'center'}}>
                          <div style={{fontSize: '22px', fontWeight: '700'}}>{moisClotures}/12</div>
                          <div style={{fontSize: '11px', opacity: 0.85, marginTop: '2px'}}>Mois clôturés</div>
                        </div>
                        <div style={{background: 'rgba(255,255,255,0.15)', borderRadius: '10px', padding: '12px 16px', minWidth: '90px', textAlign: 'center'}}>
                          <div style={{fontSize: '22px', fontWeight: '700'}}>{totalTraites}/{totalDocs}</div>
                          <div style={{fontSize: '11px', opacity: 0.85, marginTop: '2px'}}>Docs traités</div>
                        </div>
                        <div style={{background: 'rgba(255,255,255,0.15)', borderRadius: '10px', padding: '12px 16px', minWidth: '90px', textAlign: 'center'}}>
                          <div style={{fontSize: '22px', fontWeight: '700'}}>{tvaNet >= 0 ? '+' : ''}{tvaNet.toFixed(0)}€</div>
                          <div style={{fontSize: '11px', opacity: 0.85, marginTop: '2px'}}>TVA nette</div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Chargement */}
              {clotureLoading ? (
                <div style={{textAlign: 'center', padding: '60px', background: 'white', borderRadius: '12px', border: '1px solid #E8E7E3'}}>
                  <div style={{fontSize: '36px', marginBottom: '12px', animation: 'spin 1.5s linear infinite'}}>⏳</div>
                  <p style={{color: '#73726C', fontSize: '14px'}}>Chargement du suivi mensuel...</p>
                </div>
              ) : !clotureData ? (
                <div style={{textAlign: 'center', padding: '60px', background: 'white', borderRadius: '12px', border: '1px solid #E8E7E3'}}>
                  <div style={{fontSize: '48px', marginBottom: '16px'}}>📂</div>
                  <p style={{color: '#73726C'}}>Aucune donnée disponible</p>
                  <button onClick={loadClotureData} style={{marginTop: '12px', padding: '10px 20px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px'}}>Recharger</button>
                </div>
              ) : (
                <>
                  {/* FRISE CHRONOLOGIQUE — 12 mois */}
                  <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                    {clotureData.map((mois) => {
                      const isCloture = mois.cloture.statut === 'cloture';
                      const hasReleve = mois.releve.importe;
                      const docsOK = mois.documents.a_traiter === 0;
                      const canClose = !isCloture && hasReleve && docsOK;
                      const periodeStr = `${selectedYear}-${String(mois.mois).padStart(2, '0')}-01`;
                      const moisPasse = mois.mois <= new Date().getMonth() + 1;
                      const tvaNet = mois.tva.solde;
                      return (
                        <div key={mois.mois} style={{
                          background: 'white',
                          borderRadius: '12px',
                          border: `1px solid ${isCloture ? '#C7E8D9' : '#E8E7E3'}`,
                          overflow: 'hidden',
                          transition: 'box-shadow 0.2s',
                          boxShadow: isCloture ? '0 1px 4px rgba(15,110,86,0.08)' : 'none'
                        }}>

                          {/* En-tête du mois */}
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '14px 20px',
                            background: isCloture ? 'linear-gradient(90deg, #E1F5EE, #F0FAF5)' : moisPasse ? '#FAF9F5' : 'white',
                            borderBottom: '1px solid #E8E7E3'
                          }}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                              <div style={{
                                width: '36px', height: '36px', borderRadius: '50%',
                                background: isCloture ? '#1D9E75' : moisPasse ? '#EF9F27' : '#E8E7E3',
                                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '13px', fontWeight: '700'
                              }}>{String(mois.mois).padStart(2, '0')}</div>
                              <div>
                                <div style={{fontSize: '15px', fontWeight: '600', color: '#2D2D2D'}}>{mois.nom} {selectedYear}</div>
                                {mois.cloture.date_cloture && (
                                  <div style={{fontSize: '11px', color: '#73726C', marginTop: '1px'}}>
                                    Clôturé le {new Date(mois.cloture.date_cloture).toLocaleDateString('fr-FR')}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                              <span style={{
                                padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
                                background: isCloture ? '#E1F5EE' : moisPasse ? '#FEF3E2' : '#F5F4ED',
                                color: isCloture ? '#0F6E56' : moisPasse ? '#B45309' : '#73726C',
                                border: `1px solid ${isCloture ? '#5DCAA5' : moisPasse ? '#FAC775' : '#E8E7E3'}`
                              }}>
                                {isCloture ? '🔒 Clôturé' : moisPasse ? '🔓 En cours' : '📅 À venir'}
                              </span>
                              {!isCloture && canClose && (
                                <button
                                  onClick={() => handleCloturerMois(periodeStr, mois.nom)}
                                  style={{
                                    padding: '6px 14px', background: '#1D9E75', color: 'white',
                                    border: 'none', borderRadius: '8px', fontSize: '12px',
                                    fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#0F6E56'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = '#1D9E75'}
                                >
                                  🔒 Clôturer
                                </button>
                              )}
                              {!isCloture && !canClose && moisPasse && (
                                <span style={{fontSize: '11px', color: '#A32D2D', background: '#FAEEDA', padding: '4px 8px', borderRadius: '6px', border: '1px solid #FAC775'}}>
                                  {!hasReleve ? '📥 Relevé manquant' : `⚠️ ${mois.documents.a_traiter} doc(s) en attente`}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Détails du mois — grille 3 colonnes */}
                          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0', borderBottom: isCloture ? 'none' : 'none'}}>

                            {/* COLONNE 1 : Relevé Bancaire */}
                            <div style={{padding: '14px 16px', borderRight: '1px solid #F0EFE9'}}>
                              <div style={{fontSize: '10px', fontWeight: '600', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px'}}>🏦 Relevé Bancaire</div>
                              {hasReleve ? (
                                <>
                                  <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px'}}>
                                    <span style={{width: '8px', height: '8px', borderRadius: '50%', background: '#1D9E75', flexShrink: 0}}></span>
                                    <span style={{fontSize: '12px', fontWeight: '500', color: '#0F6E56'}}>Importé</span>
                                  </div>
                                  <div style={{fontSize: '11px', color: '#73726C', wordBreak: 'break-word'}}>{mois.releve.nom_fichier}</div>
                                  <div style={{fontSize: '11px', color: '#73726C', marginTop: '2px'}}>{mois.releve.nb_transactions} transactions · Solde {mois.releve.solde_final?.toFixed(2)} €</div>
                                </>
                              ) : (
                                <>
                                  <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px'}}>
                                    <span style={{width: '8px', height: '8px', borderRadius: '50%', background: '#A32D2D', flexShrink: 0}}></span>
                                    <span style={{fontSize: '12px', fontWeight: '500', color: '#A32D2D'}}>Manquant</span>
                                  </div>
                                  {!isCloture && (
                                    <button
                                      onClick={() => setActiveSection('bank')}
                                      style={{fontSize: '11px', padding: '4px 10px', background: 'transparent', border: '1px solid #E8E7E3', borderRadius: '6px', cursor: 'pointer', color: '#2D2D2D'}}
                                    >📥 Importer un relevé</button>
                                  )}
                                </>
                              )}
                            </div>

                            {/* COLONNE 2 : Documents */}
                            <div style={{padding: '14px 16px', borderRight: '1px solid #F0EFE9'}}>
                              <div style={{fontSize: '10px', fontWeight: '600', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px'}}>📄 Documents</div>
                              {mois.documents.total === 0 ? (
                                <div style={{fontSize: '12px', color: '#73726C'}}>Aucun document</div>
                              ) : (
                                <>
                                  {/* Barre de progression */}
                                  <div style={{marginBottom: '8px'}}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                                      <span style={{fontSize: '12px', fontWeight: '500', color: '#2D2D2D'}}>{mois.documents.traites} / {mois.documents.total} validés</span>
                                      <span style={{fontSize: '11px', color: '#73726C'}}>{mois.documents.total > 0 ? Math.round(mois.documents.traites / mois.documents.total * 100) : 0}%</span>
                                    </div>
                                    <div style={{height: '6px', borderRadius: '3px', background: '#E8E7E3', overflow: 'hidden'}}>
                                      <div style={{
                                        height: '100%', borderRadius: '3px',
                                        background: docsOK ? '#1D9E75' : '#EF9F27',
                                        width: `${mois.documents.total > 0 ? Math.round(mois.documents.traites / mois.documents.total * 100) : 0}%`,
                                        transition: 'width 0.5s ease'
                                      }}></div>
                                    </div>
                                  </div>
                                  {mois.documents.a_traiter > 0 && (
                                    <button
                                      onClick={() => setActiveSection('documents')}
                                      style={{fontSize: '11px', padding: '4px 10px', background: '#FEF3E2', border: '1px solid #FAC775', borderRadius: '6px', cursor: 'pointer', color: '#B45309'}}
                                    >⚠️ {mois.documents.a_traiter} à valider</button>
                                  )}
                                  <div style={{fontSize: '11px', color: '#73726C', marginTop: '4px'}}>Total : {mois.documents.total_ht?.toFixed(2)} € HT</div>
                                </>
                              )}
                            </div>

                            {/* COLONNE 3 : TVA */}
                            <div style={{padding: '14px 16px'}}>
                              <div style={{fontSize: '10px', fontWeight: '600', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px'}}>% TVA</div>
                              {(mois.tva.deductible === 0 && mois.tva.collectee === 0) ? (
                                <div style={{fontSize: '12px', color: '#73726C'}}>Aucune TVA</div>
                              ) : (
                                <>
                                  <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '3px'}}>
                                    <span style={{color: '#73726C'}}>Déductible</span>
                                    <span style={{fontWeight: '500', color: '#2D2D2D'}}>{mois.tva.deductible?.toFixed(2)} €</span>
                                  </div>
                                  <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px'}}>
                                    <span style={{color: '#73726C'}}>Collectée</span>
                                    <span style={{fontWeight: '500', color: '#2D2D2D'}}>{mois.tva.collectee?.toFixed(2)} €</span>
                                  </div>
                                  <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', paddingTop: '6px', borderTop: '1px solid #E8E7E3'}}>
                                    <span style={{fontWeight: '600', color: '#2D2D2D'}}>{tvaNet >= 0 ? 'À payer' : 'Crédit TVA'}</span>
                                    <span style={{fontWeight: '700', color: tvaNet >= 0 ? '#A32D2D' : '#0F6E56'}}>{tvaNet >= 0 ? '+' : ''}{tvaNet?.toFixed(2)} €</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* SECTION BILAN ANNUEL & CLÔTURE EXERCICE */}
                  <div style={{background: 'white', borderRadius: '16px', border: '2px solid #E8E7E3', overflow: 'hidden'}}>
                    <div style={{
                      padding: '20px 24px',
                      background: clotureData.every(m => m.cloture.statut === 'cloture')
                        ? 'linear-gradient(90deg, #1e3a5f, #0F6E56)'
                        : '#FAF9F5',
                      borderBottom: '1px solid #E8E7E3',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <div>
                        <h3 style={{margin: '0 0 4px', fontSize: '17px', fontWeight: '700', color: clotureData.every(m => m.cloture.statut === 'cloture') ? 'white' : '#2D2D2D'}}>🏆 Clôture Annuelle de l'Exercice {selectedYear}</h3>
                        <p style={{margin: 0, fontSize: '13px', color: clotureData.every(m => m.cloture.statut === 'cloture') ? 'rgba(255,255,255,0.8)' : '#73726C'}}>
                          {clotureData.every(m => m.cloture.statut === 'cloture')
                            ? '✅ Tous les mois sont clôturés — vous pouvez archiver l\'exercice'
                            : `${clotureData.filter(m => m.cloture.statut === 'cloture').length} / 12 mois clôturés — clôturez tous les mois pour archiver`}
                        </p>
                      </div>
                      {clotureData.every(m => m.cloture.statut === 'cloture') && (
                        <button
                          onClick={handleCloturerExercice}
                          style={{
                            padding: '10px 20px', background: 'white', color: '#0F6E56',
                            border: 'none', borderRadius: '10px', fontSize: '14px',
                            fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            transition: 'transform 0.2s, box-shadow 0.2s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'; }}
                        >🔒 Clôturer l'Exercice</button>
                      )}
                    </div>

                    {/* Récapitulatif annuel */}
                    <div style={{padding: '20px 24px'}}>
                      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px'}}>
                        {(() => {
                          const totalHT = clotureData.reduce((s, m) => s + (m.documents.total_ht || 0), 0);
                          const totalTVAded = clotureData.reduce((s, m) => s + (m.tva.deductible || 0), 0);
                          const totalTVAcoll = clotureData.reduce((s, m) => s + (m.tva.collectee || 0), 0);
                          const totalTVAnet = totalTVAcoll - totalTVAded;
                          const totalDocs = clotureData.reduce((s, m) => s + (m.documents.total || 0), 0);
                          const totalTraites = clotureData.reduce((s, m) => s + (m.documents.traites || 0), 0);
                          return [
                            {label: 'Total HT Achats', value: `${totalHT.toFixed(2)} €`, icon: '💰', color: '#0F6E56'},
                            {label: 'TVA Déductible', value: `${totalTVAded.toFixed(2)} €`, icon: '📉', color: '#0891B2'},
                            {label: 'TVA Collectée', value: `${totalTVAcoll.toFixed(2)} €`, icon: '📈', color: '#7C3AED'},
                            {label: 'TVA Net', value: `${totalTVAnet >= 0 ? '+' : ''}${totalTVAnet.toFixed(2)} €`, icon: '%', color: totalTVAnet >= 0 ? '#A32D2D' : '#0F6E56'},
                            {label: 'Pièces traitées', value: `${totalTraites} / ${totalDocs}`, icon: '📄', color: '#EF9F27'}
                          ].map((kpi, i) => (
                            <div key={i} style={{background: '#FAF9F5', borderRadius: '10px', padding: '14px 16px', border: '1px solid #E8E7E3'}}>
                              <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px'}}>
                                <span style={{fontSize: '16px'}}>{kpi.icon}</span>
                                <span style={{fontSize: '11px', color: '#73726C', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.04em'}}>{kpi.label}</span>
                              </div>
                              <div style={{fontSize: '18px', fontWeight: '700', color: kpi.color}}>{kpi.value}</div>
                            </div>
                          ));
                        })()}
                      </div>

                      {/* Bilan simplifié */}
                      {bilanData && (
                        <div style={{borderTop: '1px solid #E8E7E3', paddingTop: '20px'}}>
                          <h4 style={{margin: '0 0 12px', fontSize: '14px', fontWeight: '600', color: '#2D2D2D'}}>📊 Bilan Comptable (récapitulatif)</h4>
                          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                            <div style={{background: '#E1F5EE', borderRadius: '8px', padding: '14px', border: '1px solid #5DCAA5'}}>
                              <div style={{fontSize: '11px', fontWeight: '600', color: '#0F6E56', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em'}}>ACTIF</div>
                              <div style={{fontSize: '20px', fontWeight: '700', color: '#0F6E56'}}>{bilanData.actif?.total?.toFixed(2) ?? '0.00'} €</div>
                            </div>
                            <div style={{background: '#FAEEDA', borderRadius: '8px', padding: '14px', border: '1px solid #FAC775'}}>
                              <div style={{fontSize: '11px', fontWeight: '600', color: '#B45309', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em'}}>PASSIF</div>
                              <div style={{fontSize: '20px', fontWeight: '700', color: '#B45309'}}>{bilanData.passif?.total?.toFixed(2) ?? '0.00'} €</div>
                            </div>
                          </div>
                          {bilanData.resultat && (
                            <div style={{marginTop: '10px', padding: '14px', borderRadius: '8px', background: bilanData.resultat.est_benefice ? '#E1F5EE' : '#FDECEA', border: `1px solid ${bilanData.resultat.est_benefice ? '#5DCAA5' : '#F5C6C6'}`}}>
                              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <span style={{fontSize: '14px', fontWeight: '600', color: '#2D2D2D'}}>Résultat Net ({bilanData.resultat.est_benefice ? 'bénéfice' : 'perte'})</span>
                                <span style={{fontSize: '20px', fontWeight: '700', color: bilanData.resultat.est_benefice ? '#0F6E56' : '#A32D2D'}}>{bilanData.resultat.net >= 0 ? '+' : ''}{bilanData.resultat.net?.toFixed(2)} €</span>
                              </div>
                            </div>
                          )}
                          <div style={{marginTop: '16px', textAlign: 'center'}}>
                            <button
                              onClick={() => { const yr = new Date().getFullYear(); const url = `/api/exports/${clientId}/archive-exercice?annee=${yr}`; const a = document.createElement('a'); a.href = url; a.download = `Archive_Exercice_${yr}.zip`; const token = localStorage.getItem('auth_token'); fetch(url, {headers: {'Authorization': `Bearer ${token}`}}).then(r => r.blob()).then(b => { const bu = URL.createObjectURL(b); a.href = bu; a.click(); URL.revokeObjectURL(bu); }).catch(() => addToast('Erreur lors du téléchargement de l\'archive', 'error')); }}
                              style={{
                                padding: '12px 28px',
                                background: 'linear-gradient(135deg, #1e3a5f, #0F6E56)',
                                color: 'white', border: 'none', borderRadius: '10px',
                                fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                                boxShadow: '0 4px 16px rgba(15,110,86,0.25)',
                                transition: 'transform 0.2s, box-shadow 0.2s'
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                            >📦 Télécharger l'Archive Comptable (.zip)</button>
                            <p style={{fontSize: '11px', color: '#73726C', marginTop: '8px'}}>Contient : Bilan PDF · Grand Livre Excel · Tous les justificatifs</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeSection === 'abonnement' && (
            <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
              <SectionAbonnement clientNom={clientNom} />
            </div>
          )}

          {activeSection === 'social' && (
            <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
              <ModulePaie clientNom={clientNom} />
            </div>
          )}

          {activeSection === 'urssaf' && (
            <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
              <URSSAFDSN />
            </div>
          )}

          {activeSection === 'support' && (
            <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
              
              {/* Header and Toggle Button */}
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', padding: '20px 24px', borderRadius: '12px', border: '1px solid #E8E7E3'}}>
                <div>
                  <h2 style={{fontSize: '18px', fontWeight: '600', color: '#2D2D2D', margin: 0}}>🎫 Espace Support Client</h2>
                  <p style={{fontSize: '13px', color: '#73726C', margin: '4px 0 0'}}>Échangez avec nos experts et posez vos questions techniques ou comptables.</p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateTicket(!showCreateTicket);
                    setSelectedTicket(null);
                    setTicketSuccessMsg(null);
                  }}
                  style={{
                    padding: '10px 16px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#0F6E56'}
                  onMouseLeave={(e) => e.target.style.background = '#1D9E75'}
                >
                  {showCreateTicket ? '← Voir mes tickets' : '➕ Nouveau ticket'}
                </button>
              </div>

              {/* Success Notification */}
              {ticketSuccessMsg && (
                <div style={{background: '#E1F5EE', border: '1px solid #5DCAA5', borderRadius: '12px', padding: '16px 20px', fontSize: '14px', color: '#0F6E56'}}>
                  {ticketSuccessMsg}
                </div>
              )}

              {/* VIEW A: CREATE TICKET FORM */}
              {showCreateTicket && (
                <div style={{background: 'white', padding: '32px', borderRadius: '12px', border: '1px solid #E8E7E3', maxWidth: '640px', margin: '0 auto', width: '100%'}}>
                  <h3 style={{fontSize: '16px', fontWeight: '600', marginBottom: '20px', color: '#2D2D2D'}}>Créer une nouvelle demande</h3>
                  <form onSubmit={handleCreateTicketSubmit} style={{display: 'flex', flexDirection: 'column', gap: '18px'}}>
                    
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
                      <div>
                        <label style={{display: 'block', fontSize: '12px', fontWeight: '500', color: '#4B5563', marginBottom: '6px'}}>Catégorie</label>
                        <select
                          value={newTicketForm.categorie}
                          onChange={(e) => setNewTicketForm({...newTicketForm, categorie: e.target.value})}
                          style={{width: '100%', padding: '10px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '13px', background: 'white', outline: 'none'}}
                        >
                          <option value="technique">🛠️ Support Technique</option>
                          <option value="comptable">📈 Conseil Comptable / Fiscal</option>
                          <option value="facturation">💳 Facturation</option>
                          <option value="autre">🔍 Autre demande</option>
                        </select>
                      </div>

                      <div>
                        <label style={{display: 'block', fontSize: '12px', fontWeight: '500', color: '#4B5563', marginBottom: '6px'}}>Priorité</label>
                        <select
                          value={newTicketForm.priorite}
                          onChange={(e) => setNewTicketForm({...newTicketForm, priorite: e.target.value})}
                          style={{width: '100%', padding: '10px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '13px', background: 'white', outline: 'none'}}
                        >
                          <option value="basse">🟢 Basse</option>
                          <option value="moyenne">🟡 Moyenne</option>
                          <option value="haute">🔴 Haute</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label style={{display: 'block', fontSize: '12px', fontWeight: '500', color: '#4B5563', marginBottom: '6px'}}>Sujet de la demande</label>
                      <input
                        type="text"
                        value={newTicketForm.sujet}
                        onChange={(e) => setNewTicketForm({...newTicketForm, sujet: e.target.value})}
                        placeholder="Ex: Explication sur la dotation aux amortissements"
                        required
                        style={{width: '100%', padding: '10px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '13px', outline: 'none'}}
                      />
                    </div>

                    <div>
                      <label style={{display: 'block', fontSize: '12px', fontWeight: '500', color: '#4B5563', marginBottom: '6px'}}>Description détaillée</label>
                      <textarea
                        value={newTicketForm.description}
                        onChange={(e) => setNewTicketForm({...newTicketForm, description: e.target.value})}
                        placeholder="Décrivez votre problème ou votre question avec le plus de précisions possibles..."
                        required
                        rows={5}
                        style={{width: '100%', padding: '12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'inherit', resize: 'vertical'}}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={ticketActionLoading}
                      style={{
                        padding: '12px', background: ticketActionLoading ? '#73726C' : '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: ticketActionLoading ? 'not-allowed' : 'pointer', transition: 'background 0.2s', marginTop: '6px'
                      }}
                      onMouseEnter={(e) => !ticketActionLoading && (e.target.style.background = '#0F6E56')}
                      onMouseLeave={(e) => e.target.style.background = '#1D9E75'}
                    >
                      {ticketActionLoading ? 'Envoi en cours...' : 'Envoyer ma demande de support'}
                    </button>
                  </form>
                </div>
              )}

              {/* VIEW B: ACTIVE CONVERSATION MESSAGING (CHAT) */}
              {!showCreateTicket && selectedTicket && (
                <div style={{display: 'flex', background: 'white', border: '1px solid #E8E7E3', borderRadius: '12px', height: '520px', overflow: 'hidden'}}>
                  
                  {/* Sidebar/Thread header */}
                  <div style={{flex: 1, display: 'flex', flexDirection: 'column', height: '100%'}}>
                    <div style={{padding: '16px 24px', background: '#FAF9F5', borderBottom: '1px solid #E8E7E3', display: 'flex', alignItems: 'center', gap: '16px'}}>
                      <button
                        onClick={() => setSelectedTicket(null)}
                        style={{background: 'none', border: 'none', color: '#1D9E75', fontSize: '13px', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'}}
                      >
                        ← Retour
                      </button>
                      <div style={{flex: 1}}>
                        <div style={{fontSize: '14px', fontWeight: '600', color: '#1F2937'}}>{selectedTicket.sujet}</div>
                        <div style={{fontSize: '11px', color: '#73726C', marginTop: '2px'}}>
                          Priorité : {selectedTicket.priorite} | Statut : <strong>{selectedTicket.statut.toUpperCase()}</strong>
                        </div>
                      </div>
                    </div>

                    {/* Chat Bubble Container */}
                    <div style={{flex: 1, padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px', background: '#FAF9F5'}}>
                      
                      {/* Ticket initial description */}
                      <div style={{alignSelf: 'flex-end', maxWidth: '80%', padding: '12px 16px', borderRadius: '12px', background: '#1D9E75', color: 'white', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'}}>
                        <div style={{fontSize: '10px', color: '#E1F5EE', fontWeight: '500', marginBottom: '4px'}}>Vous (Description initiale)</div>
                        <div style={{fontSize: '13px', whiteSpace: 'pre-wrap'}}>{selectedTicket.description}</div>
                        <div style={{fontSize: '9px', color: '#E1F5EE', textAlign: 'right', marginTop: '6px'}}>
                          {selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : ''}
                        </div>
                      </div>

                      {/* Chat messages */}
                      {(selectedTicket.messages || []).map((msg, index) => {
                        if (index === 0 && msg.sender === 'client' && msg.text === selectedTicket.description) return null;
                        
                        const isClient = msg.sender === 'client';
                        return (
                          <div
                            key={index}
                            style={{
                              alignSelf: isClient ? 'flex-end' : 'flex-start',
                              maxWidth: '80%',
                              padding: '12px 16px',
                              borderRadius: '12px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                              background: isClient ? '#1D9E75' : 'white',
                              color: isClient ? 'white' : '#1F2937',
                              border: isClient ? 'none' : '1px solid #E8E7E3'
                            }}
                          >
                            <div style={{fontSize: '10px', color: isClient ? '#E1F5EE' : '#9CA3AF', fontWeight: '500', marginBottom: '4px'}}>
                              {isClient ? 'Vous' : '🛠️ Cabinet Conseil'}
                            </div>
                            <div style={{fontSize: '13px', whiteSpace: 'pre-wrap'}}>{msg.text}</div>
                            <div style={{fontSize: '9px', color: isClient ? '#E1F5EE' : '#9CA3AF', textAlign: 'right', marginTop: '4px'}}>
                              {msg.date ? new Date(msg.date).toLocaleString('fr-FR', {hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short'}) : ''}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Chat Textarea input */}
                    {selectedTicket.statut !== 'ferme' ? (
                      <form onSubmit={handleSendTicketReply} style={{padding: '16px 24px', background: 'white', borderTop: '1px solid #E8E7E3', display: 'flex', gap: '12px', alignItems: 'center'}}>
                        <textarea
                          value={ticketReplyText}
                          onChange={(e) => setTicketReplyText(e.target.value)}
                          placeholder="Écrivez un message ou répondez au cabinet..."
                          required
                          rows={2}
                          style={{flex: 1, padding: '10px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '13px', outline: 'none', resize: 'none', fontFamily: 'inherit'}}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendTicketReply(e);
                            }
                          }}
                        />
                        <button
                          type="submit"
                          disabled={ticketActionLoading || !ticketReplyText.trim()}
                          style={{
                            padding: '10px 20px', background: ticketActionLoading || !ticketReplyText.trim() ? '#E5E7EB' : '#1D9E75',
                            color: ticketActionLoading || !ticketReplyText.trim() ? '#9CA3AF' : 'white',
                            border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', height: '42px'
                          }}
                        >
                          Envoyer
                        </button>
                      </form>
                    ) : (
                      <div style={{padding: '16px', background: '#F3F4F6', textAlign: 'center', fontSize: '13px', color: '#4B5563', borderTop: '1px solid #E8E7E3'}}>
                        🔒 Ce ticket a été classé et fermé. Si vous avez une autre question, merci d'ouvrir un nouveau ticket.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* VIEW C: TICKETS INBOX LIST */}
              {!showCreateTicket && !selectedTicket && (
                <div style={{background: 'white', border: '1px solid #E8E7E3', borderRadius: '12px', overflow: 'hidden'}}>
                  {ticketsLoading ? (
                    <div style={{padding: '40px', textAlign: 'center'}}>Chargement de l'historique des tickets...</div>
                  ) : supportTickets.length === 0 ? (
                    <div style={{padding: '50px', textAlign: 'center', color: '#73726C'}}>
                      <div style={{fontSize: '36px', marginBottom: '12px'}}>🎫</div>
                      <h4 style={{margin: 0}}>Vous n'avez soumis aucun ticket d'assistance pour le moment.</h4>
                      <p style={{fontSize: '13px', marginTop: '6px'}}>Cliquez sur le bouton <strong>➕ Nouveau ticket</strong> ci-dessus pour poser votre première question.</p>
                    </div>
                  ) : (
                    <table style={{width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px'}}>
                      <thead>
                        <tr style={{background: '#FAF9F5', borderBottom: '1px solid #E8E7E3'}}>
                          <th style={{padding: '14px 20px', fontWeight: '600', color: '#4B5563'}}>Sujet</th>
                          <th style={{padding: '14px 20px', fontWeight: '600', color: '#4B5563'}}>Catégorie</th>
                          <th style={{padding: '14px 20px', fontWeight: '600', color: '#4B5563'}}>Priorité</th>
                          <th style={{padding: '14px 20px', fontWeight: '600', color: '#4B5563'}}>Dernière mise à jour</th>
                          <th style={{padding: '14px 20px', fontWeight: '600', color: '#4B5563'}}>Statut</th>
                          <th style={{padding: '14px 20px', fontWeight: '600', color: '#4B5563', textAlign: 'center'}}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {supportTickets.map((t) => {
                          const pInfo = getPriorityColor(t.priorite);
                          const sInfo = getStatusBadge(t.statut);
                          return (
                            <tr key={t.id} style={{borderBottom: '1px solid #F3F4F6'}}>
                              <td style={{padding: '16px 20px', fontWeight: '600', color: '#1F2937'}}>{t.sujet}</td>
                              <td style={{padding: '16px 20px', color: '#4B5563'}}>
                                <span style={{padding: '4px 8px', background: '#F3F4F6', borderRadius: '4px', fontSize: '11px'}}>{t.categorie}</span>
                              </td>
                              <td style={{padding: '16px 20px'}}>
                                <span style={{fontSize: '11px', color: pInfo.text, fontWeight: '500'}}>{pInfo.icon} {t.priorite}</span>
                              </td>
                              <td style={{padding: '16px 20px', color: '#6B7280'}}>
                                {t.updated_at ? new Date(t.updated_at).toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'}) : 'N/A'}
                              </td>
                              <td style={{padding: '16px 20px'}}>
                                <span style={{
                                  fontSize: '11px', padding: '3px 8px', borderRadius: '12px', fontWeight: '600', border: `1px solid ${sInfo.border}`,
                                  background: sInfo.bg, color: sInfo.text
                                }}>
                                  {sInfo.label.toUpperCase()}
                                </span>
                              </td>
                              <td style={{padding: '16px 20px', textAlign: 'center'}}>
                                <button
                                  onClick={() => setSelectedTicket(t)}
                                  style={{
                                    padding: '6px 12px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', transition: 'background 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.target.style.background = '#0F6E56'}
                                  onMouseLeave={(e) => e.target.style.background = '#1D9E75'}
                                >
                                  Consulter / Discuter
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

            </div>
          )}

          {activeSection === 'admin_rag' && (
            <AdminRAG />
          )}

          {activeSection === 'cadrage' && (
            <CadrageTiers />
          )}

          {activeSection === 'ecritures' && (
            <div style={{background:'white', padding:'24px', borderRadius:'12px', border:'1px solid #E8E7E3'}}>
              <h2 style={{fontSize:'20px', fontWeight:'600', marginBottom:'16px'}}>Écritures comptables</h2>
              <p style={{color:'#73726C', marginBottom: '20px'}}>Votre grand livre et le détail de toutes vos écritures s'afficheront ici.</p>
              
              {ecrituresLoading ? (
                <div style={{textAlign: 'center', padding: '40px'}}>Chargement des écritures...</div>
              ) : ecrituresData.length === 0 ? (
                <div style={{textAlign: 'center', padding: '40px', background: '#FAF9F5', borderRadius: '8px', color: '#73726C'}}>
                  Aucune écriture trouvée pour le moment.
                </div>
              ) : (
                window.innerWidth < 768 ? (
                  // VERSION MOBILE - CARTES
                  <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                    {ecrituresData.map((ecriture, idx) => (
                      <div key={idx} style={{background: 'white', border: '1px solid #E8E7E3', borderRadius: '8px', padding: '12px'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px'}}>
                          <div style={{flex: 1}}>
                            <div style={{fontSize: '12px', color: '#73726C', marginBottom: '4px'}}>
                              {new Date(ecriture.date).toLocaleDateString()}
                            </div>
                            <div style={{fontSize: '13px', fontWeight: '500', color: '#2D2D2D'}}>
                              {ecriture.compte}
                            </div>
                            <div style={{fontSize: '12px', color: '#73726C', marginTop: '4px', wordWrap: 'break-word', lineHeight: '1.4'}}>
                              {ecriture.libelle}
                            </div>
                          </div>
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', marginLeft: '12px'}}>
                            {ecriture.debit > 0 && (
                              <div style={{fontSize: '14px', fontWeight: '500', color: '#0F6E56'}}>
                                {ecriture.debit.toFixed(2)} €
                              </div>
                            )}
                            {ecriture.credit > 0 && (
                              <div style={{fontSize: '14px', fontWeight: '500', color: '#A32D2D'}}>
                                {ecriture.credit.toFixed(2)} €
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // VERSION DESKTOP - TABLEAU
                  <div style={{overflowX: 'auto'}}>
                    <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
                      <thead>
                        <tr style={{borderBottom: '1px solid #E8E7E3', textAlign: 'left', color: '#73726C'}}>
                          <th style={{padding: '12px 8px', fontWeight: '500', width: '100px'}}>Date</th>
                          <th style={{padding: '12px 8px', fontWeight: '500', width: '100px'}}>Compte</th>
                          <th style={{padding: '12px 8px', fontWeight: '500', width: '35%'}}>Libellé</th>
                          <th style={{padding: '12px 8px', fontWeight: '500', textAlign: 'right', minWidth: '100px'}}>Débit</th>
                          <th style={{padding: '12px 8px', fontWeight: '500', textAlign: 'right', minWidth: '100px'}}>Crédit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ecrituresData.map((ecriture, idx) => (
                          <tr key={idx} style={{borderBottom: '1px solid #E8E7E3'}}>
                            <td style={{padding: '10px 8px', whiteSpace: 'nowrap'}}>{new Date(ecriture.date).toLocaleDateString()}</td>
                            <td style={{padding: '10px 8px', whiteSpace: 'nowrap'}}>{ecriture.compte}</td>
                            <td style={{padding: '10px 8px', maxWidth: '200px', wordWrap: 'break-word', whiteSpace: 'normal', lineHeight: '1.4'}}>{ecriture.libelle}</td>
                            <td style={{padding: '10px 8px', textAlign: 'right', color: ecriture.debit > 0 ? '#2D2D2D' : '#D1D5DB', whiteSpace: 'nowrap', minWidth: '80px'}}>{ecriture.debit > 0 ? ecriture.debit.toFixed(2) + ' €' : '-'}</td>
                            <td style={{padding: '10px 8px', textAlign: 'right', color: ecriture.credit > 0 ? '#2D2D2D' : '#D1D5DB', whiteSpace: 'nowrap', minWidth: '80px'}}>{ecriture.credit > 0 ? ecriture.credit.toFixed(2) + ' €' : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          )}
          
          {activeSection === 'tva' && (
            <div style={{background:'white', padding:'24px', borderRadius:'12px', border:'1px solid #E8E7E3'}}>
              <h2 style={{fontSize:'20px', fontWeight:'600', marginBottom:'16px'}}>Déclaration TVA</h2>
              <p style={{color:'#73726C', marginBottom: '24px'}}>Suivez votre TVA collectée et déductible pour la période en cours.</p>
              
              {tvaLoading ? (
                <div style={{textAlign: 'center', padding: '40px'}}>Chargement des données TVA...</div>
              ) : !tvaData ? (
                <div style={{textAlign: 'center', padding: '40px', background: '#FAF9F5', borderRadius: '8px', color: '#73726C'}}>
                  Aucune donnée de TVA disponible.
                </div>
              ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#FAF9F5', padding: '16px', borderRadius: '8px', border: '1px solid #E8E7E3'}}>
                    <div>
                      <div style={{fontSize: '12px', color: '#73726C', textTransform: 'uppercase', marginBottom: '4px'}}>Période</div>
                      <div style={{fontSize: '16px', fontWeight: '500', color: '#2D2D2D'}}>{tvaData.periode.mois} ({tvaData.periode.type})</div>
                    </div>
                    <div style={{textAlign: 'right'}}>
                      <div style={{fontSize: '12px', color: '#73726C', textTransform: 'uppercase', marginBottom: '4px'}}>Régime</div>
                      <div style={{fontSize: '16px', fontWeight: '500', color: '#2D2D2D'}}>{tvaData.regime === 'reel_normal' ? 'Réel Normal' : tvaData.regime}</div>
                    </div>
                  </div>

                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
                    {/* TVA Collectée */}
                    <div style={{border: '1px solid #E8E7E3', borderRadius: '8px', padding: '16px'}}>
                      <h3 style={{fontSize: '14px', fontWeight: '600', color: '#2D2D2D', marginBottom: '12px'}}>TVA Collectée (Ventes)</h3>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        {tvaData.tva_collectee.detail.map((item, idx) => (
                          <div key={idx} style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px'}}>
                            <span style={{color: '#73726C'}}>Taux à {item.taux}%</span>
                            <span style={{fontWeight: '500', color: '#2D2D2D'}}>{item.tva.toFixed(2)} €</span>
                          </div>
                        ))}
                        <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '600', color: '#2D2D2D', borderTop: '1px solid #E8E7E3', paddingTop: '8px', marginTop: '4px'}}>
                          <span>Total</span>
                          <span>{tvaData.tva_collectee.total.toFixed(2)} €</span>
                        </div>
                      </div>
                    </div>

                    {/* TVA Déductible */}
                    <div style={{border: '1px solid #E8E7E3', borderRadius: '8px', padding: '16px'}}>
                      <h3 style={{fontSize: '14px', fontWeight: '600', color: '#2D2D2D', marginBottom: '12px'}}>TVA Déductible (Achats)</h3>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                        {tvaData.tva_deductible.detail.map((item, idx) => (
                          <div key={idx} style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px'}}>
                            <span style={{color: '#73726C'}}>Taux à {item.taux}%</span>
                            <span style={{fontWeight: '500', color: '#2D2D2D'}}>{item.tva_deductible.toFixed(2)} €</span>
                          </div>
                        ))}
                        <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '600', color: '#2D2D2D', borderTop: '1px solid #E8E7E3', paddingTop: '8px', marginTop: '4px'}}>
                          <span>Total</span>
                          <span>{tvaData.tva_deductible.total.toFixed(2)} €</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Solde */}
                  <div style={{background: tvaData.solde.nature === 'a_payer' ? '#E1F5EE' : '#FAF9F5', border: '1px solid', borderColor: tvaData.solde.nature === 'a_payer' ? '#5DCAA5' : '#E8E7E3', borderRadius: '8px', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <div>
                      <div style={{fontSize: '14px', fontWeight: '600', color: tvaData.solde.nature === 'a_payer' ? '#0F6E56' : '#2D2D2D', marginBottom: '4px'}}>
                        {tvaData.solde.nature === 'a_payer' ? 'Net à payer' : 'Crédit de TVA'}
                      </div>
                      <div style={{fontSize: '12px', color: '#73726C'}}>
                        Échéance : {new Date(tvaData.echeance).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{fontSize: '24px', fontWeight: '600', color: tvaData.solde.nature === 'a_payer' ? '#0F6E56' : '#2D2D2D'}}>
                      {tvaData.solde.montant.toFixed(2)} €
                    </div>
                  </div>

                  {/* Bouton Lancement Copilote TVA */}
                  <button 
                    onClick={() => setShowTvaCopilote(true)}
                    style={{width:'100%', padding:'14px', background:'linear-gradient(135deg, #10B981, #059669)', color:'white', border:'none', borderRadius:'10px', fontWeight:'600', fontSize:'14px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', marginTop:'16px', boxShadow:'0 4px 12px rgba(16, 185, 129, 0.2)', transition:'transform 0.2s'}}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    🤖 Déclarer la TVA avec le Copilote
                  </button>

                  {showTvaCopilote && (
                    <CopiloteDeclaration
                      clientId={clientId}
                      onClose={() => setShowTvaCopilote(false)}
                      type="TVA"
                      data={tvaData}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {activeSection === 'documents' && (
            <div style={{
              display: 'flex',
              flexDirection: window.innerWidth < 768 ? 'column' : 'row',
              gap: '24px',
              width: '100%',
              alignItems: 'flex-start',
              padding: '24px',
              boxSizing: 'border-box'
            }}>
              {/* Panneau latéral gauche : Filtres des mois */}
              {window.innerWidth < 768 ? (
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  overflowX: 'auto',
                  padding: '4px 0 12px',
                  width: '100%',
                  WebkitOverflowScrolling: 'touch'
                }}>
                  <button
                    onClick={() => setDocMonth(null)}
                    style={{
                      whiteSpace: 'nowrap',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      border: '1px solid #E8E7E3',
                      background: docMonth === null ? '#1D9E75' : 'white',
                      color: docMonth === null ? 'white' : '#2D2D2D',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    📂 Toute l'année
                  </button>
                  {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'].map((mName, idx) => {
                    const mVal = idx + 1;
                    return (
                      <button
                        key={mVal}
                        onClick={() => setDocMonth(mVal)}
                        style={{
                          whiteSpace: 'nowrap',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          border: '1px solid #E8E7E3',
                          background: docMonth === mVal ? '#1D9E75' : 'white',
                          color: docMonth === mVal ? 'white' : '#2D2D2D',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer'
                        }}
                      >
                        📁 {mName}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div style={{
                  width: '240px',
                  background: 'white',
                  borderRadius: '12px',
                  border: '1px solid #E8E7E3',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  flexShrink: 0
                }}>
                  <h3 style={{fontSize: '14px', fontWeight: '600', color: '#73726C', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', paddingLeft: '8px'}}>📁 Dossiers mensuels</h3>
                  <button
                    onClick={() => setDocMonth(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: 'none',
                      background: docMonth === null ? '#E1F5EE' : 'transparent',
                      color: docMonth === null ? '#0F6E56' : '#2D2D2D',
                      fontSize: '13px',
                      fontWeight: docMonth === null ? '600' : '400',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >
                    <span>📂</span> Toute l'année
                  </button>
                  {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'].map((mName, idx) => {
                    const mVal = idx + 1;
                    return (
                      <button
                        key={mVal}
                        onClick={() => setDocMonth(mVal)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: 'none',
                          background: docMonth === mVal ? '#E1F5EE' : 'transparent',
                          color: docMonth === mVal ? '#0F6E56' : '#2D2D2D',
                          fontSize: '13px',
                          fontWeight: docMonth === mVal ? '600' : '400',
                          textAlign: 'left',
                          cursor: 'pointer',
                          transition: 'all 0.15s'
                        }}
                      >
                        <span>📁</span> {mName}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Partie droite : Téléversement et Historique */}
              <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '24px', width: '100%'}}>
                <div style={{background:'white', padding: window.innerWidth < 768 ? '12px' : '24px', borderRadius:'12px', border:'1px solid #E8E7E3'}}>
                  <h2 style={{fontSize:'20px', fontWeight:'600', marginBottom:'16px'}}>Déposer un document</h2>
                  {isExerciseClosed ? (
                    <div style={{padding: '24px', border: '2px dashed #E8E7E3', borderRadius: '12px', background: '#FAF9F5', textAlign: 'center', color: '#73726C', fontSize: '14px'}}>
                      🔒 Le dépôt de documents est désactivé car cet exercice est clôturé.
                    </div>
                  ) : (
                    window.innerWidth < 768 ? (
                      // Mobile : 3 boutons distincts
                      <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                        <button
                          type="button"
                          onClick={() => {
                            console.log('DEBUG: Click camera2 button');
                            if (fileInputCameraRef.current) {
                              fileInputCameraRef.current.click();
                            } else {
                              console.error('DEBUG: fileInputCameraRef not found');
                            }
                          }}
                          style={{display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', border: '2px solid #E8E7E3', borderRadius: '12px', background: 'white', cursor: 'pointer', fontSize: '14px', color: '#2D2D2D', transition: 'all 0.2s'}}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1D9E75'; e.currentTarget.style.background = '#E1F5EE'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E8E7E3'; e.currentTarget.style.background = 'white'; }}
                        >
                          <span style={{fontSize: '24px'}}>📷</span>
                          <span style={{flex: 1, textAlign: 'left'}}>Prendre une photo</span>
                          <span style={{fontSize: '18px', color: '#73726C'}}>→</span>
                        </button>
                        <input id="fileInputCamera2" ref={fileInputCameraRef} type="file" style={{display: 'none'}} accept="image/*" capture="environment" onChange={(e) => handleFileUpload(e)} />

                        <button
                          type="button"
                          onClick={() => {
                            console.log('DEBUG: Click gallery2 button');
                            if (fileInputGalleryRef.current) {
                              fileInputGalleryRef.current.click();
                            } else {
                              console.error('DEBUG: fileInputGalleryRef not found');
                            }
                          }}
                          style={{display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', border: '2px solid #E8E7E3', borderRadius: '12px', background: 'white', cursor: 'pointer', fontSize: '14px', color: '#2D2D2D', transition: 'all 0.2s'}}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1D9E75'; e.currentTarget.style.background = '#E1F5EE'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E8E7E3'; e.currentTarget.style.background = 'white'; }}
                        >
                          <span style={{fontSize: '24px'}}>📁</span>
                          <span style={{flex: 1, textAlign: 'left'}}>Depuis la galerie</span>
                          <span style={{fontSize: '18px', color: '#73726C'}}>→</span>
                        </button>
                        <input id="fileInputGallery2" ref={fileInputGalleryRef} type="file" style={{display: 'none'}} accept="image/*" onChange={(e) => handleFileUpload(e)} />

                        <button
                          type="button"
                          onClick={() => {
                            console.log('DEBUG: Click PDF2 button');
                            if (fileInputPdfRef.current) {
                              fileInputPdfRef.current.click();
                            } else {
                              console.error('DEBUG: fileInputPdfRef not found');
                            }
                          }}
                          style={{display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', border: '2px solid #E8E7E3', borderRadius: '12px', background: 'white', cursor: 'pointer', fontSize: '14px', color: '#2D2D2D', transition: 'all 0.2s'}}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1D9E75'; e.currentTarget.style.background = '#E1F5EE'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E8E7E3'; e.currentTarget.style.background = 'white'; }}
                        >
                          <span style={{fontSize: '24px'}}>📄</span>
                          <span style={{flex: 1, textAlign: 'left'}}>Fichier PDF</span>
                          <span style={{fontSize: '18px', color: '#73726C'}}>→</span>
                        </button>
                        <input id="fileInputPdf2" ref={fileInputPdfRef} type="file" style={{display: 'none'}} accept=".pdf" onChange={(e) => handleFileUpload(e)} />
                      </div>
                    ) : (
                      // Desktop : zone de dépôt classique
                      <div
                        style={{border: '2px dashed #E8E7E3', borderRadius: '12px', padding: '40px', textAlign: 'center', background: '#FAF9F5', cursor: 'pointer', transition: 'all 0.2s'}}
                        onClick={() => {
                          console.log('DEBUG: Click documents desktop upload zone');
                          if (fileInputDocumentsDesktopRef.current) {
                            fileInputDocumentsDesktopRef.current.click();
                          } else {
                            console.error('DEBUG: fileInputDocumentsDesktopRef not found');
                          }
                        }}
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#1D9E75'; e.currentTarget.style.background = '#E1F5EE'; }}
                        onDragLeave={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#E8E7E3'; e.currentTarget.style.background = '#FAF9F5'; }}
                        onDrop={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#E8E7E3'; e.currentTarget.style.background = '#FAF9F5'; handleFileUpload(e); }}
                      >
                        <div style={{fontSize: '32px', marginBottom: '12px'}}>📤</div>
                        <div style={{fontSize: '16px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px'}}>Cliquez ou glissez-déposez un fichier ici</div>
                        <div style={{fontSize: '13px', color: '#73726C'}}>PDF, JPG ou PNG (Max 10 Mo)</div>
                        <input type="file" id="fileInputDocumentsDesktop" ref={fileInputDocumentsDesktopRef} style={{display: 'none'}} accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileUpload(e)} />
                      </div>
                    )
                  )}
                </div>

                <div style={{background:'white', padding: window.innerWidth < 768 ? '12px' : '24px', borderRadius:'12px', border:'1px solid #E8E7E3'}}>
                  <h2 style={{fontSize:'20px', fontWeight:'600', marginBottom:'16px'}}>Historique des documents</h2>
                  
                  {/* Status tabs + Search bar */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #E8E7E3',
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                    gap: '12px',
                    paddingBottom: '8px'
                  }}>
                    <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                      {[
                        {id: 'tous', label: 'Tous'},
                        {id: 'a_valider', label: '⏳ À valider'},
                        {id: 'traites', label: '✅ Traités'},
                        {id: 'non_rapproches', label: '🔗 Non rapprochés'}
                      ].map(tab => (
                        <button
                          key={tab.id}
                          onClick={() => setDocStatus(tab.id)}
                          style={{
                            padding: '6px 16px',
                            borderRadius: '20px',
                            border: 'none',
                            background: docStatus === tab.id ? '#1D9E75' : 'transparent',
                            color: docStatus === tab.id ? 'white' : '#73726C',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.15s'
                          }}
                          onMouseEnter={e => { if (docStatus !== tab.id) e.currentTarget.style.background = '#F5F4ED'; }}
                          onMouseLeave={e => { if (docStatus !== tab.id) e.currentTarget.style.background = 'transparent'; }}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    <div style={{display: 'flex', gap: '8px', width: window.innerWidth < 768 ? '100%' : 'auto', alignItems: 'center'}}>
                      <button
                        onClick={handleTriggerSync}
                        disabled={syncRunning}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #1D9E75',
                          background: syncRunning ? '#FAF9F5' : 'white',
                          color: '#0F6E56',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          boxSizing: 'border-box',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.2s'
                        }}
                      >
                        <span>{syncRunning ? '⏳ Synchro...' : '🔄 Synchro Mobile'}</span>
                      </button>
                      <div style={{position: 'relative', width: window.innerWidth < 768 ? '100%' : '260px'}}>
                        <input
                          type="text"
                          placeholder="Rechercher un document..."
                          value={docSearch}
                          onChange={e => setDocSearch(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px 8px 32px',
                            borderRadius: '8px',
                            border: '1px solid #E8E7E3',
                            fontSize: '13px',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                        />
                        <span style={{position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: '#73726C'}}>🔍</span>
                      </div>
                    </div>
                  </div>

                  {documentsLoading ? (
                    <div style={{textAlign: 'center', padding: '40px'}}>Chargement des documents...</div>
                  ) : !documentsData || documentsData.documents.length === 0 ? (
                    <div style={{textAlign: 'center', padding: window.innerWidth < 768 ? '16px' : '40px', background: '#FAF9F5', borderRadius: '8px', color: '#73726C'}}>
                      Aucun document trouvé avec ces filtres.
                    </div>
                  ) : (
                    <div style={{overflowX: 'auto'}}>
                      <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
                        <thead>
                          <tr style={{borderBottom: '1px solid #E8E7E3', textAlign: 'left', color: '#73726C'}}>
                            <th style={{padding: '12px 8px', fontWeight: '500'}}>Date</th>
                            <th style={{padding: '12px 8px', fontWeight: '500'}}>Nom du fichier</th>
                            <th style={{padding: '12px 8px', fontWeight: '500'}}>Fournisseur</th>
                            <th style={{padding: '12px 8px', fontWeight: '500', textAlign: 'center'}}>Conformité</th>
                            <th style={{padding: '12px 8px', fontWeight: '500', textAlign: 'right'}}>Montant TTC</th>
                            <th style={{padding: '12px 8px', fontWeight: '500', textAlign: 'center'}}>Statut</th>
                            <th style={{padding: '12px 8px', fontWeight: '500', textAlign: 'center'}}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documentsData && documentsData.documents && documentsData.documents.length > 0 ? (
                            documentsData.documents.map((doc, idx) => {
                              console.log('📄 DEBUG - Rendu document:', idx, doc);
                              return (
                            <tr
                              key={idx}
                              onClick={() => {
                                console.log('📄 DEBUG - Click sur ligne, doc statut:', doc.statut);
                                loadDocumentDetails(doc.id);
                              }}
                              style={{borderBottom: '1px solid #E8E7E3', cursor: 'pointer', background: doc.statut === 'en_attente' ? '#FAEEDA' : 'transparent'}}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#FAF9F5'}
                              onMouseLeave={(e) => e.currentTarget.style.background = doc.statut === 'en_attente' ? '#FAEEDA' : 'transparent'}
                            >
                              <td style={{padding: '12px 8px', color: '#73726C'}}>
                                {doc.date ? new Date(doc.date.split('/').reverse().join('-')).toLocaleDateString('fr-FR') : '-'}
                              </td>
                              <td style={{padding: '12px 8px', fontWeight: '500'}}>{doc.nom}</td>
                              <td style={{padding: '12px 8px'}}>{doc.fournisseur || '-'}</td>
                              <td style={{padding: '12px 8px', textAlign: 'center'}}>
                                {(() => {
                                  const badge = getConformiteBadge(doc.conformite);
                                  return (
                                    <span
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (badge.details) {
                                          setConformiteTooltip({
                                            docId: doc.id,
                                            x: e.clientX,
                                            y: e.clientY,
                                            conformite: doc.conformite
                                          });
                                        }
                                      }}
                                      style={{
                                        padding: '4px 8px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        background: badge.background,
                                        color: badge.color,
                                        cursor: badge.details ? 'pointer' : 'default',
                                        display: 'inline-block'
                                      }}
                                    >
                                      {badge.label}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td style={{padding: '12px 8px', textAlign: 'right', fontWeight: '500'}}>
                                {doc.montant_ttc && doc.montant_ttc !== 'N/A' ? doc.montant_ttc : '-'}
                              </td>
                              <td style={{padding: '12px 8px', textAlign: 'center'}}>
                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'}}>
                                  <span style={{
                                    padding: '4px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '500',
                                    background: doc.statut === 'traite' ? '#E1F5EE' : doc.statut === 'en_attente' || doc.statut === 'revision' ? '#FAEEDA' : '#FEE2E2',
                                    color: doc.statut === 'traite' ? '#0F6E56' : doc.statut === 'en_attente' || doc.statut === 'revision' ? '#B45309' : '#991B1B'
                                  }}>
                                    {doc.statut === 'traite' ? 'Traité' : doc.statut === 'en_attente' ? 'En attente' : doc.statut === 'revision' ? 'À réviser' : 'Erreur'}
                                  </span>
                                  {doc.statut === 'traite' && (
                                    <span style={{
                                      padding: '2px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '600',
                                      background: doc.rapproche ? '#E0F2FE' : '#FEF3C7',
                                      color: doc.rapproche ? '#0369A1' : '#B45309',
                                      border: doc.rapproche ? '1px solid #BAE6FD' : '1px solid #FDE68A',
                                      display: 'inline-flex', alignItems: 'center', gap: '3px'
                                    }} title={doc.rapproche ? `Rapproché le ${doc.date_rapprochement} - ${doc.libelle_rapprochement}` : 'Aucune transaction bancaire correspondante trouvée'}>
                                      {doc.rapproche ? '🔗 Rapproché' : '⏳ Non rapproché'}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td style={{padding: '12px 8px', textAlign: 'center'}}>
                                {doc.statut === 'en_attente' ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      loadDocumentDetails(doc.id);
                                    }}
                                    disabled={isExerciseClosed}
                                    style={{
                                      padding: '6px 12px',
                                      background: isExerciseClosed ? '#9CA3AF' : '#1D9E75',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      fontSize: '11px',
                                      fontWeight: '500',
                                      cursor: isExerciseClosed ? 'not-allowed' : 'pointer',
                                      transition: 'background 0.2s'
                                    }}
                                  >
                                    {isExerciseClosed ? 'Verrouillé' : 'Valider'}
                                  </button>
                                ) : doc.statut === 'traite' ? (
                                  <span style={{fontSize: '11px', color: '#73726C'}}>✓ Validé</span>
                                ) : (
                                  <span style={{fontSize: '11px', color: '#A32D2D'}}>⚠ {doc.statut}</span>
                                )}
                              </td>
                            </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="7" style={{padding: '20px', textAlign: 'center', color: '#73726C'}}>
                                Aucun document à afficher
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {activeSection === 'parametres' && (
            <div style={{height: '100%', overflowY: 'auto'}}>
              <DashboardConfigurator clientId={clientId} />
            </div>
          )}
          </div>
        )}
      </div>

      {/* Modale de validation des documents */}
      {showValidationModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '20px'
        }}>
          {!selectedDocument || loadingDocumentDetails ? (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              textAlign: 'center'
            }}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>⏳</div>
              <div style={{fontSize: '16px', color: '#2D2D2D', marginBottom: '8px'}}>Chargement des données...</div>
              <div style={{fontSize: '13px', color: '#73726C'}}>Veuillez patienter</div>
            </div>
          ) : (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: window.innerWidth < 768 ? '20px' : '32px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Header */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px'}}>
              <h2 style={{fontSize: '18px', fontWeight: '600', color: '#2D2D2D', margin: 0}}>
                📝 Validation Document
              </h2>
              <button
                onClick={() => {
                  setShowValidationModal(false);
                  setSelectedDocument(null);
                  setSelectedDocumentConformite(null);

                  // Réinitialiser le formulaire
                  setValidationForm({
                    fournisseur: '',
                    montant_ht: 0,
                    tva: 0,
                    taux_tva: 20
                  });
                }}
                style={{background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#73726C', padding: 0}}
              >
                ×
              </button>
            </div>

            {/* Informations du document */}
            <div style={{background: '#FAF9F5', padding: '16px', borderRadius: '8px', marginBottom: '24px'}}>
              <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                <strong>Nom du fichier :</strong> {selectedDocument.nom}
              </div>
              <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                <strong>Fournisseur :</strong> {selectedDocument.fournisseur || 'Non identifié'}
              </div>
              {selectedDocument.fournisseur_complet?.siret && (
                <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                  <strong>SIRET :</strong> {selectedDocument.fournisseur_complet.siret}
                </div>
              )}
              {selectedDocument.fournisseur_complet?.numero_tva && (
                <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                  <strong>N° TVA :</strong> {selectedDocument.fournisseur_complet.numero_tva}
                </div>
              )}
              {selectedDocument.fournisseur_complet?.adresse && (
                <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                  <strong>Adresse :</strong> {selectedDocument.fournisseur_complet.adresse}
                </div>
              )}
              <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                <strong>Date de facture :</strong> {selectedDocument.date_facture || 'N/A'}
              </div>
              <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                <strong>Date de réception :</strong> {selectedDocument.date_reception || 'N/A'}
              </div>
              {selectedDocument.date_echeance && (
                <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                  <strong>Date d'échéance :</strong> {selectedDocument.date_echeance}
                </div>
              )}
              <div style={{fontSize: '13px', color: '#73726C'}}>
                <strong>Montant TTC extrait :</strong> {
                  selectedDocument.montant_ht && selectedDocument.tva
                    ? `${(parseFloat(selectedDocument.montant_ht) + parseFloat(selectedDocument.tva)).toFixed(2)} ${selectedDocument.devise || '€'}`
                    : selectedDocument.montant_ttc ? `${selectedDocument.montant_ttc} ${selectedDocument.devise || '€'}` : 'En cours d\'extraction'
                }
              </div>
            </div>

            {/* Détails TVA */}
            {selectedDocument.tva_complet && (
              <div style={{background: '#F3F4F6', padding: '16px', borderRadius: '8px', marginBottom: '24px'}}>
                <div style={{fontSize: '14px', fontWeight: '600', color: '#2D2D2D', marginBottom: '12px'}}>
                  💰 Détails de la TVA
                </div>
                <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                  <strong>Taux applicable :</strong> {selectedDocument.tva_complet.taux || 0}%
                </div>
                <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                  <strong>Montant TVA :</strong> {selectedDocument.tva_complet.montant || 0} {selectedDocument.devise || '€'}
                </div>
                {selectedDocument.tva_complet.categorie && (
                  <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                    <strong>Catégorie :</strong> {selectedDocument.tva_complet.categorie}
                  </div>
                )}
                {selectedDocument.tva_complet.compte_pcg && (
                  <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                    <strong>Compte PCG :</strong> {selectedDocument.tva_complet.compte_pcg}
                  </div>
                )}
                <div style={{fontSize: '13px', color: selectedDocument.tva_complet.deductible ? '#0F6E56' : '#A32D2D'}}>
                  <strong>TVA récupérable :</strong> {selectedDocument.tva_complet.deductible ? '✅ Oui' : '❌ Non'}
                </div>
              </div>
            )}

            {/* Qualité OCR */}
            {selectedDocument.extraction && (
              <div style={{background: '#F3F4F6', padding: '16px', borderRadius: '8px', marginBottom: '24px'}}>
                <div style={{fontSize: '14px', fontWeight: '600', color: '#2D2D2D', marginBottom: '12px'}}>
                  🔍 Qualité de l'extraction OCR
                </div>
                <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                  <strong>Méthode :</strong> {selectedDocument.extraction.methode || 'N/A'}
                </div>
                {selectedDocument.extraction.confiance !== undefined && (
                  <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                    <strong>Confiance :</strong> {(selectedDocument.extraction.confiance * 100).toFixed(1)}%
                  </div>
                )}
                {selectedDocument.extraction.texte_brut && (
                  <div style={{fontSize: '13px', color: '#73726C', marginBottom: '8px'}}>
                    <strong>Texte extrait :</strong> {selectedDocument.extraction.texte_brut.substring(0, 100)}...
                  </div>
                )}
              </div>
            )}

            {/* Écritures comptables */}
            {selectedDocument.ecritures && selectedDocument.ecritures.length > 0 && (
              <div style={{background: '#F3F4F6', padding: '16px', borderRadius: '8px', marginBottom: '24px'}}>
                <div style={{fontSize: '14px', fontWeight: '600', color: '#2D2D2D', marginBottom: '12px'}}>
                  📒 Écritures comptables générées ({selectedDocument.ecritures.length})
                </div>
                {selectedDocument.ecritures.map((ecriture, idx) => (
                  <div key={idx} style={{
                    background: 'white',
                    padding: '10px',
                    borderRadius: '6px',
                    marginBottom: idx < selectedDocument.ecritures.length - 1 ? '8px' : '0',
                    fontSize: '12px'
                  }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                      <strong>{ecriture.compte_pcg}</strong>
                      <span style={{color: ecriture.debit > 0 ? '#0F6E56' : '#A32D2D'}}>
                        {ecriture.debit > 0 ? `D ${ecriture.debit}€` : `C ${ecriture.credit}€`}
                      </span>
                    </div>
                    <div style={{color: '#73726C', fontSize: '11px'}}>
                      {ecriture.libelle}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Formulaire de validation */}
            <div style={{marginBottom: '24px'}}>
              <div style={{fontSize: '14px', fontWeight: '600', color: '#2D2D2D', marginBottom: '16px'}}>
                {selectedDocument.statut === 'traite' ? 'Détails du document validé :' : 'Confirmer les montants avant validation :'}
              </div>

              {/* Montant HT global et Fournisseur */}
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px'}}>
                <div>
                  <label style={{fontSize: '12px', color: '#73726C', marginBottom: '4px', display: 'block'}}>Montant HT total (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={validationForm.montant_ht}
                    onChange={(e) => handleFormFieldChange('montant_ht', e.target.value)}
                    disabled={selectedDocument.statut === 'traite' || isExerciseClosed}
                    id={`validation_ht_${selectedDocument.id}`}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #E8E7E3',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      background: (selectedDocument.statut === 'traite' || isExerciseClosed) ? '#F3F4F6' : 'white',
                      color: (selectedDocument.statut === 'traite' || isExerciseClosed) ? '#6B7280' : 'inherit'
                    }}
                  />
                </div>
                <div>
                  <label style={{fontSize: '12px', color: '#73726C', marginBottom: '4px', display: 'block'}}>TVA totale (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={validationForm.tva}
                    onChange={(e) => handleFormFieldChange('tva', e.target.value)}
                    disabled={selectedDocument.statut === 'traite' || isExerciseClosed}
                    id={`validation_tva_${selectedDocument.id}`}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #E8E7E3',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                      background: (selectedDocument.statut === 'traite' || isExerciseClosed) ? '#F3F4F6' : 'white',
                      color: (selectedDocument.statut === 'traite' || isExerciseClosed) ? '#6B7280' : 'inherit'
                    }}
                  />
                </div>
              </div>

              {/* VENTILATION TVA MULTI-TAUX */}
              <div style={{
                background: validationForm.lignes_tva && validationForm.lignes_tva.length > 1
                  ? '#FFF7ED' : '#F3F4F6',
                border: validationForm.lignes_tva && validationForm.lignes_tva.length > 1
                  ? '2px solid #F59E0B' : '1px solid #E8E7E3',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                  <div style={{fontSize: '13px', fontWeight: '600', color: '#2D2D2D'}}>
                    📊 Ventilation TVA par taux
                    {validationForm.lignes_tva && validationForm.lignes_tva.length > 1 && (
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '11px',
                        background: '#F59E0B',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontWeight: '600'
                      }}>⚖️ Multi-taux — Conformité CGI Art. 206</span>
                    )}
                  </div>
                  {selectedDocument.statut !== 'traite' && !isExerciseClosed && (
                    <button
                      onClick={() => {
                        const newLine = { taux: 20, base_ht: 0, montant_tva: 0, libelle: 'TVA 20%' };
                        setValidationForm(prev => ({
                          ...prev,
                          lignes_tva: [...(prev.lignes_tva || []), newLine]
                        }));
                      }}
                      style={{
                        background: '#1D9E75',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >+ Ajouter taux</button>
                  )}
                </div>

                {/* Tableau des lignes TVA */}
                {validationForm.lignes_tva && validationForm.lignes_tva.length > 0 ? (
                  <div style={{overflowX: 'auto'}}>
                    <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '12px'}}>
                      <thead>
                        <tr style={{borderBottom: '2px solid #E8E7E3'}}>
                          <th style={{textAlign: 'left', padding: '6px 4px', color: '#73726C', fontWeight: '600', width: '60px'}}>Taux</th>
                          <th style={{textAlign: 'right', padding: '6px 4px', color: '#73726C', fontWeight: '600'}}>Base HT (€)</th>
                          <th style={{textAlign: 'right', padding: '6px 4px', color: '#73726C', fontWeight: '600'}}>TVA (€)</th>
                          <th style={{textAlign: 'left', padding: '6px 4px', color: '#73726C', fontWeight: '600'}}>Compte</th>
                          {selectedDocument.statut !== 'traite' && !isExerciseClosed && <th style={{width: '30px'}}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {validationForm.lignes_tva.map((ligne, idx) => {
                          const COULEURS_TAUX = {
                            20: { bg: '#EEF2FF', border: '#6366F1', text: '#4338CA' },
                            10: { bg: '#FFF7ED', border: '#F97316', text: '#C2410C' },
                            5.5: { bg: '#F0FDF4', border: '#22C55E', text: '#15803D' },
                            2.1: { bg: '#FFF1F2', border: '#FB7185', text: '#BE123C' }
                          };
                          const couleur = COULEURS_TAUX[ligne.taux] || { bg: '#F3F4F6', border: '#9CA3AF', text: '#374151' };
                          const COMPTES_TVA = { 20: '445662', 10: '445661', 5.5: '445663', 2.1: '445664' };
                          const compte = ligne.compte_tva || COMPTES_TVA[ligne.taux] || '445662';
                          return (
                            <tr key={idx} style={{
                              background: couleur.bg,
                              borderLeft: `3px solid ${couleur.border}`,
                              marginBottom: '4px'
                            }}>
                              <td style={{padding: '6px 4px'}}>
                                <select
                                  value={ligne.taux}
                                  disabled={selectedDocument.statut === 'traite' || isExerciseClosed}
                                  onChange={(e) => {
                                    const newTaux = parseFloat(e.target.value);
                                    setValidationForm(prev => {
                                      const lignes = [...prev.lignes_tva];
                                      const COMPTES = { 20: '445662', 10: '445661', 5.5: '445663', 2.1: '445664' };
                                      lignes[idx] = { ...lignes[idx], taux: newTaux, compte_tva: COMPTES[newTaux] || '445662' };
                                      return { ...prev, lignes_tva: lignes };
                                    });
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: couleur.text,
                                    fontWeight: '700',
                                    fontSize: '12px',
                                    cursor: (selectedDocument.statut === 'traite' || isExerciseClosed) ? 'default' : 'pointer'
                                  }}
                                >
                                  <option value="20">20%</option>
                                  <option value="10">10%</option>
                                  <option value="5.5">5.5%</option>
                                  <option value="2.1">2.1%</option>
                                </select>
                              </td>
                              <td style={{padding: '6px 4px', textIndent: 'right'}}>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={ligne.base_ht || 0}
                                  disabled={selectedDocument.statut === 'traite' || isExerciseClosed}
                                  onChange={(e) => {
                                    const newBaseHT = parseFloat(e.target.value) || 0;
                                    setValidationForm(prev => {
                                      const lignes = [...prev.lignes_tva];
                                      const tva_calc = Math.round(newBaseHT * lignes[idx].taux) / 100;
                                      lignes[idx] = { ...lignes[idx], base_ht: newBaseHT, montant_tva: tva_calc };
                                      // Recalculer totaux
                                      const totalHT = lignes.reduce((s, l) => s + (parseFloat(l.base_ht) || 0), 0);
                                      const totalTVA = lignes.reduce((s, l) => s + (parseFloat(l.montant_tva) || 0), 0);
                                      return { ...prev, lignes_tva: lignes, montant_ht: Math.round(totalHT * 100) / 100, tva: Math.round(totalTVA * 100) / 100 };
                                    });
                                  }}
                                  style={{
                                    width: '70px',
                                    border: 'none',
                                    background: 'transparent',
                                    textAlign: 'right',
                                    fontSize: '12px',
                                    color: '#2D2D2D'
                                  }}
                                />
                              </td>
                              <td style={{padding: '6px 4px', textAlign: 'right'}}>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={ligne.montant_tva || 0}
                                  disabled={selectedDocument.statut === 'traite' || isExerciseClosed}
                                  onChange={(e) => {
                                    const newTVA = parseFloat(e.target.value) || 0;
                                    setValidationForm(prev => {
                                      const lignes = [...prev.lignes_tva];
                                      lignes[idx] = { ...lignes[idx], montant_tva: newTVA };
                                      const totalTVA = lignes.reduce((s, l) => s + (parseFloat(l.montant_tva) || 0), 0);
                                      return { ...prev, lignes_tva: lignes, tva: Math.round(totalTVA * 100) / 100 };
                                    });
                                  }}
                                  style={{
                                    width: '60px',
                                    border: 'none',
                                    background: 'transparent',
                                    textAlign: 'right',
                                    fontSize: '12px',
                                    color: couleur.text,
                                    fontWeight: '600'
                                  }}
                                />
                              </td>
                              <td style={{padding: '6px 4px', color: '#9CA3AF', fontSize: '11px'}}>
                                {compte}
                              </td>
                              {selectedDocument.statut !== 'traite' && !isExerciseClosed && (
                                <td style={{padding: '6px 4px', textAlign: 'center'}}>
                                  <button
                                    onClick={() => {
                                      setValidationForm(prev => {
                                        const lignes = prev.lignes_tva.filter((_, i) => i !== idx);
                                        const totalHT = lignes.reduce((s, l) => s + (parseFloat(l.base_ht) || 0), 0);
                                        const totalTVA = lignes.reduce((s, l) => s + (parseFloat(l.montant_tva) || 0), 0);
                                        return { ...prev, lignes_tva: lignes, montant_ht: Math.round(totalHT * 100) / 100, tva: Math.round(totalTVA * 100) / 100 };
                                      });
                                    }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: '14px', padding: '0' }}
                                  >✕</button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{borderTop: '2px solid #E8E7E3', background: '#F9FAFB'}}>
                          <td style={{padding: '6px 4px', fontSize: '11px', color: '#73726C', fontWeight: '600'}}>TOTAL</td>
                          <td style={{padding: '6px 4px', textAlign: 'right', fontWeight: '700', fontSize: '12px'}}>
                            {(validationForm.lignes_tva.reduce((s, l) => s + (parseFloat(l.base_ht) || 0), 0)).toFixed(2)} €
                          </td>
                          <td style={{padding: '6px 4px', textAlign: 'right', fontWeight: '700', fontSize: '12px', color: '#4338CA'}}>
                            {(validationForm.lignes_tva.reduce((s, l) => s + (parseFloat(l.montant_tva) || 0), 0)).toFixed(2)} €
                          </td>
                          <td colSpan={selectedDocument.statut !== 'traite' || isExerciseClosed ? (selectedDocument.statut !== 'traite' ? 2 : 1) : 2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div style={{fontSize: '12px', color: '#9CA3AF', textAlign: 'center', padding: '12px 0'}}>
                    Aucune ligne TVA détectée automatiquement — utilisez "+&nbsp;Ajouter taux" pour ventiler manuellement
                  </div>
                )}
              </div>

              {/* Fournisseur */}
              <div style={{marginBottom: '16px'}}>
                <label style={{fontSize: '12px', color: '#73726C', marginBottom: '4px', display: 'block'}}>Fournisseur</label>
                <input
                  type="text"
                  value={validationForm.fournisseur}
                  onChange={(e) => handleFormFieldChange('fournisseur', e.target.value)}
                  disabled={selectedDocument.statut === 'traite' || isExerciseClosed}
                  id={`validation_fournisseur_${selectedDocument.id}`}
                  placeholder="Nom du fournisseur"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #E8E7E3',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    background: (selectedDocument.statut === 'traite' || isExerciseClosed) ? '#F3F4F6' : 'white',
                    color: (selectedDocument.statut === 'traite' || isExerciseClosed) ? '#6B7280' : 'inherit'
                  }}
                />
              </div>

              {/* Calcul automatique du TTC */}
              <div style={{
                background: '#E1F5EE',
                padding: '12px',
                borderRadius: '6px',
                marginTop: '8px',
                fontSize: '13px',
                color: '#0F6E56',
                textAlign: 'center'
              }}>
                💡 TTC calculé : HT + TVA = {
                  (parseFloat(validationForm.montant_ht || 0) + parseFloat(validationForm.tva || 0)).toFixed(2)
                } €
              </div>
            </div>

            {/* SECTION ANALYSE DE CONFORMITÉ */}
            {(() => {
              if (selectedDocumentConformite) {
                return (
              <div style={{marginTop: '24px'}}>
                {/* 1. IDENTITÉ CLIENT (PRIORITÉ ABSOLUE) */}
                {selectedDocumentConformite.verification_client && (
                  <div style={{
                    background: selectedDocumentConformite.verification_client.client_trouve ? '#E1F5EE' : '#FEE2E2',
                    border: `2px solid ${selectedDocumentConformite.verification_client.client_trouve ? '#5DCAA5' : '#A32D2D'}`,
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      fontWeight: '600',
                      marginBottom: '8px',
                      color: selectedDocumentConformite.verification_client.client_trouve ? '#0F6E56' : '#A32D2D',
                      fontSize: '14px'
                    }}>
                      🔍 IDENTITÉ CLIENT
                    </div>
                    {selectedDocumentConformite.verification_client.client_trouve ? (
                      <div style={{color: '#0F6E56', fontSize: '13px', lineHeight: '1.5'}}>
                        ✅ <strong>Facture établie au nom de :</strong><br/>
                        {selectedDocumentConformite.verification_client.client_nom}
                        {selectedDocumentConformite.verification_client.nom_trouve && (
                          <span style={{fontSize: '11px', color: '#0F6E56', marginLeft: '8px'}}>
                            (nom trouvé dans la facture)
                          </span>
                        )}
                        {selectedDocumentConformite.verification_client.siret_trouve && (
                          <span style={{fontSize: '11px', color: '#0F6E56', marginLeft: '8px'}}>
                            (SIRET trouvé)
                          </span>
                        )}
                      </div>
                    ) : (
                      <div style={{color: '#A32D2D', fontSize: '13px', lineHeight: '1.5'}}>
                        🚨 <strong>ATTENTION :</strong><br/>
                        {selectedDocumentConformite.verification_client.alerte}
                      </div>
                    )}
                  </div>
                )}

                {/* 2. TYPE DE DOCUMENT DÉTECTÉ */}
                {selectedDocumentConformite.type_label && (
                  <div style={{
                    background: '#F3F4F6',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '13px'
                  }}>
                    <div style={{fontWeight: '600', marginBottom: '4px', color: '#73726C'}}>
                      📄 Type de document détecté
                    </div>
                    <div style={{color: '#2D2D2D'}}>
                      {selectedDocumentConformite.type === 'TYPE_1_B2B' && '🏢 Facture B2B (Règles Article L441-9)'}
                      {selectedDocumentConformite.type === 'TYPE_2_TICKET' && '🧾 Ticket de caisse / Facture comptoir'}
                      {selectedDocumentConformite.type === 'TYPE_3_FRAIS' && '🚗 Note de frais / Restaurant'}
                      {!selectedDocumentConformite.type && selectedDocumentConformite.type_label}
                    </div>
                  </div>
                )}

                {/* 2.5. MESSAGES SPÉCIFIQUES TICKETS */}
                {selectedDocumentConformite.message_pas_facture && (
                  <div style={{
                    background: '#FEF3C7',
                    border: '2px solid #F59E0B',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '13px'
                  }}>
                    <div style={{fontWeight: '600', marginBottom: '4px', color: '#B45309'}}>
                      ⚠️ Attention
                    </div>
                    <div style={{color: '#2D2D2D', lineHeight: '1.5'}}>
                      {selectedDocumentConformite.message_pas_facture}
                    </div>
                  </div>
                )}

                {selectedDocumentConformite.message_ticket && (
                  <div style={{
                    background: '#E0F2FE',
                    border: '2px solid #0EA5E9',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '13px'
                  }}>
                    <div style={{fontWeight: '600', marginBottom: '4px', color: '#0369A1'}}>
                      ℹ️ Information
                    </div>
                    <div style={{color: '#2D2D2D', lineHeight: '1.5'}}>
                      {selectedDocumentConformite.message_ticket}
                    </div>
                  </div>
                )}

                {/* 3. SCORE DE CONFORMITÉ */}
                <div style={{
                  background: '#FAF9F5',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <div style={{fontWeight: '600', marginBottom: '12px', color: '#73726C', fontSize: '14px'}}>
                    📊 Score de conformité
                  </div>

                  {/* Barre de progression */}
                  <div style={{marginBottom: '12px'}}>
                    <div style={{
                      width: '100%',
                      height: '24px',
                      background: '#E8E7E3',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <div style={{
                        width: `${selectedDocumentConformite.score}%`,
                        height: '100%',
                        background: selectedDocumentConformite.conforme && selectedDocumentConformite.au_nom_client
                          ? 'linear-gradient(90deg, #5DCAA5 0%, #0F6E56 100%)'
                          : 'linear-gradient(90deg, #FEE2E2 0%, #A32D2D 100%)',
                        transition: 'width 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: '700',
                        color: 'white'
                      }}>
                        {selectedDocumentConformite.score >= 50 && `${selectedDocumentConformite.score}%`}
                      </div>
                    </div>
                  </div>

                  {/* Détail score */}
                  <div style={{fontSize: '13px', color: '#2D2D2D', textAlign: 'center', marginBottom: '12px'}}>
                    <strong>{selectedDocumentConformite.score}/100</strong> —
                    {selectedDocumentConformite.champs_presents} champs présents /
                    {selectedDocumentConformite.total_champs} champs requis
                  </div>

                  {/* Taux TVA inféré */}
                  {selectedDocumentConformite.taux_tva_inferent !== undefined && selectedDocumentConformite.taux_tva_inferent !== null && (
                    <div style={{
                      background: selectedDocumentConformite.tva_recuperable ? '#E1F5EE' : '#F3F4F6',
                      padding: '8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      textAlign: 'center',
                      color: selectedDocumentConformite.tva_recuperable ? '#0F6E56' : '#73726C'
                    }}>
                      💡 Taux TVA inféré depuis les montants : <strong>{selectedDocumentConformite.taux_tva_inferent}%</strong>
                      {selectedDocumentConformite.tva_recuperable && ' ✅ Récupérable'}
                    </div>
                  )}

                  {/* Qualité OCR */}
                  {selectedDocumentConformite.qualite_ocr && (
                    <div style={{
                      background: selectedDocumentConformite.qualite_ocr.alerte ? '#FEE2E2' : '#E1F5EE',
                      padding: '8px',
                      borderRadius: '6px',
                      marginTop: '8px',
                      fontSize: '12px'
                    }}>
                      <div style={{fontWeight: '500', marginBottom: '4px', color: selectedDocumentConformite.qualite_ocr.alerte ? '#A32D2D' : '#0F6E56'}}>
                        🔍 Qualité OCR : {selectedDocumentConformite.qualite_ocr.niveau || 'N/A'}
                      </div>
                      <div style={{color: selectedDocumentConformite.qualite_ocr.alerte ? '#A32D2D' : '#0F6E56'}}>
                        {selectedDocumentConformite.qualite_ocr.message}
                      </div>
                    </div>
                  )}
                </div>

                {/* 4. CHAMPS OBLIGATOIRES */}
                {selectedDocumentConformite.champs_obligatoires && (
                  <div style={{
                    background: '#FAF9F5',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}>
                    <div style={{fontWeight: '600', marginBottom: '12px', color: '#73726C', fontSize: '14px'}}>
                      📋 Checklist des champs obligatoires
                    </div>

                    {/* Liste des champs avec labels lisibles */}
                    <div style={{fontSize: '12px'}}>
                      {Object.entries(selectedDocumentConformite.champs_obligatoires).map(([champ, present], idx) => {
                        // Labels lisibles pour chaque champ
                        const champsLabels = {
                          'numero_facture': 'Numéro de facture',
                          'date': 'Date d\'émission',
                          'date_emission': 'Date d\'émission',
                          'siret': 'SIRET du vendeur (14 chiffres)',
                          'siren': 'SIREN du vendeur (9 chiffres)',
                          'montant_ttc': 'Montant TTC',
                          'montant_ht': 'Montant HT',
                          'montants_ht': 'Montant HT',
                          'taux_tva': 'Taux de TVA',
                          'vendeur_nom': 'Nom du vendeur',
                          'vendeur_adresse': 'Adresse complète du vendeur',
                          'vendeur_siret': 'SIRET du vendeur',
                          'client_nom': 'Nom du client',
                          'client_adresse': 'Adresse du client',
                          'description_prestations': 'Description des produits/services'
                        };

                        const label = champsLabels[champ] || champ.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                        return (
                          <div key={idx} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 0',
                            borderBottom: idx < Object.keys(selectedDocumentConformite.champs_obligatoires).length - 1 ? '1px solid #E8E7E3' : 'none',
                            background: present ? 'transparent' : '#FEF2F2'
                          }}>
                            <span style={{
                              fontSize: '16px',
                              marginRight: '10px',
                              width: '24px',
                              textAlign: 'center'
                            }}>
                              {present ? '✅' : '❌'}
                            </span>
                            <span style={{
                              color: present ? '#0F6E56' : '#A32D2D',
                              flex: 1,
                              fontWeight: present ? '400' : '500'
                            }}>
                              {label}
                            </span>
                            {!present && (
                              <span style={{
                                fontSize: '11px',
                                color: '#A32D2D',
                                fontStyle: 'italic',
                                marginLeft: '8px'
                              }}>
                                (manquant)
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Résumé des champs manquants */}
                    {selectedDocumentConformite.score < 100 && (
                      <div style={{
                        marginTop: '12px',
                        padding: '10px',
                        background: selectedDocumentConformite.conforme && selectedDocumentConformite.au_nom_client
                          ? '#E1F5EE'
                          : '#FEE2E2',
                        borderRadius: '6px',
                        fontSize: '12px',
                        textAlign: 'center',
                        color: selectedDocumentConformite.conforme && selectedDocumentConformite.au_nom_client
                          ? '#0F6E56'
                          : '#A32D2D',
                        fontWeight: '500'
                      }}>
                        {!selectedDocumentConformite.au_nom_client && '🚨 Document non établi au nom de votre société'}
                        {selectedDocumentConformite.au_nom_client && !selectedDocumentConformite.conforme &&
                          `🚨 ${(selectedDocumentConformite.champs_manquants || []).length} champ(s) manquant(s)`
                        }
                        {selectedDocumentConformite.au_nom_client && selectedDocumentConformite.conforme &&
                          '✅ Tous les champs obligatoires sont présents'
                        }
                      </div>
                    )}
                  </div>
                )}

                {/* 5. RECOMMANDATION FINALE (basée sur conforme, pas score) */}
                <div style={{
                  background: selectedDocumentConformite.conforme && selectedDocumentConformite.au_nom_client
                    ? '#E1F5EE'
                    : '#FEE2E2',
                  border: `2px solid ${
                    selectedDocumentConformite.conforme && selectedDocumentConformite.au_nom_client
                      ? '#5DCAA5'
                      : '#A32D2D'
                  }`,
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: selectedDocumentConformite.conforme && selectedDocumentConformite.au_nom_client
                      ? '#0F6E56'
                      : '#A32D2D',
                    fontSize: '14px'
                  }}>
                    {selectedDocumentConformite.conforme && selectedDocumentConformite.au_nom_client && '✅ Recommandation finale'}
                    {(!selectedDocumentConformite.conforme || !selectedDocumentConformite.au_nom_client) && '🚨 Recommandation finale'}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    lineHeight: '1.5',
                    color: selectedDocumentConformite.conforme && selectedDocumentConformite.au_nom_client
                      ? '#0F6E56'
                      : '#A32D2D'
                  }}>
                    {!selectedDocumentConformite.au_nom_client && (
                      <div>
                        <strong>🚨 {selectedDocumentConformite.message_alerte || 'Document non établi au nom de votre société'}</strong><br/>
                        Ce document ne peut pas être comptabilisé en l'état. Vérifiez qu'il s'agit bien d'une facture adressée à votre entreprise.
                      </div>
                    )}
                    {selectedDocumentConformite.au_nom_client && !selectedDocumentConformite.conforme && (
                      <div>
                        <strong>🚨 Document non conforme — ne pas comptabiliser en l'état</strong><br/>
                        Des informations obligatoires sont manquantes. Contactez le fournisseur ou vérifiez le document.
                      </div>
                    )}
                    {selectedDocumentConformite.au_nom_client && selectedDocumentConformite.conforme && (
                      <strong>✅ Document conforme — vous pouvez valider</strong>
                    )}
                  </div>
                </div>
              </div>
                );
              } else {
                return (
              <div style={{
                background: '#F3F4F6',
                padding: '16px',
                borderRadius: '8px',
                marginTop: '24px',
                textAlign: 'center',
                fontSize: '13px',
                color: '#9CA3AF'
              }}>
                ⏳ Analyse de conformité non disponible
              </div>
                );
              }
            })()}

            {/* Boutons d'action */}
            <div style={{display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center'}}>
              <button
                onClick={() => supprimerDocument(selectedDocument.id)}
                disabled={validationLoading || isExerciseClosed}
                style={{
                  padding: '10px 20px',
                  border: isExerciseClosed ? '1px solid #D1D5DB' : '1px solid #D85A30',
                  background: isExerciseClosed ? '#F3F4F6' : 'white',
                  color: isExerciseClosed ? '#9CA3AF' : '#D85A30',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: (validationLoading || isExerciseClosed) ? 'not-allowed' : 'pointer',
                  opacity: (validationLoading || isExerciseClosed) ? 0.6 : 1
                }}
              >
                🗑️ Supprimer
              </button>
              <div style={{display: 'flex', gap: '12px'}}>
                <button
                  onClick={() => {
                    setShowValidationModal(false);
                    setSelectedDocument(null);

                    // Réinitialiser le formulaire
                    setValidationForm({
                      fournisseur: '',
                      montant_ht: 0,
                      tva: 0,
                      taux_tva: 20
                    });
                  }}
                  disabled={validationLoading}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #E8E7E3',
                    background: 'white',
                    color: '#2D2D2D',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: validationLoading ? 'not-allowed' : 'pointer',
                    opacity: validationLoading ? 0.6 : 1
                  }}
                >
                  Annuler
                </button>
                {selectedDocument.statut !== 'traite' && (
                  <button
                    onClick={async () => {
                      setValidationLoading(true);

                      await validerDocument(selectedDocument.id);

                      setValidationLoading(false);
                      setShowValidationModal(false);
                      setSelectedDocument(null);

                      // Réinitialiser le formulaire
                      setValidationForm({
                        fournisseur: '',
                        montant_ht: 0,
                        tva: 0,
                        taux_tva: 20
                      });
                    }}
                    disabled={validationLoading || isExerciseClosed}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      background: isExerciseClosed ? '#9CA3AF' : (validationLoading ? '#B45309' : '#1D9E75'),
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: (validationLoading || isExerciseClosed) ? 'not-allowed' : 'pointer',
                      opacity: (validationLoading || isExerciseClosed) ? 0.6 : 1
                    }}
                  >
                    {isExerciseClosed ? '🔒 Exercice clôturé' : (validationLoading ? 'Validation en cours...' : '✅ Valider le document')}
                  </button>
                )}
              </div>
            </div>

            {validationLoading && (
              <div style={{
                textAlign: 'center',
                marginTop: '16px',
                fontSize: '12px',
                color: '#73726C'
              }}>
                Création des écritures comptables en cours...
              </div>
            )}
          </div>
          )}
        </div>
      )}

      {/* Overlay de progression d'upload */}
      {uploadInProgress && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Icône de chargement */}
            <div style={{
              fontSize: '48px',
              marginBottom: '24px',
              animation: 'bounce 1s infinite'
            }}>
              📤
            </div>

            {/* Nom du fichier */}
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#2D2D2D',
              marginBottom: '8px',
              wordBreak: 'break-all'
            }}>
              {uploadFileName}
            </div>

            {/* Statut */}
            <div style={{
              fontSize: '13px',
              color: '#73726C',
              marginBottom: '20px'
            }}>
              {uploadStatus}
            </div>

            {/* Barre de progression */}
            <div style={{
              width: '100%',
              height: '8px',
              background: '#E8E7E3',
              borderRadius: '4px',
              overflow: 'hidden',
              marginBottom: '12px'
            }}>
              <div style={{
                width: `${uploadProgress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #1D9E75 0%, #0F6E56 100%)',
                transition: 'width 0.3s ease',
                borderRadius: '4px'
              }}></div>
            </div>

            {/* Pourcentage */}
            <div style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#1D9E75'
            }}>
              {uploadProgress}%
            </div>

            {/* Message informatif */}
            <div style={{
              fontSize: '11px',
              color: '#73726C',
              marginTop: '16px',
              lineHeight: '1.4'
            }}>
              Veuillez patienter pendant le traitement de votre document...
            </div>
          </div>
        </div>
      )}

      {/* Tooltip de conformité */}
      {conformiteTooltip && (
        <div style={{
          position: 'fixed',
          left: `${Math.min(conformiteTooltip.x, window.innerWidth - 320)}px`,
          top: `${conformiteTooltip.y + 20}px`,
          background: 'white',
          border: '1px solid #E8E7E3',
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 10000,
          maxWidth: '300px',
          fontSize: '13px'
        }}>
          {/* En-tête avec type de document */}
          <div style={{fontWeight: '600', marginBottom: '8px', color: '#2D2D2D'}}>
            {conformiteTooltip.conformite.type_label || 'Type inconnu'}
          </div>

          {/* Score de conformité */}
          <div style={{marginBottom: '12px'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
              <span style={{fontWeight: '500', color: '#73726C'}}>Score :</span>
              <span style={{
                fontWeight: '600',
                color: conformiteTooltip.conformite.score >= 80 ? '#0F6E56' :
                       conformiteTooltip.conformite.score >= 50 ? '#B45309' : '#A32D2D'
              }}>
                {conformiteTooltip.conformite.score}/100
              </span>
            </div>
            {conformiteTooltip.conformite.champs_presents !== undefined && (
              <div style={{fontSize: '11px', color: '#73726C'}}>
                {conformiteTooltip.conformite.champs_presents}/{conformiteTooltip.conformite.total_champs} champs présents
              </div>
            )}
          </div>

          {/* Alerte identité client si applicable */}
          {conformiteTooltip.conformite.verification_client &&
           conformiteTooltip.conformite.verification_client.alerte && (
            <div style={{
              padding: '8px',
              background: '#FEE2E2',
              borderRadius: '4px',
              fontSize: '11px',
              color: '#A32D2D',
              marginBottom: '12px',
              lineHeight: '1.4'
            }}>
              {conformiteTooltip.conformite.verification_client.alerte}
            </div>
          )}

          {/* Champs manquants */}
          {conformiteTooltip.conformite.champs_manquants &&
           conformiteTooltip.conformite.champs_manquants.length > 0 && (
            <div>
              <div style={{fontWeight: '500', marginBottom: '4px', color: '#73726C'}}>
                Champs manquants :
              </div>
              <ul style={{
                margin: 0,
                paddingLeft: '16px',
                fontSize: '11px',
                color: '#A32D2D'
              }}>
                {conformiteTooltip.conformite.champs_manquants.map((champ, idx) => (
                  <li key={idx}>{champ}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Bouton fermer */}
          <button
            onClick={() => setConformiteTooltip(null)}
            style={{
              marginTop: '12px',
              padding: '6px 12px',
              background: '#F3F4F6',
              border: 'none',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '500',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            Fermer
          </button>
        </div>
      )}

      {/* Overlay pour fermer le tooltip au clic extérieur */}
      {conformiteTooltip && (
        <div
          onClick={() => setConformiteTooltip(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999
          }}
        />
      )}

      {/* MODAL MISE A JOUR */}
      {showUpdateModal && updateInfo && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{fontSize: '18px', fontWeight: '700', margin: '0 0 12px', color: '#2D2D2D'}}>
              🚀 Mise à jour disponible (v{updateInfo.latest_version})
            </h3>
            <p style={{fontSize: '14px', color: '#73726C', marginBottom: '16px'}}>
              Votre version actuelle : <strong>v{updateInfo.current_version}</strong>
            </p>
            <div style={{
              background: '#FAF9F5',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #E8E7E3',
              maxHeight: '180px',
              overflowY: 'auto',
              fontSize: '13px',
              color: '#2D2D2D',
              whiteSpace: 'pre-line',
              marginBottom: '20px',
              lineHeight: '1.5'
            }}>
              {updateInfo.changelog}
            </div>
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px'}}>
              <button 
                onClick={() => setShowUpdateModal(false)}
                disabled={updateApplying}
                style={{
                  padding: '10px 16px',
                  background: '#F5F4ED',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Plus tard
              </button>
              <button 
                onClick={handleApplyUpdate}
                disabled={updateApplying}
                style={{
                  padding: '10px 16px',
                  background: '#1D9E75',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                {updateApplying ? 'Téléchargement...' : 'Télécharger et Installer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL / POPUP DE SURCLASSEMENT (QUOTAS OU MODULES BLOQUÉS) */}
      {upgradePopup.show && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 3000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px', fontFamily: 'Inter, system-ui, sans-serif'
        }} onClick={() => setUpgradePopup(prev => ({...prev, show: false}))}>
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', padding: '32px', borderRadius: '16px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
              maxWidth: '480px', width: '100%', textAlign: 'center'
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚀</div>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1F2937', margin: '0 0 12px' }}>
              {upgradePopup.title}
            </h3>
            <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: '1.6', margin: '0 0 24px' }}>
              {upgradePopup.message}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button 
                onClick={() => setUpgradePopup(prev => ({...prev, show: false}))}
                style={{
                  padding: '10px 18px', background: '#F3F4F6', color: '#4B5563',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontSize: '13px', fontWeight: '600'
                }}
              >
                Plus tard
              </button>
              <button 
                onClick={() => {
                  setUpgradePopup(prev => ({...prev, show: false}));
                  setActiveSection('abonnement');
                }}
                style={{
                  padding: '10px 18px', background: '#1D9E75', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontSize: '13px', fontWeight: '600'
                }}
              >
                🚀 Découvrir les offres
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SYNC MOBILE DEPUIS LE DASHBOARD */}
      {showMobileSyncModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 3000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px', fontFamily: 'Inter, system-ui, sans-serif'
        }} onClick={() => setShowMobileSyncModal(false)}>
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', padding: '28px', borderRadius: '16px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
              maxWidth: '480px', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center'
            }}
          >
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>📲</div>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1F2937', margin: '0 0 8px', textAlign: 'center' }}>
              Relier votre Smartphone
            </h3>
            <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 20px', textAlign: 'center', lineHeight: '1.4' }}>
              Une fois votre téléphone enregistré, vous pourrez prendre en photo vos reçus et les envoyer à votre comptabilité en un instant !
            </p>

            <div style={{ background: '#F9FAFB', padding: '16px', borderRadius: '12px', border: '1px solid #E5E7EB', width: '100%', boxSizing: 'border-box', marginBottom: '20px' }}>
              <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#374151', lineHeight: '1.5' }}>
                1. Scannez ce code QR avec l'appareil photo de votre smartphone :
              </p>
              <div style={{ background: 'white', padding: '8px', borderRadius: '8px', border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '136px', height: '136px', margin: '0 auto 16px' }}>
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(`${window.location.origin}/mobile`)}`} 
                  alt="QR Code Mobile Sync" 
                  style={{ width: '120px', height: '120px', display: 'block' }} 
                />
              </div>
              <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#374151', lineHeight: '1.5' }}>
                2. Saisissez votre identifiant client unique sur votre téléphone :
              </p>
              <div style={{ background: '#E1F5EE', color: '#0F6E56', padding: '8px 12px', borderRadius: '6px', fontSize: '14px', fontWeight: '700', textAlign: 'center', fontFamily: 'monospace' }}>
                {clientId}
              </div>
            </div>

            <button 
              onClick={() => setShowMobileSyncModal(false)}
              style={{
                width: '100%', padding: '12px', background: '#1D9E75', color: 'white',
                border: 'none', borderRadius: '8px', cursor: 'pointer',
                fontSize: '14px', fontWeight: '600', transition: 'background 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#0F6E56'}
              onMouseLeave={e => e.currentTarget.style.background = '#1D9E75'}
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrapper avec ToastProvider
const ComptableDashboard = ({ clientId, clientNom, onLogout }) => {
  return (
    <ToastProvider>
      <ComptableDashboardInner clientId={clientId} clientNom={clientNom} onLogout={onLogout} />
    </ToastProvider>
  );
};

// ============================================================================
// COMPOSANT INVENTAIRE & IMMOBILISATIONS
// ============================================================================
const InventaireSection = ({ clientId, token }) => {
  const [tab, setTab] = useState('immos');   // 'immos'|'cca'|'fnp'|'provisions'
  const [exercice] = useState(new Date().getFullYear());
  const [immos, setImmos] = useState([]);
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [newImmo, setNewImmo] = useState({
    libelle:'', date_acquisition:'', montant_ht:'', duree_annees:'5', type_amortissement:'lineaire',
    compte_immo:'2183', compte_amort:'28183', compte_dotation:'68112'
  });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadImmos = async () => {
    try {
      const r = await fetch('/inventaire/immobilisations', { headers });
      const d = await r.json();
      if (d.success) setImmos(d.data);
    } catch(e) {}
  };

  useEffect(() => { loadImmos(); }, []);

  const simulerAmorts = async () => {
    setLoading(true);
    try {
      const r = await fetch(`/inventaire/amortissements/simuler?exercice=${exercice}`, { headers });
      const d = await r.json();
      setSimulation(d);
    } finally { setLoading(false); }
  };

  const validerAmorts = async (ids) => {
    setLoading(true);
    try {
      const r = await fetch(`/inventaire/amortissements/valider?exercice=${exercice}`, {
        method: 'POST', headers,
        body: JSON.stringify(ids)
      });
      const d = await r.json();
      setMsg(d.message);
      setSimulation(null);
    } finally { setLoading(false); }
  };

  const ajouterImmo = async () => {
    setLoading(true);
    try {
      const r = await fetch('/inventaire/immobilisations', {
        method: 'POST', headers,
        body: JSON.stringify({...newImmo, montant_ht: parseFloat(newImmo.montant_ht), duree_annees: parseInt(newImmo.duree_annees)})
      });
      const d = await r.json();
      if (d.success) { setMsg('Immobilisation ajoutée !'); loadImmos(); setNewImmo({libelle:'',date_acquisition:'',montant_ht:'',duree_annees:'5',type_amortissement:'lineaire',compte_immo:'2183',compte_amort:'28183',compte_dotation:'68112'}); }
    } finally { setLoading(false); }
  };

  const cardStyle = {background:'white',padding:'24px',borderRadius:'12px',border:'1px solid #E8E7E3',marginBottom:'16px'};
  const tabStyle = (active) => ({padding:'8px 16px',borderRadius:'8px',border:'none',cursor:'pointer',fontWeight:'500',fontSize:'13px',background:active?'#0F6E56':'#F5F4ED',color:active?'white':'#2D2D2D'});

  return (
    <div>
      <div style={{...cardStyle, marginBottom:'16px'}}>
        <h2 style={{fontSize:'20px',fontWeight:'600',marginBottom:'4px'}}>📦 Immobilisations & Inventaire</h2>
        <p style={{color:'#73726C',fontSize:'13px'}}>Gérez vos actifs et générez les écritures de fin d'exercice ({exercice})</p>
        <div style={{display:'flex',gap:'8px',marginTop:'16px',flexWrap:'wrap'}}>
          {[['immos','📋 Immobilisations'],['cca','🔄 CCA'],['fnp','🔍 FNP'],['provisions','⚠️ Provisions']].map(([id,label]) => (
            <button key={id} style={tabStyle(tab===id)} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>
      </div>

      {msg && <div style={{background:'#E1F5EE',color:'#0F6E56',padding:'12px 16px',borderRadius:'8px',marginBottom:'16px',fontWeight:'500'}}>{msg} <button onClick={() => setMsg(null)} style={{float:'right',background:'none',border:'none',cursor:'pointer',color:'#0F6E56'}}>✕</button></div>}

      {/* LISTE IMMOBILISATIONS */}
      {tab === 'immos' && (
        <div>
          <div style={cardStyle}>
            <h3 style={{fontWeight:'600',marginBottom:'16px'}}>➕ Nouvelle immobilisation</h3>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <input placeholder="Libellé (ex: Ordinateur Dell)" value={newImmo.libelle} onChange={e => setNewImmo({...newImmo, libelle:e.target.value})} style={{padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px'}} />
              <input type="date" placeholder="Date d'acquisition" value={newImmo.date_acquisition} onChange={e => setNewImmo({...newImmo, date_acquisition:e.target.value})} style={{padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px'}} />
              <input type="number" placeholder="Montant HT (€)" value={newImmo.montant_ht} onChange={e => setNewImmo({...newImmo, montant_ht:e.target.value})} style={{padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px'}} />
              <select value={newImmo.duree_annees} onChange={e => setNewImmo({...newImmo, duree_annees:e.target.value})} style={{padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px'}}>
                {[3,4,5,6,7,10,15,20].map(d => <option key={d} value={d}>{d} ans</option>)}
              </select>
              <select value={newImmo.type_amortissement} onChange={e => setNewImmo({...newImmo, type_amortissement:e.target.value})} style={{padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px'}}>
                <option value="lineaire">Linéaire</option>
                <option value="degressif">Dégressif</option>
              </select>
              <input placeholder="Compte immo (ex: 2183)" value={newImmo.compte_immo} onChange={e => setNewImmo({...newImmo, compte_immo:e.target.value})} style={{padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px'}} />
            </div>
            <button onClick={ajouterImmo} disabled={loading || !newImmo.libelle || !newImmo.date_acquisition || !newImmo.montant_ht} style={{marginTop:'12px',padding:'10px 20px',background:'#0F6E56',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'500',opacity:loading?0.7:1}}>
              {loading ? 'Enregistrement...' : '✅ Ajouter l\'immobilisation'}
            </button>
          </div>

          <div style={cardStyle}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
              <h3 style={{fontWeight:'600'}}>Tableau des immobilisations</h3>
              <button onClick={simulerAmorts} disabled={loading} style={{padding:'8px 16px',background:'#0891B2',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'500',fontSize:'13px'}}>
                🧮 Simuler les dotations {exercice}
              </button>
            </div>
            {immos.length === 0 ? <p style={{color:'#73726C',textAlign:'center',padding:'24px'}}>Aucune immobilisation enregistrée</p> : (
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
                <thead><tr style={{borderBottom:'2px solid #E8E7E3'}}>
                  {['Libellé','Date acq.','Montant HT','Durée','Type amort.','VNC actuelle'].map(h => <th key={h} style={{textAlign:'left',padding:'8px',color:'#73726C',fontWeight:'500'}}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {immos.map((im, i) => (
                    <tr key={im.id} style={{borderBottom:'1px solid #F5F4ED',background:i%2?'#FAFAF8':'white'}}>
                      <td style={{padding:'8px'}}>{im.libelle}</td>
                      <td style={{padding:'8px'}}>{im.date_acquisition}</td>
                      <td style={{padding:'8px'}}>{parseFloat(im.montant_ht).toFixed(2)} €</td>
                      <td style={{padding:'8px'}}>{im.duree_annees} ans</td>
                      <td style={{padding:'8px'}}><span style={{background:im.type_amortissement==='degressif'?'#EFF6FF':'#F0FDF4',color:im.type_amortissement==='degressif'?'#1D4ED8':'#166534',padding:'2px 8px',borderRadius:'12px',fontSize:'11px'}}>{im.type_amortissement}</span></td>
                      <td style={{padding:'8px',fontWeight:'600',color:im.totalement_amorti?'#9CA3AF':'#0F6E56'}}>{im.totalement_amorti ? 'Amorti' : `${parseFloat(im.valeur_nette_comptable).toFixed(2)} €`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* SIMULATION DOTATIONS */}
          {simulation && (
            <div style={{...cardStyle, border:'2px solid #0891B2'}}>
              <h3 style={{fontWeight:'600',color:'#0891B2',marginBottom:'16px'}}>🧮 Simulation des dotations {exercice} — À valider</h3>
              <p style={{color:'#73726C',fontSize:'13px',marginBottom:'16px'}}>Total proposé : <strong>{simulation.total_dotations?.toFixed(2)} €</strong> — Ces écritures ne sont PAS encore enregistrées.</p>
              {simulation.propositions?.map(p => (
                <div key={p.immobilisation_id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px',background:'#F0FAFF',borderRadius:'8px',marginBottom:'8px'}}>
                  <div>
                    <div style={{fontWeight:'500'}}>{p.libelle}</div>
                    <div style={{fontSize:'12px',color:'#73726C'}}>{p.ecriture_proposee}</div>
                  </div>
                  <strong style={{color:'#0891B2'}}>{p.dotation_calculee.toFixed(2)} €</strong>
                </div>
              ))}
              <div style={{display:'flex',gap:'12px',marginTop:'16px'}}>
                <button onClick={() => validerAmorts(simulation.propositions.map(p => p.immobilisation_id))} style={{flex:1,padding:'10px',background:'#0F6E56',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'600'}}>
                  ✅ Valider et enregistrer les écritures
                </button>
                <button onClick={() => setSimulation(null)} style={{padding:'10px 16px',background:'#F5F4ED',color:'#2D2D2D',border:'none',borderRadius:'8px',cursor:'pointer'}}>
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CCA */}
      {tab === 'cca' && <CCATab exercice={exercice} headers={headers} setMsg={setMsg} />}

      {/* FNP */}
      {tab === 'fnp' && <FNPTab exercice={exercice} headers={headers} setMsg={setMsg} />}

      {/* PROVISIONS */}
      {tab === 'provisions' && <ProvisionsTab exercice={exercice} headers={headers} setMsg={setMsg} />}
    </div>
  );
};

// Onglet CCA
const CCATab = ({ exercice, headers, setMsg }) => {
  const [propositions, setPropositions] = useState(null);
  const [selection, setSelection] = useState({});
  const [loading, setLoading] = useState(false);

  const simuler = async () => {
    setLoading(true);
    try {
      const r = await fetch(`/inventaire/cca/simuler?exercice=${exercice}`, { headers });
      const d = await r.json();
      if (d.success) {
        setPropositions(d.propositions);
        const sel = {};
        d.propositions.forEach(p => { sel[p.ecriture_id] = { valide: true, montant: p.montant_cca_propose }; });
        setSelection(sel);
      }
    } finally { setLoading(false); }
  };

  const valider = async () => {
    setLoading(true);
    try {
      const items = Object.entries(selection).map(([id, v]) => ({ ecriture_id: id, montant_cca: v.montant, valide: v.valide }));
      const r = await fetch(`/inventaire/cca/valider?exercice=${exercice}`, { method:'POST', headers, body: JSON.stringify(items) });
      const d = await r.json();
      setMsg(d.message);
      setPropositions(null);
    } finally { setLoading(false); }
  };

  return (
    <div style={{background:'white',padding:'24px',borderRadius:'12px',border:'1px solid #E8E7E3'}}>
      <h3 style={{fontWeight:'600',marginBottom:'8px'}}>🔄 Charges Constatées d'Avance (CCA)</h3>
      <p style={{color:'#73726C',fontSize:'13px',marginBottom:'16px'}}>Détection automatique des charges nov/déc à reporter sur N+1 (loyers, abonnements, assurances...)</p>
      {!propositions ? (
        <button onClick={simuler} disabled={loading} style={{padding:'10px 20px',background:'#0891B2',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'500'}}>
          {loading ? 'Analyse en cours...' : '🔍 Analyser les charges à constater'}
        </button>
      ) : (
        <div>
          {propositions.length === 0 ? <p style={{color:'#73726C'}}>Aucune CCA détectée pour {exercice}</p> : (
            <>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px',marginBottom:'16px'}}>
                <thead><tr style={{borderBottom:'2px solid #E8E7E3'}}>
                  {['Libellé','Montant payé','Part N+1 proposée','Valider ?'].map(h => <th key={h} style={{textAlign:'left',padding:'8px',color:'#73726C',fontWeight:'500'}}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {propositions.map(p => (
                    <tr key={p.ecriture_id} style={{borderBottom:'1px solid #F5F4ED'}}>
                      <td style={{padding:'8px'}}>{p.libelle}</td>
                      <td style={{padding:'8px'}}>{p.montant_total.toFixed(2)} €</td>
                      <td style={{padding:'8px'}}>
                        <input type="number" value={selection[p.ecriture_id]?.montant || p.montant_cca_propose}
                          onChange={e => setSelection({...selection, [p.ecriture_id]: {...selection[p.ecriture_id], montant: parseFloat(e.target.value)}})}
                          style={{width:'80px',padding:'4px 8px',border:'1px solid #E8E7E3',borderRadius:'6px'}} /> €
                      </td>
                      <td style={{padding:'8px'}}>
                        <input type="checkbox" checked={selection[p.ecriture_id]?.valide !== false}
                          onChange={e => setSelection({...selection, [p.ecriture_id]: {...selection[p.ecriture_id], valide: e.target.checked}})} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{display:'flex',gap:'12px'}}>
                <button onClick={valider} disabled={loading} style={{padding:'10px 20px',background:'#0F6E56',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'600'}}>✅ Enregistrer les CCA sélectionnées</button>
                <button onClick={() => setPropositions(null)} style={{padding:'10px 16px',background:'#F5F4ED',color:'#2D2D2D',border:'none',borderRadius:'8px',cursor:'pointer'}}>Annuler</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Onglet FNP
const FNPTab = ({ exercice, headers, setMsg }) => {
  const [propositions, setPropositions] = useState(null);
  const [montants, setMontants] = useState({});
  const [selection, setSelection] = useState({});
  const [loading, setLoading] = useState(false);

  const simuler = async () => {
    setLoading(true);
    try {
      const r = await fetch(`/inventaire/fnp/simuler?exercice=${exercice}`, { headers });
      const d = await r.json();
      if (d.success) {
        setPropositions(d.propositions);
        const m = {}, s = {};
        d.propositions.forEach(p => { m[p.fournisseur] = p.montant_estime_ht; s[p.fournisseur] = true; });
        setMontants(m); setSelection(s);
      }
    } finally { setLoading(false); }
  };

  const valider = async () => {
    setLoading(true);
    try {
      const items = propositions.filter(p => selection[p.fournisseur]).map(p => ({
        fournisseur: p.fournisseur, montant_fnp_ht: montants[p.fournisseur] || p.montant_estime_ht,
        montant_fnp_tva: (montants[p.fournisseur] || p.montant_estime_ht) * 0.20,
        compte_charge: p.compte_charge, valide: true
      }));
      const r = await fetch(`/inventaire/fnp/valider?exercice=${exercice}`, { method:'POST', headers, body: JSON.stringify(items) });
      const d = await r.json();
      setMsg(d.message);
      setPropositions(null);
    } finally { setLoading(false); }
  };

  return (
    <div style={{background:'white',padding:'24px',borderRadius:'12px',border:'1px solid #E8E7E3'}}>
      <h3 style={{fontWeight:'600',marginBottom:'8px'}}>🔍 Factures Non Parvenues (FNP)</h3>
      <p style={{color:'#73726C',fontSize:'13px',marginBottom:'16px'}}>Détection des fournisseurs habituels sans facture en décembre {exercice}</p>
      {!propositions ? (
        <button onClick={simuler} disabled={loading} style={{padding:'10px 20px',background:'#0891B2',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'500'}}>
          {loading ? 'Analyse en cours...' : '🔍 Détecter les FNP'}
        </button>
      ) : propositions.length === 0 ? <p style={{color:'#73726C'}}>Aucune FNP détectée pour {exercice}</p> : (
        <div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px',marginBottom:'16px'}}>
            <thead><tr style={{borderBottom:'2px solid #E8E7E3'}}>
              {['Fournisseur','Nb factures hist.','Montant estimé HT (modifiable)','Valider ?'].map(h => <th key={h} style={{textAlign:'left',padding:'8px',color:'#73726C',fontWeight:'500'}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {propositions.map(p => (
                <tr key={p.fournisseur} style={{borderBottom:'1px solid #F5F4ED'}}>
                  <td style={{padding:'8px',fontWeight:'500'}}>{p.fournisseur}</td>
                  <td style={{padding:'8px',textAlign:'center'}}>{p.nb_factures_historique}</td>
                  <td style={{padding:'8px'}}>
                    <input type="number" value={montants[p.fournisseur] || p.montant_estime_ht}
                      onChange={e => setMontants({...montants, [p.fournisseur]: parseFloat(e.target.value)})}
                      style={{width:'90px',padding:'4px 8px',border:'1px solid #E8E7E3',borderRadius:'6px'}} /> €
                  </td>
                  <td style={{padding:'8px'}}>
                    <input type="checkbox" checked={!!selection[p.fournisseur]} onChange={e => setSelection({...selection, [p.fournisseur]: e.target.checked})} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{display:'flex',gap:'12px'}}>
            <button onClick={valider} disabled={loading} style={{padding:'10px 20px',background:'#0F6E56',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'600'}}>✅ Enregistrer les FNP sélectionnées</button>
            <button onClick={() => setPropositions(null)} style={{padding:'10px 16px',background:'#F5F4ED',color:'#2D2D2D',border:'none',borderRadius:'8px',cursor:'pointer'}}>Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Onglet Provisions
const ProvisionsTab = ({ exercice, headers, setMsg }) => {
  const [form, setForm] = useState({ motif:'', type:'risque', montant:'' });
  const [proposition, setProposition] = useState(null);
  const [loading, setLoading] = useState(false);

  const proposer = async () => {
    setLoading(true);
    try {
      const r = await fetch('/inventaire/provisions/proposer', { method:'POST', headers, body: JSON.stringify({...form, montant: parseFloat(form.montant)}) });
      const d = await r.json();
      if (d.success) setProposition(d.proposition);
    } finally { setLoading(false); }
  };

  const valider = async () => {
    setLoading(true);
    try {
      const r = await fetch(`/inventaire/provisions/valider?exercice=${exercice}`, {
        method:'POST', headers,
        body: JSON.stringify({ ...proposition, valide: true })
      });
      const d = await r.json();
      setMsg(d.message);
      setProposition(null);
      setForm({ motif:'', type:'risque', montant:'' });
    } finally { setLoading(false); }
  };

  return (
    <div style={{background:'white',padding:'24px',borderRadius:'12px',border:'1px solid #E8E7E3'}}>
      <h3 style={{fontWeight:'600',marginBottom:'8px'}}>⚠️ Provisions pour risques et charges</h3>
      <p style={{color:'#73726C',fontSize:'13px',marginBottom:'16px'}}>Saisissez le motif et le montant. Le système suggère le bon compte PCG.</p>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'16px'}}>
        <input placeholder="Motif (ex: Litige client Dupont)" value={form.motif} onChange={e => setForm({...form, motif:e.target.value})} style={{padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px'}} />
        <select value={form.type} onChange={e => setForm({...form, type:e.target.value})} style={{padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px'}}>
          <option value="client">Risque client (491)</option>
          <option value="stock">Dépréciation stock (391)</option>
          <option value="risque">Provision risques (151)</option>
          <option value="litige">Provision litige (1518)</option>
        </select>
        <input type="number" placeholder="Montant (€)" value={form.montant} onChange={e => setForm({...form, montant:e.target.value})} style={{padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px'}} />
      </div>
      <button onClick={proposer} disabled={loading || !form.motif || !form.montant} style={{padding:'10px 20px',background:'#0891B2',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'500'}}>
        {loading ? '...' : '🧮 Proposer l\'écriture'}
      </button>

      {proposition && (
        <div style={{marginTop:'20px',background:'#FFF7ED',border:'1px solid #FED7AA',borderRadius:'10px',padding:'16px'}}>
          <h4 style={{fontWeight:'600',marginBottom:'12px',color:'#92400E'}}>📋 Résumé de la provision à enregistrer</h4>
          <p style={{fontSize:'13px',marginBottom:'4px'}}><strong>Motif :</strong> {proposition.motif}</p>
          <p style={{fontSize:'13px',marginBottom:'4px'}}><strong>Montant :</strong> {proposition.montant.toFixed(2)} €</p>
          <p style={{fontSize:'13px',marginBottom:'12px',fontFamily:'monospace',background:'white',padding:'8px',borderRadius:'6px',whiteSpace:'pre-line'}}>{proposition.ecriture_proposee}</p>
          <div style={{display:'flex',gap:'12px'}}>
            <button onClick={valider} disabled={loading} style={{padding:'10px 20px',background:'#0F6E56',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'600'}}>✅ Confirmer et enregistrer</button>
            <button onClick={() => setProposition(null)} style={{padding:'10px 16px',background:'#F5F4ED',color:'#2D2D2D',border:'none',borderRadius:'8px',cursor:'pointer'}}>Modifier</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPOSANT SOCIAL & PAIE
// ============================================================================
const SocialSection = ({ clientId, token }) => {
  const [salaries, setSalaries] = useState([]);
  const [simulation, setSimulation] = useState(null);
  const [selectedSal, setSelectedSal] = useState(null);
  const [periode, setPeriode] = useState(new Date().toISOString().slice(0,7));
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newSal, setNewSal] = useState({nom:'',prenom:'',type_contrat:'CDI',statut_dirigeant:false,regime_social:'general',salaire_brut:'',taux_patronal:'45',taux_salarial:'22',date_entree:''});
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadSalaries = async () => {
    try {
      const r = await fetch('/social/salaries', { headers });
      const d = await r.json();
      if (d.success) setSalaries(d.data);
    } catch(e) {}
  };

  useEffect(() => { loadSalaries(); }, []);

  const creerSalarie = async () => {
    setLoading(true);
    try {
      const r = await fetch('/social/salaries', { method:'POST', headers, body: JSON.stringify({...newSal, salaire_brut:parseFloat(newSal.salaire_brut), taux_patronal:parseFloat(newSal.taux_patronal), taux_salarial:parseFloat(newSal.taux_salarial)}) });
      const d = await r.json();
      if (d.success) { setMsg(d.message); loadSalaries(); setShowForm(false); }
    } finally { setLoading(false); }
  };

  const simulerPaie = async () => {
    if (!selectedSal) return;
    setLoading(true);
    try {
      const r = await fetch('/social/fiches-paie/simuler', { method:'POST', headers, body: JSON.stringify({ salarie_id: selectedSal, periode }) });
      const d = await r.json();
      if (d.success) setSimulation(d.simulation);
    } finally { setLoading(false); }
  };

  const validerPaie = async () => {
    if (!simulation) return;
    setLoading(true);
    try {
      const r = await fetch('/social/fiches-paie/valider', {
        method:'POST', headers,
        body: JSON.stringify({ salarie_id: selectedSal, periode, salaire_brut: simulation.salaire_brut, cotisations_sal: simulation.cotisations_salariales, cotisations_pat: simulation.cotisations_patronales, salaire_net: simulation.salaire_net, valide: true })
      });
      const d = await r.json();
      if (d.success) { setMsg(`✅ ${d.message} — URSSAF à payer : ${d.urssaf_a_payer} € (échéance : ${d.echeance_urssaf})`); setSimulation(null); }
    } finally { setLoading(false); }
  };

  const cardStyle = {background:'white',padding:'24px',borderRadius:'12px',border:'1px solid #E8E7E3',marginBottom:'16px'};
  const badgeColor = {CDI:'#E1F5EE',CDD:'#FFF7ED',dirigeant_salarie:'#EFF6FF',tns:'#F5F3FF',alternance:'#FEF9C3'};
  const badgeText = {CDI:'#0F6E56',CDD:'#92400E',dirigeant_salarie:'#1D4ED8',tns:'#6D28D9',alternance:'#854D0E'};

  return (
    <div>
      {msg && <div style={{background:'#E1F5EE',color:'#0F6E56',padding:'12px 16px',borderRadius:'8px',marginBottom:'16px',fontWeight:'500'}}>{msg} <button onClick={() => setMsg(null)} style={{float:'right',background:'none',border:'none',cursor:'pointer',color:'#0F6E56'}}>✕</button></div>}

      <div style={cardStyle}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
          <h2 style={{fontSize:'20px',fontWeight:'600'}}>👥 Salariés & Paie</h2>
          <button onClick={() => setShowForm(!showForm)} style={{padding:'8px 16px',background:'#0F6E56',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'500',fontSize:'13px'}}>
            ➕ Ajouter
          </button>
        </div>

        {showForm && (
          <div style={{background:'#F9FAFB',borderRadius:'10px',padding:'16px',marginBottom:'16px',border:'1px solid #E8E7E3'}}>
            <h3 style={{fontWeight:'600',marginBottom:'12px',fontSize:'14px'}}>Nouveau salarié / dirigeant</h3>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <div>
                <label style={{fontSize:'11px',color:'#73726C',marginBottom:'4px',display:'block',fontWeight:'500'}}>Nom</label>
                <input placeholder="Nom" value={newSal.nom} onChange={e => setNewSal({...newSal, nom:e.target.value})} style={{width:'100%',padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}} />
              </div>
              <div>
                <label style={{fontSize:'11px',color:'#73726C',marginBottom:'4px',display:'block',fontWeight:'500'}}>Prénom</label>
                <input placeholder="Prénom" value={newSal.prenom} onChange={e => setNewSal({...newSal, prenom:e.target.value})} style={{width:'100%',padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}} />
              </div>
              <div>
                <label style={{fontSize:'11px',color:'#73726C',marginBottom:'4px',display:'block',fontWeight:'500'}}>Type de contrat</label>
                <select value={newSal.type_contrat} onChange={e => setNewSal({...newSal, type_contrat:e.target.value, regime_social: e.target.value==='tns'?'tns':'general'})} style={{width:'100%',padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}}>
                  <option value="CDI">CDI</option>
                  <option value="CDD">CDD</option>
                  <option value="alternance">Alternance</option>
                  <option value="dirigeant_salarie">Dirigeant assimilé-salarié (SAS/SASU)</option>
                  <option value="tns">Gérant Majoritaire TNS (SARL/EURL)</option>
                </select>
              </div>
              <div>
                <label style={{fontSize:'11px',color:'#73726C',marginBottom:'4px',display:'block',fontWeight:'500'}}>Salaire brut mensuel (€)</label>
                <input type="number" placeholder="Brut mensuel (€)" value={newSal.salaire_brut} onChange={e => setNewSal({...newSal, salaire_brut:e.target.value})} style={{width:'100%',padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}} />
              </div>
              <div>
                <label style={{fontSize:'11px',color:'#73726C',marginBottom:'4px',display:'block',fontWeight:'500'}}>Charges Salariales - Taux moyen : 22%</label>
                <input type="number" placeholder="Taux salarial (%)" value={newSal.taux_salarial} onChange={e => setNewSal({...newSal, taux_salarial:e.target.value})} style={{width:'100%',padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}} />
              </div>
              <div>
                <label style={{fontSize:'11px',color:'#73726C',marginBottom:'4px',display:'block',fontWeight:'500'}}>Charges Patronales - Taux moyen : 45%</label>
                <input type="number" placeholder="Taux patronal (%)" value={newSal.taux_patronal} onChange={e => setNewSal({...newSal, taux_patronal:e.target.value})} style={{width:'100%',padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}} />
              </div>
              <div style={{gridColumn:'span 2'}}>
                <label style={{fontSize:'11px',color:'#73726C',marginBottom:'4px',display:'block',fontWeight:'500'}}>Date d'entrée</label>
                <input type="date" value={newSal.date_entree} onChange={e => setNewSal({...newSal, date_entree:e.target.value})} style={{width:'100%',padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px',boxSizing:'border-box'}} />
              </div>
            </div>
            {newSal.type_contrat === 'tns' && <p style={{fontSize:'12px',color:'#6D28D9',marginTop:'8px',background:'#F5F3FF',padding:'8px',borderRadius:'6px'}}>⚠️ Mode TNS : Compte 455 (Compte courant associé) et 646 (Cotisations SSI) seront utilisés</p>}
            <div style={{display:'flex',gap:'12px',marginTop:'12px'}}>
              <button onClick={creerSalarie} disabled={loading || !newSal.nom || !newSal.salaire_brut} style={{padding:'10px 20px',background:'#0F6E56',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'500'}}>✅ Enregistrer</button>
              <button onClick={() => setShowForm(false)} style={{padding:'10px 16px',background:'#F5F4ED',color:'#2D2D2D',border:'none',borderRadius:'8px',cursor:'pointer'}}>Annuler</button>
            </div>
          </div>
        )}

        {salaries.length === 0 ? <p style={{color:'#73726C',textAlign:'center',padding:'24px'}}>Aucun salarié enregistré</p> : (
          <div style={{display:'grid',gap:'10px'}}>
            {salaries.map(s => (
              <div key={s.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 16px',background:selectedSal===s.id?'#E1F5EE':'#F9FAFB',borderRadius:'10px',border:`1px solid ${selectedSal===s.id?'#0F6E56':'#E8E7E3'}`,cursor:'pointer'}} onClick={() => setSelectedSal(s.id===selectedSal?null:s.id)}>
                <div>
                  <span style={{fontWeight:'600'}}>{s.prenom} {s.nom}</span>
                  <span style={{marginLeft:'10px',background:badgeColor[s.type_contrat]||'#F5F4ED',color:badgeText[s.type_contrat]||'#2D2D2D',fontSize:'11px',padding:'2px 8px',borderRadius:'12px'}}>{s.type_contrat}</span>
                  {s.statut_dirigeant && <span style={{marginLeft:'6px',background:'#EFF6FF',color:'#1D4ED8',fontSize:'11px',padding:'2px 8px',borderRadius:'12px'}}>Dirigeant</span>}
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{fontWeight:'600'}}>{s.salaire_brut.toFixed(2)} € brut</div>
                  <div style={{fontSize:'12px',color:'#73726C'}}>Net ~{s.salaire_net_estime.toFixed(2)} € / Coût {s.cout_employeur_estime.toFixed(2)} €</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* GÉNÉRER PAIE */}
      {selectedSal && (
        <div style={{...cardStyle, border:'2px solid #7C3AED'}}>
          <h3 style={{fontWeight:'600',color:'#7C3AED',marginBottom:'16px'}}>💳 Générer la paie du mois</h3>
          <div style={{display:'flex',gap:'12px',alignItems:'center',marginBottom:'16px'}}>
            <input type="month" value={periode} onChange={e => setPeriode(e.target.value)} style={{padding:'8px 12px',border:'1px solid #E8E7E3',borderRadius:'8px',fontSize:'13px'}} />
            <button onClick={simulerPaie} disabled={loading} style={{padding:'10px 20px',background:'#7C3AED',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'500'}}>
              {loading ? '...' : '🧮 Simuler la paie'}
            </button>
          </div>

          {simulation && (
            <div style={{background:'#F5F3FF',borderRadius:'10px',padding:'16px'}}>
              <h4 style={{fontWeight:'600',marginBottom:'12px'}}>{simulation.salarie} — {simulation.periode}</h4>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginBottom:'16px'}}>
                {[['Salaire brut', simulation.salaire_brut?.toFixed(2)+' €','#1F2937'],['Cotisations salariales', '-'+simulation.cotisations_salariales?.toFixed(2)+' €','#DC2626'],['Salaire net', simulation.salaire_net?.toFixed(2)+' €','#059669'],['Charges patronales', simulation.cotisations_patronales?.toFixed(2)+' €','#D97706'],['Coût total employeur', simulation.cout_total_employeur?.toFixed(2)+' €','#7C3AED']].map(([label,val,color]) => (
                  <div key={label} style={{background:'white',borderRadius:'8px',padding:'10px'}}>
                    <div style={{fontSize:'12px',color:'#73726C'}}>{label}</div>
                    <div style={{fontWeight:'700',color,fontSize:'16px'}}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{background:'white',borderRadius:'8px',padding:'12px',marginBottom:'16px'}}>
                <div style={{fontSize:'12px',fontWeight:'600',color:'#73726C',marginBottom:'6px'}}>ÉCRITURES PROPOSÉES</div>
                {simulation.ecritures_proposees?.map((e,i) => <div key={i} style={{fontSize:'12px',fontFamily:'monospace',padding:'2px 0',color:'#1F2937'}}>{e}</div>)}
              </div>
              <div style={{display:'flex',gap:'12px'}}>
                <button onClick={validerPaie} disabled={loading} style={{flex:1,padding:'10px',background:'#059669',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'600'}}>✅ Valider et comptabiliser</button>
                <button onClick={() => setSimulation(null)} style={{padding:'10px 16px',background:'#F5F4ED',color:'#2D2D2D',border:'none',borderRadius:'8px',cursor:'pointer'}}>Annuler</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPOSANT URSSAF / DSN
// ============================================================================
const URSSAFSection = ({ clientId, token }) => {
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [activeCopiloteDecl, setActiveCopiloteDecl] = useState(null);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadURSSAF = async () => {
    setLoading(true);
    try {
      const r = await fetch('/social/urssaf', { headers });
      const d = await r.json();
      if (d.success) {
        setDeclarations(d.data);
        if (d.message_alerte) setMsg(d.message_alerte);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { loadURSSAF(); }, []);

  const payer = async (id, montant) => {
    const date_paiement = new Date().toISOString().slice(0,10);
    setLoading(true);
    try {
      const r = await fetch('/social/urssaf/payer', { method:'POST', headers, body: JSON.stringify({ declaration_id: id, date_paiement }) });
      const d = await r.json();
      if (d.success) { setMsg(`✅ ${d.message}`); loadURSSAF(); }
    } finally { setLoading(false); }
  };

  return (
    <div style={{background:'white',padding:'24px',borderRadius:'12px',border:'1px solid #E8E7E3'}}>
      <h2 style={{fontSize:'20px',fontWeight:'600',marginBottom:'4px'}}>🏛️ Tableau de bord URSSAF / DSN</h2>
      <p style={{color:'#73726C',fontSize:'13px',marginBottom:'20px'}}>Échéance légale : le 15 de chaque mois (DSN mensuelle)</p>

      {msg && <div style={{background: msg.includes('⚠️')?'#FEF2F2':'#E1F5EE', color: msg.includes('⚠️')?'#991B1B':'#0F6E56', padding:'12px 16px',borderRadius:'8px',marginBottom:'16px',fontWeight:'500'}}>{msg} <button onClick={() => setMsg(null)} style={{float:'right',background:'none',border:'none',cursor:'pointer'}}>✕</button></div>}

      {loading ? <p style={{textAlign:'center',color:'#73726C'}}>Chargement...</p> : declarations.length === 0 ? (
        <p style={{textAlign:'center',color:'#73726C',padding:'40px'}}>Aucune déclaration URSSAF — Générez des fiches de paie d'abord</p>
      ) : (
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
          <thead><tr style={{borderBottom:'2px solid #E8E7E3'}}>
            {['Période','Type','Montant','Échéance','Statut','Action'].map(h => <th key={h} style={{textAlign:'left',padding:'10px 8px',color:'#73726C',fontWeight:'500'}}>{h}</th>)}
          </tr></thead>
          <tbody>
            {declarations.map(d => (
              <tr key={d.id} style={{borderBottom:'1px solid #F5F4ED',background:d.en_retard?'#FEF2F2':'white'}}>
                <td style={{padding:'10px 8px',fontWeight:'500'}}>{d.periode}</td>
                <td style={{padding:'10px 8px',fontSize:'12px',color:'#73726C'}}>{d.type}</td>
                <td style={{padding:'10px 8px',fontWeight:'600'}}>{d.montant_total.toFixed(2)} €</td>
                <td style={{padding:'10px 8px',color:d.en_retard?'#DC2626':'#1F2937'}}>{d.date_echeance} {d.en_retard && <span style={{fontSize:'11px'}}>({d.jours_retard}j retard)</span>}</td>
                <td style={{padding:'10px 8px'}}>
                  <span style={{padding:'3px 10px',borderRadius:'12px',fontSize:'11px',fontWeight:'600',background:d.statut==='paye'?'#E1F5EE':d.en_retard?'#FEE2E2':'#FEF9C3',color:d.statut==='paye'?'#0F6E56':d.en_retard?'#991B1B':'#92400E'}}>
                    {d.badge} {d.statut}
                  </span>
                </td>
                <td style={{padding:'10px 8px'}}>
                  {d.statut !== 'paye' && (
                    <div style={{display:'flex', gap:'8px'}}>
                      <button onClick={() => payer(d.id, d.montant_total)} disabled={loading} style={{padding:'5px 12px',background:'#0F6E56',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'500'}}>
                        💳 Marquer payé
                      </button>
                      <button onClick={() => setActiveCopiloteDecl(d)} disabled={loading} style={{padding:'5px 12px',background:'#2563EB',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px',fontWeight:'500'}}>
                        🤖 Copilote
                      </button>
                    </div>
                  )}
                  {d.date_paiement && <span style={{fontSize:'11px',color:'#73726C'}}>Payé le {d.date_paiement}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {activeCopiloteDecl && (
        <CopiloteDeclaration
          clientId={clientId}
          onClose={() => setActiveCopiloteDecl(null)}
          type="URSSAF"
          data={{
            montant_total: activeCopiloteDecl.montant_total,
            periode: activeCopiloteDecl.periode
          }}
        />
      )}
    </div>
  );
};

export default ComptableDashboard;
